import React, { useMemo, useState, useEffect, useRef } from "react";
import { useTimePrefs, fmtClock } from "../lib/timePrefs";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, getTeams } from "../lib/api";
import { TEAM_INFO, teamAbbr, teamName, teamColor } from "../lib/teamMap";
import { TeamBadge } from "../components/TeamBadge";
import { PlayerAvatar } from "../components/PlayerAvatar";
import { fmtSvFromCounts as fmtSv } from "../lib/statColumns";
import { formatDate } from "../lib/date";
import { Calendar, MapPin, Users, Clock, Star, Goal, ShieldAlert, Activity, Crosshair, Shield, CircleDot, Mic, Flag, Code2, ChevronDown, Hash, Info as InfoIcon, Wrench, ClipboardList, Trash2, Plus, Minus, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, X, GripVertical, Settings2, Zap, Timer, Play, Pause, Check } from "lucide-react";
import { usePWHLSync, PWHLSyncControl, PWHLMatchReview } from "../components/admin/PWHLSyncPanel";
import { LineupCard } from "../components/LineupCard";
import { HoverInfo, HoverCard } from "../components/LineupCard";
import { NewLineupBoard } from "../components/NewLineupBoard";
import { StoryTemplateView } from "../components/stories/StoryFeed";
import { getStudioTemplates, saveStudioTemplates, getPlayers, getPwhlSchedule, getTrades, getTransfers, getContractHistory, getNews, getPwhlThreeStars, getPwhlScoring, getPwhlPxp, getGoalieGameLogs } from "../lib/api";
import { pbpSurface, PBP_SURFACE_KEYS, pubKeyForItem, buildPlayerIndex, isItemHiddenGC, applyStudioToItem, applyGroupsAndPriority } from "../lib/storyResolve";
import { buildRealStories } from "../lib/storiesReal";
import { STORY_TEMPLATES } from "../lib/storyTemplates";

const TPL_LABEL = Object.fromEntries(STORY_TEMPLATES.map((t) => [t.key, t.label]));
import { LineupBuilder, emptyLineup, hydrateLineup, RESERVE_LABELS } from "../components/admin/LineupBuilder";
import { computeSkaterInsights, computeGoalieSplits } from "../lib/gameCentre";
import { useGameNav } from "../context/GameNavContext";
import { useAdminMode } from "../lib/AdminModeContext";
import { pathForTab } from "../lib/teamsRoutes";
import GameCentreNavBar from "../components/GameCentreNavBar";
import { toast } from "sonner";

// ===================== PUBLIC PWHL GAME CENTRE (Phase 2) =====================
// Renders a synced PWHL import (staging data) as a clean, dark Rosterly box
// score. Display-only — never reads or writes season stats.

const periodLabel = (n) => (n <= 3 ? `${n}` : n === 4 ? "OT" : n === 5 ? "SO" : `${n}`);
const periodTab = (n) => (n <= 3 ? `P${n}` : n === 4 ? "OT" : "SO");
const periodLong = (n) => (n <= 3 ? `${n}${["st", "nd", "rd"][n - 1]} Period` : n === 4 ? "Overtime" : "Shootout");
const STRENGTH_PILL = { PP: "PPG", SH: "SHG", EN: "EN", PS: "PS" };

const cityOf = (id, code) => {
    const n = teamName(id);
    if (!n || n === id) return (code || "").toUpperCase();
    const parts = n.split(" ");
    return parts.slice(0, -1).join(" ").toUpperCase() || n.toUpperCase();
};
const nickOf = (id, code) => {
    const n = teamName(id);
    if (!n || n === id) return "";
    return n.split(" ").slice(-1)[0].toUpperCase();
};
const abbrevName = (full) => {
    const p = String(full || "").trim().split(" ");
    return p.length > 1 ? `${p[0][0]}. ${p.slice(1).join(" ")}` : full;
};

function Card({ title, icon: Icon, children, testId, accent = "#9D4CDD", className = "", style }) {
    return (
        <section className={`rounded-2xl bg-[#0E0F16] border border-white/10 shadow-xl shadow-black/40 p-4 sm:p-5 min-w-0 ${className}`} data-testid={testId} style={style}>
            {title && (
                <h2 className="font-heading text-xs font-bold uppercase tracking-[0.18em] text-[#94A3B8] flex items-center gap-2 mb-4">
                    {Icon && <Icon size={14} style={{ color: accent }} />} {title}
                </h2>
            )}
            {children}
        </section>
    );
}

// Admin-controlled highlight mode for period-affected stat boxes:
//  - "all"     → every periodized box shows its period colour (even when 0)
//  - "changed" → only boxes whose value actually changed (non-zero) get coloured
const HighlightCtx = React.createContext("all");
const useHl = () => React.useContext(HighlightCtx);
// Consistent darker base for every stat box (not the old grey tint).
const BOX_BASE = { backgroundColor: "rgba(0,0,0,0.30)", borderColor: "rgba(255,255,255,0.06)" };
// Per-stat pill box — IDENTICAL to the Leaderboards page StatBlock so the Stat
// Summary matches it exactly (floating-row cards + boxed stat values).
const StatBlock = ({ value, color = null }) => {
    const dim = value === "—" || value === 0 || value === "0" || value == null;
    const lit = !!color && !dim;
    const style = lit ? { backgroundColor: `${color}26`, borderColor: `${color}59` } : BOX_BASE;
    return (
        <span className="inline-flex items-center justify-center min-w-[34px] px-1.5 py-1 rounded-lg border font-mono-stat tabular-nums transition-colors" style={style}>
            <span className={dim ? "text-[#3A3F55]" : "font-bold text-[#E5E7EB]"} style={lit ? { color } : undefined}>{value}</span>
        </span>
    );
};


// Elapsed/Left clock pref for PWHL event times (feed times are elapsed). OT is 5:00 in the
// regular season, 20:00 in the playoffs — the open game's stage is noted on render.
const PW_STAGE = { current: "regular" };
const usePwClock = () => {
    const { clockMode } = useTimePrefs();
    return (time, period) => fmtClock(String(time || ""), clockMode, Number(period) >= 4 && PW_STAGE.current !== "playoffs" ? 300 : 1200);
};

export default function PWHLGameCentre({ data, admin = false, gameId, fixture, hoistNav = true, viewProp = null, onViewChange = null, periodProp = null, onPeriodChange = null, onNavConfig = null, backLink = null }) {
    PW_STAGE.current = data?.stage || "regular";
    useTimePrefs();
    const m = data.meta || {};
    const homeId = m.home_team_id, awayId = m.away_team_id;
    const homeWon = (m.home_score || 0) > (m.away_score || 0);
    const [showRaw, setShowRaw] = useState({});
    const toggleRaw = (k) => setShowRaw((s) => ({ ...s, [k]: !s[k] }));
    const [insTab, setInsTab] = useState("main:basic");
    const [insPeriodLocal, setInsPeriodLocal] = useState("combined");
    const insPeriod = periodProp != null ? periodProp : insPeriodLocal;
    const setInsPeriod = onPeriodChange || setInsPeriodLocal;
    // Selection: single-click replaces, Shift+click toggles (multi-select).
    // `gsShow` = event categories (Team Summary events). `statSel` = player-stat
    // groups (Player Summary columns).
    const [gsShow, setGsShow] = useState({ goal: true });
    const [statSel, setStatSel] = useState({ all: true });
    const pickSel = (setter) => (key, shift) => setter((s) => (shift ? { ...s, [key]: !s[key] } : { [key]: true }));
    const selectEventCat = pickSel(setGsShow);
    const [searchParams] = useSearchParams();
    const VALID_VIEWS = ["team_summary", "lineup"];
    const _legacyView = { summary: "team_summary", player_summary: "team_summary", home_roster: "team_summary", away_roster: "team_summary", home_lineup: "lineup", away_lineup: "lineup" };
    const _qv = searchParams.get("view");
    const initialView = VALID_VIEWS.includes(_qv) ? _qv : (_legacyView[_qv] || "team_summary");
    const [gameViewLocal, setGameViewLocal] = useState(initialView);
    const [openLineup, setOpenLineup] = useState(null);
    const gameView = viewProp != null ? viewProp : gameViewLocal;
    const setGameView = onViewChange || setGameViewLocal;
    const [teamFilter, setTeamFilter] = useState(homeId);
    const [hlMode, setHlMode] = useState(() => { try { return localStorage.getItem("pgc_hl_mode") || "all"; } catch { return "all"; } });
    const setHl = (m) => { setHlMode(m); try { localStorage.setItem("pgc_hl_mode", m); } catch { /* noop */ } };
    const { setNav } = useGameNav();

    const sync = usePWHLSync({ gameId, fixture, enabled: admin && !!gameId });

    const playersQ = useQuery({
        queryKey: ["pgc-players"],
        queryFn: () => api.get(`/players?include_inactive=true`).then((r) => r.data),
    });
    const standingsQ = useQuery({
        queryKey: ["pgc-standings", data.season],
        queryFn: () => api.get(`/pwhl/standings?season=${data.season}`).then((r) => r.data),
        enabled: !!data.season,
    });
    const summaryQ = useQuery({
        queryKey: ["pgc-summary", gameId],
        queryFn: () => api.get(`/games/${gameId}/summary`).then((r) => r.data),
        enabled: !!gameId,
    });
    // Per-team lineup availability. Non-admins need players AND a published flag
    // on that side; admins can always open the builder. Drives the two nav lineup
    // buttons (greyed + disabled when that team has no viewable lineup).
    const lineupAvail = useMemo(() => {
        const sd = summaryQ.data || {};
        const hasPlayers = (lu) => {
            if (!lu || typeof lu !== "object") return false;
            let n = 0;
            const walk = (v) => { if (!v) return; if (Array.isArray(v)) v.forEach(walk); else if (typeof v === "object") { if (v.player_id || v.pwhl_id || v.id) n += 1; else Object.values(v).forEach(walk); } else if (typeof v === "string" && v.trim()) n += 1; };
            Object.values(lu).forEach(walk);
            return n > 0;
        };
        const ok = (side) => admin || hasPlayers(sd[`${side}_lineup`]);
        return { away: ok("away"), home: ok("home") };
    }, [summaryQ.data, admin]);
    // If the current lineup view isn't available for the chosen team, fall back.
    useEffect(() => {
        if (gameView !== "lineup") return;
        const side = teamFilter === awayId ? "away" : "home";
        if (!lineupAvail[side]) setGameView("team_summary");
    }, [gameView, teamFilter, lineupAvail, awayId]); // eslint-disable-line react-hooks/exhaustive-deps
    // Global Insights column layout (admin-editable, inline per-column controls).
    const insCfgQ = useQuery({ queryKey: ["insights-config"], queryFn: () => api.get(`/insights-config`).then((r) => r.data) });
    const [groupsState, setGroupsState] = useState(null);
    const [flags, setFlags] = useState({});
    useEffect(() => {
        if (insCfgQ.data) {
            setGroupsState(insCfgQ.data.groups?.length ? insCfgQ.data.groups : DEFAULT_INSIGHTS_GROUPS);
            setFlags(insCfgQ.data.flags || {});
        }
    }, [insCfgQ.data]);
    const insGroups = groupsState || DEFAULT_INSIGHTS_GROUPS;
    const insTabsCfg = useMemo(() => buildInsTabs(insGroups), [insGroups]);
    const [matchOpen, setMatchOpen] = useState(false);
    const saveInsights = async (groups, nextFlags) => {
        try { await api.put(`/admin/insights-config`, { groups, flags: nextFlags ?? flags }); }
        catch (e) { toast.error("Failed to save insights layout"); }
    };
    const toggleForce = (which) => {
        const next = { ...flags, [which]: !flags[which] };
        setFlags(next);
        saveInsights(insGroups, next);
    };
    // Persist a column tweak (width / order / remove / move-to-new-section) globally.
    const applyColAction = async (groupId, key, action, arg) => {
        const groups = JSON.parse(JSON.stringify(insGroups));
        const g = groups.find((x) => x.id === groupId);
        if (!g || !g.cols) return;
        const i = g.cols.findIndex((c) => c.key === key);
        if (i < 0) return;
        const w = () => g.cols[i].width || MASTER_STATS[key]?.width || 40;
        if (action === "remove") g.cols.splice(i, 1);
        else if (action === "left" && i > 0) { const [m] = g.cols.splice(i, 1); g.cols.splice(i - 1, 0, m); }
        else if (action === "right" && i < g.cols.length - 1) { const [m] = g.cols.splice(i, 1); g.cols.splice(i + 1, 0, m); }
        else if (action === "shrink") g.cols[i].width = Math.max(28, w() - 8);
        else if (action === "expand") g.cols[i].width = Math.min(180, w() + 8);
        else if (action === "reorder" && typeof arg === "number") { const [m] = g.cols.splice(i, 1); g.cols.splice(Math.max(0, Math.min(g.cols.length, arg)), 0, m); }
        else if (action === "new_section") { const [m] = g.cols.splice(i, 1); groups.push({ id: `custom:${Date.now()}`, label: "New Section", cols: [m] }); }
        else return;
        setGroupsState(groups);
        saveInsights(groups);
    };
    // Section-level actions (rename / delete / add).
    const applyGroupAction = async (groupId, action, arg) => {
        const groups = JSON.parse(JSON.stringify(insGroups));
        if (action === "add") { groups.push({ id: `custom:${Date.now()}`, label: arg || "New Section", cols: [] }); setGroupsState(groups); saveInsights(groups); return; }
        const gi = groups.findIndex((x) => x.id === groupId);
        if (gi < 0) return;
        if (action === "rename") { const nm = (arg || "").trim(); if (!nm) return; groups[gi].label = nm; }
        else if (action === "delete") {
            const players = groups.filter((x) => x.type !== "goalie");
            if (players.length <= 1) { toast.error("Keep at least one section"); return; }
            groups.splice(gi, 1);
            if (insTab === groupId) setInsTab((groups.find((x) => x.type !== "goalie") || {}).id || "main:basic");
        } else return;
        setGroupsState(groups);
        saveInsights(groups);
    };

    const matchMap = useMemo(() => {
        const o = {};
        (data.people || []).forEach((p) => { if (p.matched_player_id) o[p.pwhl_id] = p.matched_player_id; });
        return o;
    }, [data.people]);
    const playerById = useMemo(() => Object.fromEntries((playersQ.data || []).map((p) => [p.id, p])), [playersQ.data]);
    // Merge per-game jersey # + C/A (from this game's data) over the player docs
    // so the lineup board shows the number worn / captaincy from THAT game.
    const gamePlayerMap = useMemo(() => {
        const meta = summaryQ.data?.player_meta || {};
        const out = { ...playerById };
        Object.entries(meta).forEach(([pid, m]) => {
            out[pid] = { ...(out[pid] || {}), jersey: (m.jersey ?? out[pid]?.jersey), position: (m.position || out[pid]?.position || ""), captain: (m.captain || out[pid]?.captain || "") };
        });
        return out;
    }, [playerById, summaryQ.data]);
    const photoFor = (pwhlId) => playerById[matchMap[pwhlId]]?.photo_url;
    const metaFor = (pwhlId) => playerById[matchMap[pwhlId]] || {};

    const records = useMemo(() => {
        // Prefer the record AS OF this fixture's date; fall back to season standings.
        const ra = data.records_asof;
        if (ra && (ra.home || ra.away)) {
            return { [homeId]: ra.home, [awayId]: ra.away };
        }
        const o = {};
        (standingsQ.data || []).forEach((r) => {
            if (r.stage && r.stage !== (data.stage || "regular")) return;
            o[r.team_id] = `${r.w ?? 0}-${(r.otw ?? 0) + (r.sow ?? 0)}-${(r.otl ?? 0) + (r.sol ?? 0)}-${r.l ?? 0}`;
        });
        return o;
    }, [data.records_asof, standingsQ.data, data.stage, homeId, awayId]);

    // pwhl_id -> game stat line (for three-star pills)
    const statByPwhl = useMemo(() => {
        const o = {};
        (data.skaters || []).forEach((s) => { o[s.player?.pwhl_id] = { g: s.g, a: s.a, p: s.p, isG: false }; });
        (data.goalies || []).forEach((g) => {
            const id = g.player?.pwhl_id;
            const cur = o[id] || {};
            o[id] = { ...cur, isG: true, saves: (cur.saves || 0) + (g.saves || 0), sa: (cur.sa || 0) + (g.shots_against || 0) };
        });
        return o;
    }, [data.skaters, data.goalies]);

    // Rosterly Insights — derived per-skater metrics + game leaders.
    const insights = useMemo(() => computeSkaterInsights(data), [data]);
    // Per-goalie splits (combined = box score; per-period = play-by-play).
    const goalieSplits = useMemo(() => computeGoalieSplits(data), [data]);
    // Which periods actually happened (drives the period toggle set).
    const presentPeriods = useMemo(() => {
        const set = new Set((data.pxp || []).map((e) => Number(e.period) || 1));
        (data.periods || []).forEach((p) => set.add(Number(p.period)));
        return set;
    }, [data.pxp, data.periods]);

    // Build the Game Centre nav controls. Published to the TOP nav bar
    // (GameNavContext) normally, or rendered INLINE when embedded (hoistNav=false)
    // so a host page (e.g. the Schedule game picker) can keep owning the nav.
    const navConfig = useMemo(() => {
        // Real OT/SO detection = actual period data present (not the stale admin
        // force flags, which were showing OT/SO on regulation games).
        const hasSoP = (data.shootout || []).length > 0 || !!(data.meta && data.meta.shootout) || presentPeriods.has(5);
        const hasOtP = presentPeriods.has(4);
        const hasOtOrSo = hasOtP || hasSoP;
        // No OT/SO → "Combined" IS "Regulation": drop the Regulation toggle and
        // rename Combined → Regulation. With OT/SO → show both.
        const navPeriods = gameView === "lineup" ? [] : PERIOD_TOGGLES
            .filter((p) => p.k === "combined" || (p.k === "regulation" && hasOtOrSo) || (p.p >= 1 && p.p <= 3 && presentPeriods.has(p.p)) || (p.p === 4 && hasOtP) || (p.p === 5 && hasSoP))
            .map((p) => ({ k: p.k, label: (!hasOtOrSo && p.k === "combined") ? "Regulation" : p.label, color: PERIOD_META[p.k].color }));
        const forcesP = [];
        return {
            view: gameView, setView: setGameView,
            teamFilter, setTeamFilter,
            // Two lineup buttons (one per team) live in the nav; greyed when unavailable.
            lineupTeams: [
                { id: awayId, label: teamAbbr(awayId) || "Team 1", color: teamColor(awayId) || "#5BD1B7", available: lineupAvail.away },
                { id: homeId, label: teamAbbr(homeId) || "Team 2", color: teamColor(homeId) || "#9D4CDD", available: lineupAvail.home },
            ],
            periods: navPeriods, insPeriod, setInsPeriod,
            // Views — single-select stat category (Main / Special Teams / Insights),
            // its OWN nav group after Periods. Drives which team + player stat
            // columns show. Always available (not tied to the Game Recap button).
            statTabs: PS_TABS,
            statSel, setStatSel,
            admin, forces: forcesP, toggleForce,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameView, teamFilter, insPeriod, presentPeriods, flags.force_ot, flags.force_so, admin, awayId, homeId, data, insTabsCfg, lineupAvail, statSel]);

    // Publish the Game Centre controls to the TOP nav bar (via GameNavContext),
    // unless embedded (then either render inline, or hand the config to a host
    // page via onNavConfig so it can render them in its own nav bar row).
    useEffect(() => {
        if (onNavConfig) { onNavConfig(navConfig); return; }
        if (!hoistNav) return;
        setNav(navConfig);
        return () => setNav(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hoistNav, navConfig, onNavConfig]);


    const skatersByTeam = useMemo(() => {
        const g = { [homeId]: [], [awayId]: [] };
        (data.skaters || []).forEach((s) => { (g[s.team_id] = g[s.team_id] || []).push(s); });
        return g;
    }, [data.skaters, homeId, awayId]);

    // Shots on goal per period per side, derived from play-by-play (shot + goal
    // events are shots on goal). Keyed by period number.
    const shotsByPeriod = useMemo(() => {
        const o = {};
        (data.pxp || []).forEach((e) => {
            if (e.type !== "shot" && e.type !== "goal") return;
            const per = e.period;
            o[per] = o[per] || { home: 0, away: 0 };
            if (e.team_id === homeId) o[per].home += 1;
            else if (e.team_id === awayId) o[per].away += 1;
        });
        return o;
    }, [data.pxp, homeId, awayId]);

    const AdminRaw = ({ k, value }) => {
        if (!admin) return null;
        return (
            <div className="mt-3 pt-3 border-t border-dashed border-[#9D4CDD]/30" data-testid={`pgc-raw-${k}`}>
                <button type="button" onClick={() => toggleRaw(k)}
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-[#9D4CDD] hover:text-[#C99BF0]"
                    data-testid={`pgc-raw-toggle-${k}`}>
                    <Code2 size={12} /> Imported source data
                    <ChevronDown size={12} className={`transition-transform ${showRaw[k] ? "rotate-180" : ""}`} />
                </button>
                {showRaw[k] && (
                    <pre className="mt-2 max-h-64 overflow-auto rounded bg-black/50 border border-[#1E2233] p-2 text-[10px] leading-relaxed text-[#9FB3C8] font-mono" data-testid={`pgc-raw-body-${k}`}>
                        {JSON.stringify(value, null, 2)}
                    </pre>
                )}
            </div>
        );
    };

    return (
        <HighlightCtx.Provider value={hlMode}>
        <div className="space-y-6">
            {backLink && <div className="-mt-1 -mb-1" data-testid="pgc-backlink">{backLink}</div>}
            {/* Embedded fallback: render controls inline only when no host nav
                slot is available (onNavConfig). Normally the host renders them. */}
            {!hoistNav && !onNavConfig && (
                <div className="rounded-xl border border-white/10 bg-[#0A0B10]/60 px-3 py-2.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-2" data-testid="pgc-inline-nav">
                    <GameCentreNavBar {...navConfig} />
                </div>
            )}
            {/* ============ Compact admin sync bar (admins only) ============ */}
            {admin && gameId && gameView === "team_summary" && (
                <div className="rounded-lg border border-[#9D4CDD]/30 bg-[#9D4CDD]/[0.05] px-4 py-3" data-testid="pgc-admin-sync-bar">
                    <PWHLSyncControl sync={sync} />
                </div>
            )}
            {/* Admin: stat-box highlight mode (period-affected boxes) */}
            {admin && gameView === "player_summary" && (
                <div className="flex items-center gap-2 rounded-lg border border-[#9D4CDD]/30 bg-[#9D4CDD]/[0.05] px-4 py-2" data-testid="pgc-hl-mode-bar">
                    <span className="text-[10px] font-black uppercase tracking-wider text-[#94A3B8]">Highlight boxes</span>
                    <button type="button" data-testid="pgc-hl-all" onClick={() => setHl("all")}
                        className={`h-7 px-2.5 rounded-md border text-[10px] font-bold uppercase transition-colors ${hlMode === "all" ? "border-[#9D4CDD] bg-[#9D4CDD]/20 text-white" : "border-white/10 text-[#94A3B8] hover:text-white"}`}>All periodized</button>
                    <button type="button" data-testid="pgc-hl-changed" onClick={() => setHl("changed")}
                        className={`h-7 px-2.5 rounded-md border text-[10px] font-bold uppercase transition-colors ${hlMode === "changed" ? "border-[#9D4CDD] bg-[#9D4CDD]/20 text-white" : "border-white/10 text-[#94A3B8] hover:text-white"}`}>Only changed</button>
                    <button type="button" data-testid="pgc-hl-team" onClick={() => setHl("team")}
                        className={`h-7 px-2.5 rounded-md border text-[10px] font-bold uppercase transition-colors ${hlMode === "team" ? "border-[#9D4CDD] bg-[#9D4CDD]/20 text-white" : "border-white/10 text-[#94A3B8] hover:text-white"}`}>Team colours</button>
                </div>
            )}

            {/* Full-width scoreboard removed — the score now shows in the schedule game picker. */}
            {gameView === "team_summary" && <GameDetailsStrip m={m} data={data} gameId={gameId} admin={admin} />}

            {(() => {
                const teamsToShow = gameView === "team_summary" ? [awayId, homeId] : [teamFilter];
                const sideOf = (tid) => (tid === awayId ? "away" : "home");
                if (gameView === "lineup") {
                    return teamsToShow.map((tid) => (
                        <GameLineupTab key={tid} side={sideOf(tid)} data={data} teamId={tid} admin={admin} gameId={gameId}
                            isPublic={!!summaryQ.data?.[`${sideOf(tid)}_lineup_public`]}
                            lineup={summaryQ.data?.[`${sideOf(tid)}_lineup`]} playerById={gamePlayerMap} players={playersQ.data || []}
                            matchMap={matchMap} onChanged={() => summaryQ.refetch()} />
                    ));
                }
                // team_summary — the all-in-one Game Recap page (lineups now live in the nav).
                return (
                    <div className="space-y-6" data-testid="pgc-recap">
                        {/* Recap row (4 templates) · single modern timeline (all in one component) */}
                        <Card testId="pgc-recap-top"><GameTimelineRow data={data} m={m} matchMap={matchMap} photoFor={photoFor} admin={admin} insPeriod={insPeriod} gameId={gameId} /></Card>
                        {/* Stat summary — ONE team at a time (stacked, not side-by-side) */}
                        <div className="grid gap-6 grid-cols-1" data-testid="pgc-recap-stats">
                            {[awayId, homeId].map((tid) => (
                                <PlayerSummaryPanel key={tid} teamId={tid} side={tid === awayId ? "away" : "home"} data={data} insights={insights} goalieSplits={goalieSplits}
                                    matchMap={matchMap} photoFor={photoFor} metaFor={metaFor} insPeriod={insPeriod} statSel={statSel} admin={admin} applyColAction={applyColAction} />
                            ))}
                        </div>
                    </div>
                );
            })()}
        </div>
        </HighlightCtx.Provider>
    );
}

// ===================== Stats and Insights (Team / Goalie / Player) =====================
// Per-user collapsible section (uncollapsed by default). Collapse state persists
// in localStorage, keyed per section, so each user keeps their preference.
function CollapsibleSection({ id, title, accent = "#9D4CDD", right = null, children }) {
    const key = `pgc-ins-collapse-${id}`;
    const [open, setOpen] = useState(() => { try { return localStorage.getItem(key) !== "1"; } catch { return true; } });
    const toggle = () => setOpen((v) => { const nv = !v; try { localStorage.setItem(key, nv ? "0" : "1"); } catch { /* ignore */ } return nv; });
    return (
        <div className="border-t border-[#1E2233] pt-4 mt-4 first:mt-0 first:border-t-0 first:pt-0" data-testid={`pgc-ins-section-${id}`}>
            <div className="flex items-center justify-between gap-2 mb-3">
                <button type="button" onClick={toggle} data-testid={`pgc-ins-collapse-${id}`} className="flex items-center gap-2">
                    <ChevronDown className={`h-4 w-4 transition-transform ${open ? "" : "-rotate-90"}`} style={{ color: accent }} />
                    <span className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: accent }}>{title}</span>
                </button>
                {open && right}
            </div>
            {open && children}
        </div>
    );
}

const TeamHead = ({ tid }) => (
    <div className="flex items-center gap-2 mb-2">
        <TeamBadge teamId={tid} size="sm" />
        <span className="font-bold text-white text-sm">{teamName(tid)}</span>
    </div>
);

function StatsAndInsights({ data, admin, homeId, awayId, insights, goalieSplits, presentPeriods, matchMap, insTab, setInsTab, insTabsCfg, insPeriod, setInsPeriod, applyColAction, applyGroupAction, flags, toggleForce, sync, matchOpen, setMatchOpen, AdminRaw }) {
    const playerTabs = insTabsCfg.filter((t) => t.type !== "goalie");
    const activeTab = playerTabs.find((t) => t.id === insTab) || playerTabs[0];
    const pm = PERIOD_META[insPeriod] || PERIOD_META.combined;
    const periodColor = insPeriod === "combined" ? null : pm.color;
    const soMode = insPeriod === "so";
    const skaterCols = soMode ? SO_SKATER_COLS : resolveCols(activeTab, insPeriod);
    const teams = [awayId, homeId];
    const hasGoalies = (data.goalies || []).length > 0;

    const renameGroup = () => { const nm = window.prompt("Rename section", activeTab?.label || ""); if (nm != null) applyGroupAction(activeTab.id, "rename", nm); };
    const deleteGroup = () => { if (window.confirm(`Delete section "${activeTab?.label}"?`)) applyGroupAction(activeTab.id, "delete"); };

    return (
        <Card title="Stats and Insights" testId="pgc-skaters">
            {/* Period filter now lives in the unified Game Summary control bar. */}

            {/* 1 — Team Summary */}
            <CollapsibleSection id="team" title="Team Summary" accent="#38BDF8">
                <TeamSummaryTable rows={insights.rows} goalieSplits={goalieSplits} teams={teams} data={data} period={insPeriod} periodColor={periodColor} />
            </CollapsibleSection>

            {/* 2 — Goalie Summary */}
            {hasGoalies && (
                <CollapsibleSection id="goalie" title="Goalie Summary" accent="#5BD1B7">
                    <div className="grid grid-cols-1 gap-6">
                        {teams.map((tid) => (
                            <div key={tid} className="min-w-0" data-testid={`pgc-goalie-side-${tid}`}>
                                <TeamHead tid={tid} />
                                <GoalieStatsTable rows={goalieSplits.filter((gl) => gl.team_id === tid)} period={insPeriod} periodColor={periodColor} />
                            </div>
                        ))}
                    </div>
                    {admin && <AdminRaw k="goalies" value={data.goalies} />}
                </CollapsibleSection>
            )}

            {/* 3 — Player Summary */}
            <CollapsibleSection id="player" title="Stat Summary" accent="#9D4CDD"
                right={admin && (
                    <div className="flex items-center gap-1" data-testid="pgc-group-admin">
                        <button type="button" onClick={renameGroup} data-testid="pgc-group-rename" title="Rename section" className="p-1 rounded text-[#64748B] hover:text-[#9D4CDD]"><Settings2 className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={deleteGroup} data-testid="pgc-group-delete" title="Delete section" className="p-1 rounded text-[#64748B] hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={() => applyGroupAction(null, "add")} data-testid="pgc-group-add" title="Add section" className="p-1 rounded text-[#64748B] hover:text-[#5BD1B7]"><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                )}>
                <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="pgc-insights-tabs">
                    {playerTabs.map((t) => (
                        <button key={t.id} type="button" data-testid={`pgc-instab-${t.id}`}
                            onClick={() => setInsTab(t.id)}
                            className={`px-2.5 py-1 rounded-md border text-[11px] font-bold uppercase tracking-wider transition-colors ${insTab === t.id ? "border-[#9D4CDD] bg-[#9D4CDD]/15 text-white" : "border-[#1E2233] text-[#64748B] hover:text-white"}`}>{t.label}</button>
                    ))}
                </div>
                <div className="grid lg:grid-cols-2 gap-6">
                    {teams.map((tid) => (
                        <div key={tid} className="min-w-0" data-testid={`pgc-player-side-${tid}`}>
                            <TeamHead tid={tid} />
                            <InsightsTable rows={insights.rows.filter((r) => r.team_id === tid && (!soMode || (r.soAtt || 0) > 0))} matchMap={matchMap} cols={skaterCols} periodColor={periodColor} admin={admin && !soMode} groupId={activeTab.id} onColAction={applyColAction} testId={`pgc-${soMode ? "so" : activeTab.id}-table`} soMode={soMode} />
                        </div>
                    ))}
                </div>
            </CollapsibleSection>

            {/* Player Matching — moved to the BOTTOM so admin toggling never shifts the tables */}
            {admin && (
                <div className="mt-5 border-t border-[#1E2233] pt-4" data-testid="pgc-admin-matching">
                    <button type="button" onClick={() => setMatchOpen((v) => !v)} data-testid="pgc-matching-toggle"
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-[#1E2233] bg-[#0E0F16] text-left hover:border-[#9D4CDD]/40 transition-colors">
                        <span className="text-[11px] uppercase tracking-wider font-bold text-[#94A3B8]">Player Matching {sync?.unmatchedCount ? <span className="ml-1 text-[#F6C453]">({sync.unmatchedCount} unlinked)</span> : null}</span>
                        <ChevronDown className={`h-4 w-4 text-[#64748B] transition-transform ${matchOpen ? "rotate-180" : ""}`} />
                    </button>
                    {matchOpen && <div className="mt-2"><PWHLMatchReview sync={sync} /></div>}
                </div>
            )}
            {admin && <AdminRaw k="skaters" value={data.skaters} />}
        </Card>
    );
}

// Team-level aggregate (two rows) — reflects the active period toggle. Counting
// stats sum across the team; SH% / FO% derive. Period views only show stats the
// play-by-play splits by period (others read "—").
// Team-level game stats — proper team metrics (GF/GA, special teams, shots),
// NOT skater sums like assists/points. Computed from goals + penalties + the
// team's own goalie. Period toggle filters everything the feed splits by period.
function teamGameStats(tid, data, rows, goalieSplits, period) {
    const inP = (p) => inPeriod(period, p);
    const homeId = data?.meta?.home_team_id, awayId = data?.meta?.away_team_id;
    const opp = tid === homeId ? awayId : homeId;
    let gf = 0, ga = 0, ppg = 0, ppga = 0, shg = 0, shga = 0;
    (data?.goals || []).forEach((gl) => {
        if (!inP(gl.period)) return;
        const st = gl.strength;
        if (gl.team_id === tid) { gf++; if (st === "PP") ppg++; else if (st === "SH") shg++; }
        else if (gl.team_id === opp) { ga++; if (st === "PP") ppga++; else if (st === "SH") shga++; }
    });
    let ppo = 0, pk = 0;
    (data?.penalties || []).forEach((pn) => {
        if (!inP(pn.period)) return;
        if (![2, 4, 5].includes(Number(pn.minutes) || 0)) return;   // PP-eligible minors/majors
        if (pn.team_id === opp) ppo++; else if (pn.team_id === tid) pk++;
    });
    let sf = 0, pim = 0, hits = 0, blocks = 0, fow = 0, foa = 0;
    rows.filter((r) => r.team_id === tid).forEach((r) => {
        if (period === "combined") { sf += r.shots || r.sog || 0; pim += r.pim || 0; hits += r.hits || 0; blocks += r.blocks || 0; fow += r.fow || 0; foa += (r.foa != null ? r.foa : (r.fow || 0) + (r.fol || 0)); }
        else { const b = periodBucket(r, period); sf += b.sog || 0; pim += b.pim || 0; hits += b.hits || 0; blocks += b.blocks || 0; fow += b.fow || 0; foa += (b.fow || 0) + (b.fol || 0); }
    });
    let sa = 0, sv = 0;
    goalieSplits.filter((gl) => gl.team_id === tid).forEach((gl) => {
        if (period === "combined") { sa += gl.combined?.sa || 0; sv += gl.combined?.sv || 0; }
        else { const p = goaliePeriodSum(gl, period); sa += p.sa || 0; sv += Math.max(0, (p.sa || 0) - (p.ga || 0)); }
    });
    return { gf, ga, sf, sa, svp: sa ? sv / sa : null, ppg, ppga, shg, shga, ppo, pk,
        ppPct: ppo ? ppg / ppo : null, pkPct: pk ? (pk - ppga) / pk : null, pim, hits, blocks, fow, foa };
}
// Column set for the team summary. `per` = value is period-splittable.
const TEAM_STAT_COLS = [
    { k: "gf", label: "GF", per: true }, { k: "ga", label: "GA", per: true },
    { k: "sf", label: "SF", per: true, w: 44 }, { k: "sa", label: "SA", per: true, w: 44 },
    { k: "svp", label: "SV%", per: true, w: 54, pct: true }, { k: "ppg", label: "PPG", per: true, w: 46 },
    { k: "ppo", label: "PPO", per: true, w: 46 }, { k: "ppPct", label: "PP%", per: true, w: 54, pct: true },
    { k: "ppga", label: "PPGA", per: true, w: 50 }, { k: "pkPct", label: "PK%", per: true, w: 54, pct: true },
    { k: "shg", label: "SHG", per: true, w: 46 }, { k: "shga", label: "SHGA", per: true, w: 50 },
    { k: "fo", label: "FO%", per: true, w: 54, pct: true }, { k: "pim", label: "PIM", per: true },
    { k: "hits", label: "Hits", per: true, w: 46 }, { k: "blocks", label: "Blocks", per: true, w: 56 },
];
function TeamSummaryTable({ rows, goalieSplits, teams, data = null, period = "combined", periodColor = null }) {
    const headStyle = (per) => (periodColor && per ? { color: periodColor, backgroundColor: `${periodColor}1A`, boxShadow: `inset 0 -2px 0 ${periodColor}` } : undefined);
    const cellBg = (per) => (periodColor && per ? { backgroundColor: `${periodColor}12` } : undefined);
    const render = (s, c) => {
        if (!c.per && period !== "combined") return "—";
        if (c.k === "fo") return s.foa ? _pct((s.fow / s.foa) * 100) : "—";
        if (c.k === "svp") return s.svp == null ? "—" : s.svp.toFixed(3).replace(/^0/, "");
        if (c.k === "ppPct") return s.ppPct == null ? "—" : _pct(s.ppPct * 100);
        if (c.k === "pkPct") return s.pkPct == null ? "—" : _pct(s.pkPct * 100);
        return s[c.k] ?? 0;
    };
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="pgc-team-summary-table" style={{ minWidth: 120 + TEAM_STAT_COLS.reduce((a, c) => a + (c.w || 40), 0) }}>
                <thead><tr className="text-[#64748B] text-[10px] uppercase tracking-wider border-b border-white/10">
                    <th className="text-left px-1 py-2">Team</th>
                    {TEAM_STAT_COLS.map((c) => <th key={c.k} className="text-center px-1 cursor-help" style={headStyle(c.per)}><HoverCard title={titleCase(STAT_TIPS[c.label] || c.label)} className="items-center justify-center">{c.label}</HoverCard></th>)}
                </tr></thead>
                <tbody>
                    {teams.map((tid) => {
                        const s = teamGameStats(tid, data, rows, goalieSplits, period);
                        return (
                            <tr key={tid} className="border-t border-[#1E2233]/70" data-testid={`pgc-team-summary-${tid}`}>
                                <td className="px-1 py-2"><span className="inline-flex items-center gap-1.5"><TeamBadge teamId={tid} size="sm" /><span className="text-white font-semibold truncate">{teamAbbr(tid)}</span></span></td>
                                {TEAM_STAT_COLS.map((c) => {
                                    const v = render(s, c);
                                    return <td key={c.k} style={cellBg(c.per)} className={`text-center px-1 font-mono-stat tabular-nums ${v && v !== "—" && v !== 0 ? "text-[#CBD5E1]" : "text-[#475569]"}`}>{v}</td>;
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ---- Detailed Team Summary card (record + grouped multi-row team stats) ----
const _goalSecs = (t) => { const [m, s] = String(t || "0:0").split(":"); return (parseInt(m, 10) || 0) * 60 + (parseInt(s, 10) || 0); };
function teamFullStats(tid, data, rows, goalieSplits, period) {
    const inP = (p) => inPeriod(period, p);
    const meta = data?.meta || {};
    const homeId = meta.home_team_id, awayId = meta.away_team_id;
    const opp = tid === homeId ? awayId : homeId;
    const goals = data?.goals || [];
    const mkGB = () => ({ es: 0, pp: 0, sh: 0, en: 0, ps: 0, ot: 0, so: 0, total: 0 });
    const gfB = mkGB(), gaB = mkGB();
    goals.forEach((gl) => {
        if (!inP(gl.period)) return;
        const bkt = gl.team_id === tid ? gfB : (gl.team_id === opp ? gaB : null);
        if (!bkt) return;
        bkt.total++;
        if (gl.strength === "PP") bkt.pp++; else if (gl.strength === "SH") bkt.sh++; else bkt.es++;
        if (gl.empty_net) bkt.en++;  // EN is a situation, not a strength — still counts toward ES
        if ((Number(gl.period) || 0) >= 4) bkt.ot++;
    });
    const so = data?.shootout || [];
    const soWin = meta.shootout ? ((meta.home_score || 0) > (meta.away_score || 0) ? homeId : awayId) : null;
    if (meta.shootout && (period === "combined" || period === "so")) {
        if (soWin === tid) gfB.so = 1; else if (soWin === opp) gaB.so = 1;
    }
    const ord = goals.slice().sort((a, b) => (a.period - b.period) || (_goalSecs(a.time) - _goalSecs(b.time)));
    const fgFor = ord.length ? ord[0].team_id === tid : false;
    const lgFor = ord.length ? ord[ord.length - 1].team_id === tid : false;
    let ppo = 0, pk = 0;
    (data?.penalties || []).forEach((pn) => { if (!inP(pn.period)) return; if (![2, 4, 5].includes(Number(pn.minutes) || 0)) return; if (pn.team_id === opp) ppo++; else if (pn.team_id === tid) pk++; });
    let sf = 0, pim = 0, hits = 0, blocks = 0, psa = 0;
    rows.filter((r) => r.team_id === tid).forEach((r) => {
        if (period === "combined") { sf += r.shots || r.sog || 0; pim += r.pim || 0; hits += r.hits || 0; blocks += r.blocks || 0; psa += r.psa || 0; }
        else { const b = periodBucket(r, period); sf += b.sog || 0; pim += b.pim || 0; hits += b.hits || 0; blocks += b.blocks || 0; }
    });
    // Faceoffs straight from the play-by-play (per-player FOL isn't in the feed):
    // each faceoff event carries the winner's team_id, so a team's losses in a
    // period equal the opponent's wins — makes the per-toggle split accurate.
    let fow = 0, fol = 0;
    (data?.pxp || []).forEach((e) => { if (e.type !== "faceoff" || !inP(e.period)) return; if (e.team_id === tid) fow++; else if (e.team_id === opp) fol++; });
    const soa = so.filter((s) => s.team_id === tid).length;
    const ts = tid === homeId ? (meta.home_score || 0) : (meta.away_score || 0);
    const os = tid === homeId ? (meta.away_score || 0) : (meta.home_score || 0);
    const won = ts > os;
    const rec = meta.shootout ? (won ? "SOW" : "SOL") : (meta.overtime ? (won ? "OTW" : "OTL") : (won ? "W" : "L"));
    return {
        gfB, gaB, fgFor, lgFor, ppo, pk, ppga: gaB.pp, ppg: gfB.pp, shg: gfB.sh, sha: gaB.sh,
        sf, pim, hits, blocks, fow, fol, psa, soa, rec,
        fo: (fow + fol) ? fow / (fow + fol) : null,
        shp: sf ? gfB.total / sf : null,
        ppPct: ppo ? gfB.pp / ppo : null, pkPct: pk ? (pk - gaB.pp) / pk : null,
    };
}
const _pctv = (v) => (v == null ? "—" : _pct(v * 100));
// Capitalise the first letter of each word (for hover-box stat names).
export const titleCase = (s) => String(s || "").replace(/\b([a-z])/g, (m, c) => c.toUpperCase());
// Reusable REG/OT/SO Win-Loss record grid (shared by team header + coaching).
function RecordGrid({ dec, full = false }) {
    const cells = [
        { g: "REG", w: dec === "W", l: dec === "L" },
        { g: "OT", w: dec === "OTW", l: dec === "OTL" },
        { g: "SO", w: dec === "SOW", l: dec === "SOL" },
    ];
    return (
        <div className={`grid grid-cols-3 gap-1.5 ${full ? "w-full" : ""}`}>
            {cells.map((c) => (
                <div key={c.g} className={`rounded-lg border border-white/10 overflow-hidden ${full ? "" : "w-[64px]"}`}>
                    <div className="text-[8px] font-black uppercase tracking-wider text-center text-[#64748B] bg-white/[0.03] py-0.5">{c.g}</div>
                    <div className="grid grid-cols-2">
                        <div className={`text-center text-[10px] font-black py-1 ${c.w ? "bg-[#5BD1B7]/25 text-[#5BD1B7]" : "text-[#3A3F55]"}`} title="Win">W</div>
                        <div className={`text-center text-[10px] font-black py-1 border-l border-white/10 ${c.l ? "bg-[#F87171]/25 text-[#F87171]" : "text-[#3A3F55]"}`} title="Loss">L</div>
                    </div>
                </div>
            ))}
        </div>
    );
}
function Well({ label, value, sub, tip, accent, affected = false }) {
    const hl = useHl();
    const dim = value === "—" || value === 0 || value === "0" || value == null;
    const boxLit = affected && accent && (hl === "all" || !dim);
    return (
        <HoverCard title={titleCase(tip || label)} className="w-[54px]">
            <div className="w-full rounded-lg border px-1 py-1.5 text-center transition-colors"
                style={boxLit ? { backgroundColor: `${accent}26`, borderColor: `${accent}59` } : BOX_BASE}>
                <div className="text-[8px] font-black uppercase tracking-wider text-[#64748B] leading-none mb-1 whitespace-nowrap">{label}</div>
                <div className={`font-mono-stat text-[11px] tabular-nums leading-none ${dim ? "text-[#3A3F55]" : "text-white font-bold"}`} style={!dim && boxLit ? { color: accent } : undefined}>{value}</div>
                {sub != null && <div className="text-[9px] font-mono-stat tabular-nums text-[#94A3B8] mt-0.5 leading-none">{sub}</div>}
            </div>
        </HoverCard>
    );
}
function TSec({ title, children, Icon = null }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8] mb-1.5 flex items-center gap-1.5">{Icon && <Icon className="h-3 w-3" />}{title}</div>
            <div className="flex flex-wrap gap-1.5">{children}</div>
        </div>
    );
}
function TeamSummaryCard({ teams, data, rows, goalieSplits, coaches = [], period = "combined", periodColor = null }) {
    const yn = (b) => (b ? 1 : 0);
    const staff = (re) => (coaches || []).find((c) => re.test(_staffRole(c)));
    const aff = !!periodColor;
    return (
        <div className="space-y-4" data-testid="pgc-team-summary-card">
            {teams.map((tid) => {
                const s = teamFullStats(tid, data, rows, goalieSplits, period);
                const hc = staff(/head coach/i), gm = staff(/general manager|gm/i);
                return (
                    <div key={tid} className="space-y-3">
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5 space-y-3.5" data-testid={`pgc-team-summary-${tid}`}>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <span className="inline-flex items-center gap-2"><TeamBadge teamId={tid} size="sm" /><span className="text-white font-bold text-sm">{teamAbbr(tid)}</span></span>
                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1" data-testid={`pgc-team-fglg-${tid}`}>
                                    {[["FG", s.fgFor, "Scored first goal"], ["LG", s.lgFor, "Scored last goal"]].map(([lbl, val, tp]) => (
                                        <div key={lbl} title={tp} className="rounded-md border border-white/10 bg-white/[0.02] px-1.5 py-0.5 text-center leading-none">
                                            <div className="text-[8px] font-black uppercase tracking-wider text-[#64748B] mb-0.5">{lbl}</div>
                                            <div className="text-xs font-mono-stat font-bold text-white">{yn(val)}</div>
                                        </div>
                                    ))}
                                </div>
                                <RecordGrid dec={s.rec} />
                            </div>
                        </div>
                        <TSec title="Goals For" Icon={Goal}>
                            <Well label="GF" value={s.gfB.total} tip="Goals for" accent={periodColor} affected={aff} />
                            <Well label="ES" value={s.gfB.es} tip="Even-strength goals for" accent={periodColor} affected={aff} />
                            <Well label="PP" value={s.gfB.pp} tip="Power-play goals for" accent={periodColor} affected={aff} />
                            <Well label="SH" value={s.gfB.sh} tip="Short-handed goals for" accent={periodColor} affected={aff} />
                            <Well label="EN" value={s.gfB.en} tip="Empty-net goals for" accent={periodColor} affected={aff} />
                            <Well label="PS" value={s.gfB.ps} tip="Penalty-shot goals for" accent={periodColor} affected={aff} />
                        </TSec>
                        <TSec title="Goals Against" Icon={ShieldAlert}>
                            <Well label="GA" value={s.gaB.total} tip="Goals against" accent={periodColor} affected={aff} />
                            <Well label="ES" value={s.gaB.es} tip="Even-strength goals against" accent={periodColor} affected={aff} />
                            <Well label="PP" value={s.gaB.pp} tip="Power-play goals against" accent={periodColor} affected={aff} />
                            <Well label="SH" value={s.gaB.sh} tip="Short-handed goals against" accent={periodColor} affected={aff} />
                            <Well label="EN" value={s.gaB.en} tip="Empty-net goals against" accent={periodColor} affected={aff} />
                            <Well label="PS" value={s.gaB.ps} tip="Penalty-shot goals against" accent={periodColor} affected={aff} />
                        </TSec>
                        <div className="flex flex-nowrap gap-x-2 gap-y-3 overflow-x-auto">
                            <TSec title="Special Teams" Icon={Zap}>
                                <Well label="PP" value={`${s.ppg}/${s.ppo}`} sub={_pctv(s.ppPct)} tip="Power play — goals / opportunities (PP%)" accent={periodColor} affected={aff} />
                                <Well label="PK" value={`${Math.max(0, s.pk - s.ppga)}/${s.pk}`} sub={_pctv(s.pkPct)} tip="Penalty kill — killed / times short-handed (PK%)" accent={periodColor} affected={aff} />
                            </TSec>
                            <TSec title="Situations" Icon={Flag}>
                                <Well label="PS" value={`${s.gfB.ps}/${s.psa}`} sub={s.psa ? _pct((s.gfB.ps / s.psa) * 100) : "—"} tip="Penalty shots — goals / attempts (PS%)" accent={periodColor} affected={aff} />
                                <Well label="SO" value={`${s.gfB.so}/${s.soa}`} sub={s.soa ? _pct((s.gfB.so / s.soa) * 100) : "—"} tip="Shootout — goals / attempts (SO%)" accent={periodColor} affected={aff} />
                            </TSec>
                            <TSec title="Shots" Icon={Crosshair}>
                                <Well label="Shots" value={s.sf} sub={_pctv(s.shp)} tip="Shots on goal (SH%)" accent={periodColor} affected={aff} />
                            </TSec>
                            <TSec title="Faceoffs" Icon={CircleDot}>
                                <Well label="FO" value={`${s.fow}/${s.fow + s.fol}`} sub={_pctv(s.fo)} tip="Faceoffs won / taken (FO%)" accent={periodColor} affected={aff} />
                            </TSec>
                        </div>
                        <TSec title="Physical" Icon={Shield}>
                            <Well label="Hits" value={s.hits} tip="Hits" accent={periodColor} affected={aff} />
                            <Well label="Blocks" value={s.blocks} tip="Blocked shots" accent={periodColor} affected={aff} />
                            <Well label="PIM" value={s.pim} tip="Penalty minutes" accent={periodColor} affected={aff} />
                        </TSec>
                    </div>
                    {(hc || gm) && (
                        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5" data-testid={`pgc-team-coach-record-${tid}`}>
                            <div className="text-[11px] uppercase tracking-wider font-bold text-[#94A3B8] mb-2 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />GM &amp; HC — Game Result</div>
                            <div className="space-y-1.5">
                                {[["Head Coach", hc], ["General Manager", gm]].filter(([, c]) => c).map(([role, c]) => (
                                    <div key={role} className="flex items-center justify-between gap-3 rounded-lg bg-white/[0.02] border border-white/5 px-2.5 py-1.5">
                                        <div className="min-w-0">
                                            <div className="text-[8px] font-black uppercase tracking-wider text-[#64748B] leading-none">{role}</div>
                                            <div className="text-white font-bold text-xs truncate">{c.name}</div>
                                        </div>
                                        <RecordGrid dec={s.rec} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    </div>
                );
            })}
        </div>
    );
}



// ===================== Shared: synced coaching staff =====================
// Admin-reorderable widget stack (drag to reorder; order saved per-browser).
function ReorderableWidgets({ items, admin, storageKey = "pgc-widget-order" }) {
    const [order, setOrder] = useState(() => { try { return JSON.parse(localStorage.getItem(storageKey)) || []; } catch { return []; } });
    const dragFrom = useRef(null);
    const [overIdx, setOverIdx] = useState(null);
    const ids = items.map((i) => i.id);
    const ordered = [...order.filter((id) => ids.includes(id)), ...ids.filter((id) => !order.includes(id))];
    const commit = (arr) => { setOrder(arr); try { localStorage.setItem(storageKey, JSON.stringify(arr)); } catch { /* ignore */ } };
    const move = (from, to) => { if (from === to) return; const arr = [...ordered]; const [x] = arr.splice(from, 1); arr.splice(to, 0, x); commit(arr); };
    return (
        <div className="space-y-6" data-testid="pgc-widgets">
            {ordered.map((id, idx) => {
                const it = items.find((i) => i.id === id);
                if (!it) return null;
                return (
                    <div key={id} data-testid={`pgc-widget-${id}`}
                        draggable={admin}
                        onDragStart={() => { dragFrom.current = idx; }}
                        onDragOver={(e) => { if (admin) { e.preventDefault(); setOverIdx(idx); } }}
                        onDrop={() => { if (admin && dragFrom.current != null) move(dragFrom.current, idx); dragFrom.current = null; setOverIdx(null); }}
                        onDragEnd={() => { dragFrom.current = null; setOverIdx(null); }}
                        className={`relative ${admin ? "cursor-move" : ""} ${admin && overIdx === idx ? "ring-2 ring-[#9D4CDD]/60 rounded-2xl" : ""}`}>
                        {admin && (
                            <div className="absolute -top-2 left-4 z-20 flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#9D4CDD] text-white text-[9px] font-bold uppercase tracking-wider shadow" data-testid={`pgc-widget-drag-${id}`}>
                                <GripVertical size={11} /> Drag
                            </div>
                        )}
                        {it.node}
                    </div>
                );
            })}
        </div>
    );
}

function CoachingStaff({ side, coaches }) {
    if (!coaches.length) return null;
    return (
        <Card title="Coaching Staff" icon={Users} testId={`pgc-coaches-${side}`}>
            <div className="grid sm:grid-cols-2 gap-3">
                {coaches.map((c, i) => (
                    <div key={i} className="flex items-center justify-between rounded-md bg-white/[0.03] border border-[#1E2233] px-3 py-2" data-testid={`pgc-coach-${c.coach_id || i}`}>
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{c.role}</div>
                            <div className="text-sm text-white truncate">
                                {c.coach_id ? <Link to={`/coaches/${c.coach_id}`} className="hover:text-[#C99BF0] transition-colors">{c.name}</Link> : c.name}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
}

// ===================== Game Roster tab (synced dressed roster) =====================
function GameRosterTab({ side, data, teamId, matchMap, photoFor, insights, goalieSplits, insPeriod, admin }) {
    const roster = (data.rosters || {})[side] || [];
    const { data: teams = [] } = useQuery({ queryKey: ["teams"], queryFn: getTeams, staleTime: 5 * 60 * 1000 });
    const color = teams.find((t) => t.id === teamId)?.primary_color || teamColor(teamId) || "#9D4CDD";
    const _posOf = (p) => String(p.position || "").trim();
    const isGoalie = (p) => p.is_goalie || /^g$/i.test(_posOf(p));
    const isDef = (p) => /^(l|r)?d$/i.test(_posOf(p));
    const forwards = roster.filter((p) => !isGoalie(p) && !isDef(p));
    const defense = roster.filter((p) => !isGoalie(p) && isDef(p));
    const goalies = roster.filter((p) => isGoalie(p));

    // Players under contract with the team this season who did NOT dress for this
    // game — shown in their own section so the roster reads as a full snapshot.
    const extras = (data.season_extras || {})[side] || [];
    const dressedIds = new Set(roster.map((p) => matchMap[p.pwhl_id]).filter(Boolean));
    const notDressed = extras.filter((p) => !dressedIds.has(p.id));
    const ndForwards = notDressed.filter((p) => !isGoalie(p) && !isDef(p));
    const ndDefense = notDressed.filter((p) => !isGoalie(p) && isDef(p));
    const ndGoalies = notDressed.filter((p) => isGoalie(p));

    const RosterPlayer = ({ p }) => {
        const rid = p.not_dressed ? p.id : matchMap[p.pwhl_id];
        const photo = p.not_dressed ? p.photo_url : photoFor(p.pwhl_id);
        return (
            <div className="flex items-center gap-3 py-2 border-b border-[#1E2233] last:border-0" data-testid={`pgc-roster-player-${p.pwhl_id || p.id}`}>
                <PlayerAvatar photo={photo} name={p.name} playerId={rid} size="sm" showTierRing={false} />
                <div className="min-w-0 flex-1">
                    <div className="text-sm text-white truncate">
                        {rid ? <Link to={`/players/${rid}`} className="hover:text-[#C99BF0] transition-colors">{p.name}</Link> : p.name}
                        {p.rookie && <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/10 text-[#94A3B8]">R</span>}
                    </div>
                </div>
                {/* right columns: Starter | Special (C/A) | Position | J# */}
                <span className="w-14 text-center shrink-0">
                    {p.starter ? <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#9D4CDD]/25 text-[#E9D5FF]">Start</span> : <span className="text-[#3A3F55]">—</span>}
                </span>
                <span className="w-6 text-center shrink-0">
                    {p.captain === "C" ? <span className="text-[10px] font-black px-1 py-0.5 rounded bg-[#F6C453]/25 text-[#F6C453] border border-[#F6C453]/40" title="Captain">C</span>
                        : p.captain === "A" ? <span className="text-[10px] font-black px-1 py-0.5 rounded bg-white/10 text-[#CBD5E1] border border-white/20" title="Alternate Captain">A</span>
                            : <span className="text-[#3A3F55]">—</span>}
                </span>
                <span className="text-[11px] text-[#94A3B8] w-9 text-center shrink-0">{p.position || "—"}</span>
                <span className="font-mono-stat text-sm text-[#64748B] w-9 text-right shrink-0">{p.jersey ? `#${p.jersey}` : ""}</span>
            </div>
        );
    };

    const GroupHead = () => (
        <div className="flex items-center gap-3 pb-1 text-[8px] font-black uppercase tracking-wider text-[#475569]">
            <span className="w-8 shrink-0" />
            <span className="flex-1">Player</span>
            <span className="w-14 text-center shrink-0">Starter</span>
            <span className="w-6 text-center shrink-0">C/A</span>
            <span className="w-9 text-center shrink-0">Pos</span>
            <span className="w-9 text-right shrink-0">#</span>
        </div>
    );

    const Group = ({ label, list }) => (
        list.length === 0 ? null : (
            <div data-testid={`pgc-roster-group-${label.toLowerCase()}`}>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1" style={{ color }}>{label} <span className="text-[#64748B]">({list.length})</span></div>
                <GroupHead />
                <div>{list.map((p) => <RosterPlayer key={p.pwhl_id || p.id} p={p} />)}</div>
            </div>
        )
    );

    return (
        <div className="space-y-6" data-testid={`pgc-roster-tab-${side}`}>
            <Card title={`${teamName(teamId)} Team Roster`} icon={ClipboardList} testId={`pgc-roster-${side}`}>
                {roster.length === 0 ? (
                    <div className="py-10 text-center" data-testid={`pgc-roster-unavailable-${side}`}>
                        <ClipboardList size={26} className="mx-auto text-[#475569] mb-2" />
                        <div className="font-heading text-base font-bold text-white">No Roster Available</div>
                        <div className="text-xs text-[#94A3B8] mt-1">A game roster hasn't been recorded for this game yet.</div>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-x-8 gap-y-5 items-start">
                        <Group label="Forwards" list={forwards} />
                        {/* Defense with Goaltenders stacked directly beneath it */}
                        <div className="space-y-5">
                            <Group label="Defense" list={defense} />
                            <Group label="Goaltenders" list={goalies} />
                        </div>
                    </div>
                )}
                {notDressed.length > 0 && (
                    <div className="mt-6 border-t border-[#1E2233] pt-4" data-testid={`pgc-roster-notdressed-${side}`}>
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B] mb-1">On Roster · Did Not Dress <span className="text-[#475569]">({notDressed.length})</span></div>
                        <div className="text-[11px] text-[#475569] mb-3">Under contract with {teamName(teamId)} this season but not dressed for this game.</div>
                        <div className="grid md:grid-cols-2 gap-x-8 gap-y-5 items-start opacity-90">
                            <Group label="Forwards" list={ndForwards} />
                            <div className="space-y-5">
                                <Group label="Defense" list={ndDefense} />
                                <Group label="Goaltenders" list={ndGoalies} />
                            </div>
                        </div>
                    </div>
                )}
            </Card>
            {insights && (
                <RosterSummary teamId={teamId} data={data} insights={insights} goalieSplits={goalieSplits} insPeriod={insPeriod} photoFor={photoFor} matchMap={matchMap} />
            )}
            <GameStaff teamId={teamId} side={side} />
        </div>
    );
}

// Team Summary (aggregate) + Goalie Summary for one team — shown on the Team
// Summary view alongside the roster. Skaters live on the Player Summary view.
function RosterSummary({ teamId, data, insights, goalieSplits, insPeriod, photoFor = null, matchMap = {} }) {
    const pm = PERIOD_META[insPeriod] || PERIOD_META.combined;
    const periodColor = insPeriod === "combined" ? null : pm.color;
    const teamRows = (insights.rows || []).filter((r) => r.team_id === teamId);
    const teamGoalies = (goalieSplits || []).filter((gl) => gl.team_id === teamId);
    return (
        <Card title="Team Summary" testId={`pgc-rostersum-${teamId}`}>
            <CollapsibleSection id={`team-${teamId}`} title="Team Summary" accent="#38BDF8">
                <TeamSummaryTable rows={teamRows} goalieSplits={teamGoalies} teams={[teamId]} data={data} period={insPeriod} periodColor={periodColor} />
            </CollapsibleSection>
            {teamGoalies.length > 0 && (
                <CollapsibleSection id={`goalie-${teamId}`} title="Goalie Summary" accent="#5BD1B7">
                    <GoalieStatsTable rows={teamGoalies} period={insPeriod} periodColor={periodColor} photoFor={photoFor} matchMap={matchMap} />
                </CollapsibleSection>
            )}
        </Card>
    );
}

// Player Summary view — skaters stat table (with faces) for one team, plus that
// team's goalie summary and a small "Not Dressed" list underneath.
// Shared leaderboards-style pill-box stat table (identity cell + stat pills),
// matching the player InsightsTable visual. Used for Team / Goalie / Coach rows
// so the whole Stat Summary reads as one consistent Leaderboards-style stack.
// EXACT Leaderboards style — floating rows as cards (mirrors LeaderboardTable).
function LbStatTable({ testId, identityHead, idWidth = 200, cols, rows, periodColor = null, renderIdentity }) {
    if (!rows.length) return <Empty>No data.</Empty>;
    const minW = idWidth + cols.reduce((s, c) => s + (c.width || 60), 0);
    const CELL = "bg-[#111219] group-hover:bg-[#191b27] transition-colors border-y border-white/[0.07]";
    return (
        <div className="rounded-2xl bg-[#0E0F16] border border-white/10 shadow-xl shadow-black/40 p-1 sm:p-2.5 overflow-x-auto" data-testid={`${testId}-wrap`}>
            <table className="text-[11px] sm:text-sm border-separate" data-testid={testId} style={{ borderSpacing: "0 8px", tableLayout: "fixed", width: "100%", minWidth: minW }}>
                <colgroup><col style={{ width: idWidth }} />{cols.map((c, i) => <col key={i} style={{ width: c.width || 60 }} />)}</colgroup>
                <thead><tr className="text-white/35 uppercase text-[9px] sm:text-[10px] tracking-[0.18em]">
                    <th className="text-left px-2 py-1 sticky left-0 z-20 font-bold" style={{ background: "#0E0F16" }}>{identityHead}</th>
                    {cols.map((c, i) => (
                        <th key={i} className="text-center px-1 sm:px-2 py-1 font-bold truncate" style={periodColor && c.per ? { color: periodColor } : undefined}>
                            <HoverCard title={titleCase(STAT_TIPS[c.label] || c.tip || c.label)} className="items-center justify-center">{c.label}</HoverCard>
                        </th>
                    ))}
                </tr></thead>
                <tbody>
                    {rows.map((r, ri) => (
                        <tr key={ri} className="group" data-testid={`${testId}-row-${ri}`}>
                            <td className={`${CELL} sticky left-0 z-10 text-left px-2 py-2 rounded-l-2xl border-l truncate`}>{renderIdentity(r)}</td>
                            {cols.map((c, ci) => {
                                const val = c.fn(r);
                                const dim = val === "—" || val === 0 || val === "0" || val == null;
                                const lit = !!(periodColor && c.per) && !dim;
                                const isLast = ci === cols.length - 1;
                                return (
                                    <td key={ci} className={`${CELL} text-center px-1 sm:px-2 py-2 ${isLast ? "rounded-r-2xl border-r" : ""}`}>
                                        <StatBlock value={val} color={lit ? periodColor : null} />
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// Team stat value formatter (mirrors the legacy Team Summary render).
function teamStatRender(s, c, period) {
    if (!c.per && period !== "combined") return "—";
    if (c.k === "fo") return s.foa ? _pct((s.fow / s.foa) * 100) : "—";
    if (c.k === "svp") return s.svp == null ? "—" : s.svp.toFixed(3).replace(/^0/, "");
    if (c.k === "ppPct") return s.ppPct == null ? "—" : _pct(s.ppPct * 100);
    if (c.k === "pkPct") return s.pkPct == null ? "—" : _pct(s.pkPct * 100);
    return s[c.k] ?? 0;
}

// Goalie stat line for the active period. SV/SA, SV%, MIN, GA, P, G, A, PIM all
// translate PER PERIOD / game stage (same model as the Players summary); TOI is
// always the game total; SO / Result / Shootout are game-level.
function goalieRowStats(gl, period) {
    const per = gl.periods || {};
    const one = (p) => per[p] || { sa: 0, ga: 0, secs: 0, pim: 0, g: 0, a: 0 };
    const c = gl.combined || { sa: 0, sv: 0, ga: 0 };
    const p1 = one(1), p2 = one(2), p3 = one(3), ot = one(4);
    const soO = { sa: gl.soSa || 0, ga: gl.soGa || 0 };
    const regSa = Math.max(0, (c.sa || 0) - ot.sa - soO.sa), regGa = Math.max(0, (c.ga || 0) - ot.ga - soO.ga);
    const regSecs = (p1.secs || 0) + (p2.secs || 0) + (p3.secs || 0);
    const totalSecs = gl.toi_seconds || (regSecs + (ot.secs || 0));
    const sum = (k) => (p1[k] || 0) + (p2[k] || 0) + (p3[k] || 0) + (ot[k] || 0);
    const pickBy = {
        combined: { sa: c.sa || 0, ga: c.ga || 0, secs: totalSecs, g: gl.goals || 0, a: gl.assists || 0, pim: gl.pim || 0 },
        regulation: { sa: regSa, ga: regGa, secs: regSecs, g: sum("g"), a: sum("a"), pim: sum("pim") },
        p1, p2, p3, ot,
        so: { sa: soO.sa, ga: soO.ga, secs: 0, g: 0, a: 0, pim: 0 },
    };
    const pk = pickBy[period] || pickBy.combined;
    const sv = Math.max(0, (pk.sa || 0) - (pk.ga || 0));
    const mmss = (s) => { s = Math.max(0, Math.round(s || 0)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };
    const g = pk.g || 0, a = pk.a || 0;
    return {
        svsa: `${sv}/${pk.sa || 0}`,
        svp: pk.sa ? (sv / pk.sa).toFixed(3).replace(/^0/, "") : "—",
        toi: gl.toi || (totalSecs ? mmss(totalSecs) : "—"),
        min: gl.toi || (totalSecs ? mmss(totalSecs) : "—"),
        ga: pk.ga || 0,
        p: g + a, g, a, pim: pk.pim || 0,
        so: gl.shutout ? 1 : 0, dec: gl.decision || "—",
        soaSoga: (soO.sa || soO.ga) ? `${soO.sa}/${soO.ga}` : "—",
        soPct: soO.sa > 0 ? _pct(((soO.sa - soO.ga) / soO.sa) * 100) : "—",
    };
}

const TEAM_LB_COLS_TIP = { GF: "Goals For", GA: "Goals Against", SF: "Shots For", SA: "Shots Against" };
const GOALIE_LB_COLS = [
    { label: "SV/SA", tip: "Saves / Shots Against", per: true, width: 62, fn: (r) => r.svsa },
    { label: "SV%", tip: "Save Percentage", per: true, width: 54, fn: (r) => r.svp },
    { label: "TOI", tip: "Time On Ice (game total)", width: 54, fn: (r) => r.toi },
    { label: "GA", tip: "Goals Against", per: true, fn: (r) => r.ga },
    { label: "P", tip: "Points", per: true, fn: (r) => r.p },
    { label: "G", tip: "Goals", per: true, fn: (r) => r.g },
    { label: "A", tip: "Assists", per: true, fn: (r) => r.a },
    { label: "PIM", tip: "Penalty Minutes", per: true, fn: (r) => r.pim },
    { label: "SO", tip: "Shutout", fn: (r) => r.so },
];

function PlayerSummaryPanel({ teamId, side, data, insights, goalieSplits, matchMap, photoFor, metaFor, insPeriod, statSel, admin, applyColAction }) {
    const hl = useHl();
    const starMap = useMemo(() => { const mp = {}; (data.three_stars || []).forEach((s) => { if (s.player?.pwhl_id) mp[s.player.pwhl_id] = s.star; }); return mp; }, [data.three_stars]);
    const periodColor = insPeriod === "combined" ? null : (PERIOD_META[insPeriod] || PERIOD_META.combined).color;
    const soMode = insPeriod === "so";
    let selectedIds = PS_TABS.filter((t) => statSel[t.id]).map((t) => t.id);
    if (!selectedIds.length) selectedIds = ["all"];
    const cols = soMode ? SO_SKATER_COLS : buildPsCols(selectedIds, insPeriod);
    const teamRows = (insights.rows || []).filter((r) => r.team_id === teamId);
    const teamGoalies = (goalieSplits || []).filter((gl) => gl.team_id === teamId);
    const notDressed = ((data.rosters || {})[side] || []).filter((p) => p.did_not_dress || p.status === "dnd" || p.dnd);
    const coaches = useMemo(() => (data.coaches || {})[side] || [], [data.coaches, side]);
    const { data: teamCoaches = [] } = useQuery({ queryKey: ["team-coaches", teamId], queryFn: () => api.get(`/coaches?team_id=${teamId}`).then((r) => r.data), enabled: !!teamId });
    const mergedCoaches = useMemo(() => {
        const base = [...coaches];
        const has = (re) => base.some((c) => re.test(_staffRole(c)));
        const active = (teamCoaches || []).filter((c) => c.active !== false);
        const norm = (c) => ({ role: _staffRole(c), name: c.name, coach_id: c.coach_id || c.id });
        if (!has(/head coach/i)) { const hc = active.find((c) => /head coach/i.test(_staffRole(c))); if (hc) base.push(norm(hc)); }
        if (!has(/general manager|gm/i)) { const gm = active.find((c) => /general manager|gm/i.test(_staffRole(c))); if (gm) base.push(norm(gm)); }
        return base;
    }, [coaches, teamCoaches]);

    // Team result (for the coaches' win row + Team/Goalie Result column).
    const meta = data?.meta || {};
    const isHome = teamId === meta.home_team_id;
    const teamResult = ((isHome ? meta.home_score : meta.away_score) || 0) > ((isHome ? meta.away_score : meta.home_score) || 0) ? "W" : "L";
    // FULL team stat set (teamFullStats), grouped so special-teams stats only
    // appear when Special Teams is toggled — same behaviour as the player table.
    const T = teamFullStats(teamId, data, teamRows, teamGoalies, insPeriod);
    const teamGroups = {
        main: [
            { label: "GF", per: true, fn: () => T.gfB.total }, { label: "GA", per: true, fn: () => T.gaB.total },
            { label: "Shots", per: true, width: 50, fn: () => T.sf }, { label: "SH%", per: true, width: 50, fn: () => _pctv(T.shp) },
            { label: "FO", per: true, width: 56, fn: () => `${T.fow}/${T.fow + T.fol}` }, { label: "FO%", per: true, width: 50, fn: () => _pctv(T.fo) },
            { label: "Hits", per: true, fn: () => T.hits }, { label: "Blocks", per: true, width: 50, fn: () => T.blocks }, { label: "PIM", per: true, fn: () => T.pim },
            { label: "Result", width: 56, fn: () => teamResult },
        ],
        special: [
            { label: "PP", per: true, width: 56, fn: () => `${T.ppg}/${T.ppo}` }, { label: "PP%", per: true, width: 50, fn: () => _pctv(T.ppPct) },
            { label: "PPGA", per: true, width: 50, fn: () => T.ppga },
            { label: "PK", per: true, width: 56, fn: () => `${Math.max(0, T.pk - T.ppga)}/${T.pk}` }, { label: "PK%", per: true, width: 50, fn: () => _pctv(T.pkPct) },
            { label: "PS", per: true, width: 52, fn: () => `${T.gfB.ps}/${T.psa}` }, { label: "SO", per: true, width: 52, fn: () => `${T.gfB.so}/${T.soa}` },
        ],
        insights: [
            { label: "FG", tip: "Scored First Goal", fn: () => (T.fgFor ? 1 : 0) }, { label: "LG", tip: "Scored Last Goal", fn: () => (T.lgFor ? 1 : 0) },
            { label: "ES GF", per: true, width: 52, fn: () => T.gfB.es }, { label: "ES GA", per: true, width: 52, fn: () => T.gaB.es },
            { label: "EN GF", per: true, width: 52, fn: () => T.gfB.en }, { label: "EN GA", per: true, width: 52, fn: () => T.gaB.en },
        ],
    };
    let teamCols = [...teamGroups.main, ...teamGroups.special, ...teamGroups.insights];
    if (!teamCols.length) teamCols = teamGroups.main;
    // Head Coach first, then GM (only those two, in order).
    const staffRows = [
        ...mergedCoaches.filter((c) => /head coach/i.test(_staffRole(c))).map((c) => ({ ...c, roleLabel: "HC" })),
        ...mergedCoaches.filter((c) => /general manager|gm/i.test(_staffRole(c))).map((c) => ({ ...c, roleLabel: "GM" })),
    ];

    return (
        <Card testId={`pgc-playersum-${teamId}`} style={hl === "team" ? { background: `linear-gradient(160deg, ${teamColor(teamId) || "#9D4CDD"}1F 0%, #0A0B10 55%)`, borderColor: `${teamColor(teamId) || "#9D4CDD"}44` } : undefined}>
            <div className="flex items-center gap-2 mb-4"><TeamBadge teamId={teamId} size="sm" /><span className="font-bold text-white text-sm">{teamName(teamId)}</span></div>

            {/* 1 — TEAM (single row, full stat set; special stats gated by toggle) */}
            <CollapsibleSection id={`ps-team-${teamId}`} title="Team" accent="#38BDF8">
                <LbStatTable testId={`pgc-ps-team-${teamId}`} idWidth={150} periodColor={periodColor} cols={teamCols} rows={[{}]}
                    identityHead="Team" renderIdentity={() => <span className="inline-flex items-center gap-1.5"><TeamBadge teamId={teamId} size="sm" /><span className="text-white font-semibold">{teamAbbr(teamId)}</span></span>} />
            </CollapsibleSection>

            {/* 2 — GOALIE(S) */}
            {teamGoalies.length > 0 && (
                <CollapsibleSection id={`ps-goalie-${teamId}`} title="Goalie" accent="#5BD1B7">
                    <LbStatTable testId={`pgc-ps-goalie-${teamId}`} idWidth={200} periodColor={periodColor}
                        cols={[...GOALIE_LB_COLS,
                            { label: "Result", tip: "Decision (W / L / OTW / OTL / SOW / SOL)", width: 66, fn: (r) => r.dec },
                            { label: "SOA/SOGA", tip: "Shootout Shots Against / Goals Against", width: 82, fn: (r) => r.soaSoga },
                            { label: "SO%", tip: "Shootout Save % (saves ÷ shootout shots against)", width: 56, fn: (r) => r.soPct }]}
                        rows={teamGoalies.map((gl) => ({ ...goalieRowStats(gl, insPeriod), _gl: gl }))}
                        identityHead="Goalie" renderIdentity={(r) => (
                            <span className="inline-flex items-center gap-1.5 min-w-0">
                                <PlayerAvatar photo={photoFor ? photoFor(r._gl.player?.pwhl_id) : ""} name={r._gl.player?.name} playerId={matchMap[r._gl.player?.pwhl_id]} size="xs" showTierRing={false} />
                                <span className="text-white font-semibold truncate">{abbrevName(r._gl.player?.name)}</span>
                            </span>
                        )} />
                </CollapsibleSection>
            )}

            {/* 3 — PLAYERS (all skaters) */}
            <CollapsibleSection id={`ps-players-${teamId}`} title="Players" accent="#9D4CDD">
                <InsightsTable rows={teamRows} matchMap={matchMap} cols={cols} photoFor={photoFor} metaFor={metaFor}
                    periodColor={periodColor} admin={admin && !soMode} groupId={selectedIds[0] || "playersum"} onColAction={applyColAction}
                    testId={`pgc-playersum-table-${teamId}`} soMode={soMode} starMap={starMap} />
            </CollapsibleSection>

            {/* 4 — HC & GM (win result only, for now) */}
            {staffRows.length > 0 && (
                <CollapsibleSection id={`ps-staff-${teamId}`} title="HC & GM" accent="#F6C453">
                    <LbStatTable testId={`pgc-ps-staff-${teamId}`} idWidth={240}
                        cols={[{ label: "Result", tip: "Game Result", width: 70, fn: () => teamResult }]}
                        rows={staffRows}
                        identityHead="Staff" renderIdentity={(r) => (
                            <span className="inline-flex items-center gap-1.5 min-w-0">
                                <span className="text-[9px] font-black px-1 py-0.5 rounded bg-white/10 text-[#94A3B8] border border-white/15">{r.roleLabel}</span>
                                <span className="text-white font-semibold truncate">{abbrevName(r.name)}</span>
                            </span>
                        )} />
                </CollapsibleSection>
            )}

            {notDressed.length > 0 && (
                <div className="mt-5 pt-3 border-t border-[#1E2233]" data-testid={`pgc-playersum-dnd-${teamId}`}>
                    <div className="text-[11px] uppercase tracking-wider font-bold text-[#64748B] mb-2">Not Dressed ({notDressed.length})</div>
                    <div className="flex flex-wrap gap-1.5">
                        {notDressed.map((p, i) => (
                            <span key={i} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/[0.03] border border-white/10 text-[11px] text-[#94A3B8]" data-testid={`pgc-dnd-${teamId}-${i}`}>
                                {p.jersey != null && <span className="font-mono-stat text-[#475569]">{p.jersey}</span>}
                                {p.player?.name || p.name || "—"}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </Card>
    );
}

// ===================== Game Lineup tab =====================
// PUBLIC: identical to the team page's Projected Lineup (LineupCard).
// ADMIN:  identical to the Rosters admin builder (LineupBuilder) + populate tools.
function GameLineupTab({ side, data, teamId, admin = false, gameId, lineup, isPublic = false, playerById = {}, players = [], matchMap = {}, onChanged }) {
    const coaches = useMemo(() => (data.coaches || {})[side] || [], [data.coaches, side]);
    const primary = teamColor(teamId) || "#9D4CDD";
    const { data: allTeams = [] } = useQuery({ queryKey: ["teams"], queryFn: getTeams, staleTime: 5 * 60 * 1000 });
    const team = useMemo(() => (allTeams || []).find((t) => t.id === teamId) || { id: teamId, name: teamName(teamId), primary_color: primary }, [allTeams, teamId, primary]);
    // Auto-include Head Coach + GM from the team's staff when the game import
    // didn't carry them (game summaries show both, so lineups must too).
    const { data: teamCoaches = [] } = useQuery({ queryKey: ["team-coaches", teamId], queryFn: () => api.get(`/coaches?team_id=${teamId}`).then((r) => r.data), enabled: !!teamId });
    const mergedCoaches = useMemo(() => {
        const base = [...coaches];
        const has = (re) => base.some((c) => re.test(_staffRole(c)));
        const active = (teamCoaches || []).filter((c) => c.active !== false);
        const norm = (c) => ({ role: _staffRole(c), name: c.name, coach_id: c.coach_id || c.id });
        if (!has(/head coach/i)) { const hc = active.find((c) => /head coach/i.test(_staffRole(c))); if (hc) base.push(norm(hc)); }
        if (!has(/general manager|gm/i)) { const gm = active.find((c) => /general manager|gm/i.test(_staffRole(c))); if (gm) base.push(norm(gm)); }
        return base;
    }, [coaches, teamCoaches]);

    if (!admin) {
        // RULE: a game lineup is public the moment ANY player is placed into the
        // lineup (outside the Available Players box). No stored public/private
        // flag — placing a player IS the publish action.
        const placedCount = (lu) => {
            if (!lu || typeof lu !== "object") return 0;
            let n = 0;
            const add = (s) => { if (s && (s.player_id || s.pwhl_id)) n += 1; };
            (lu.forward_lines || []).forEach((l) => (l || []).forEach(add));
            (lu.d_pairs || []).forEach((l) => (l || []).forEach(add));
            (lu.goalies || []).forEach(add);
            (lu.reserves || []).forEach(add);
            return n;
        };
        const hasLineup = placedCount(lineup) > 0;
        if (!hasLineup) {
            return (
                <div className="space-y-6" data-testid={`pgc-lineup-${side}`}>
                    <div className="py-12 text-center rounded-2xl border border-white/10 bg-[#0E0F16]" data-testid={`pgc-lineup-none-${side}`}>
                        <ClipboardList className="mx-auto mb-2 text-white/25" size={26} />
                        <div className="text-sm font-bold text-white/70">No lineup available for this game</div>
                        <div className="text-xs text-[#94A3B8] mt-1">A lineup hasn't been added for this matchup.</div>
                    </div>
                    <CoachingStaff side={side} coaches={mergedCoaches} />
                </div>
            );
        }
        return (
            <div className="space-y-6" data-testid={`pgc-lineup-${side}`}>
                <div className="flex justify-end">
                    <Link to={pathForTab("roster", teamId)} data-testid={`pgc-lineup-tab-link-${side}`} className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#C99BF0] hover:text-white transition-colors">View in Lineups <ArrowRight size={13} /></Link>
                </div>
                <NewLineupBoard key={`${teamId}-${gameId}`} lineup={lineup} team={team} teamId={teamId} playerMap={playerById} admin={false} gameCentre initialTarget={gameId} lockTarget />
                <CoachingStaff side={side} coaches={mergedCoaches} />
            </div>
        );
    }
    // Game Centre lineup has the SAME drag-and-drop line editor as the team
    // Lineups tab (admins can reorder / place every player), but NOT the design
    // "editing" box — the layout/badge design controls live on the Lineups tab
    // only. Jersey #s + positions come from THIS game's roster (per-game).
    return (
        <div className="space-y-6" data-testid={`pgc-lineup-${side}`}>
            <div className="flex justify-end">
                <Link to={pathForTab("roster", teamId)} data-testid={`pgc-lineup-tab-link-${side}`} className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#C99BF0] hover:text-white transition-colors">View in Lineups <ArrowRight size={13} /></Link>
            </div>
            <NewLineupBoard key={`${teamId}-${gameId}`} lineup={lineup} team={team} teamId={teamId} playerMap={playerById} admin gameCentre initialTarget={gameId} lockTarget />
            <CoachingStaff side={side} coaches={mergedCoaches} />
        </div>
    );
}

// ===================== Scoreboard =====================
function Scoreboard({ m, homeWon, records }) {
    const status = m.final ? "FINAL" : (m.status && m.status.toLowerCase() !== "scheduled" ? "LIVE" : "SCHEDULED");
    const showScore = status !== "SCHEDULED";
    const venueLines = (m.venue || "").split("|").map((s) => s.trim());
    const aId = m.away_team_id, hId = m.home_team_id;

    const Team = ({ id, code, score, dim, align }) => (
        <div className={`flex items-center gap-4 min-w-0 ${align === "right" ? "flex-row-reverse text-right" : ""}`}>
            <Link to={id ? `/teams/${id}` : "#"} className="shrink-0"><TeamBadge teamId={id} size="lg" asLink={false} /></Link>
            <div className="min-w-0">
                <div className="text-[11px] md:text-sm uppercase tracking-[0.2em] text-[#94A3B8] font-semibold leading-tight">{cityOf(id, code)}</div>
                <div className="font-heading text-2xl md:text-4xl font-black uppercase text-white leading-none">{nickOf(id, code)}</div>
                {records[id] && <div className="font-mono-stat text-xs text-[#64748B] mt-1.5" data-testid={`pgc-record-${align}`}>{records[id]}</div>}
            </div>
            {showScore && (
                <div className={`font-mono-stat text-5xl md:text-7xl font-black leading-none ${dim ? "text-white/30" : "text-white"}`} data-testid={`pgc-score-${align}`}>{score ?? 0}</div>
            )}
        </div>
    );

    return (
        <div className="rounded-2xl bg-[#0E0F16] border border-white/10 shadow-xl shadow-black/40 relative overflow-hidden p-5 md:p-7" data-testid="pgc-scoreboard">
            {/* Desktop layout */}
            <div className="relative hidden md:grid grid-cols-[1fr_auto_1fr] items-center gap-5">
                <div className="md:justify-self-start"><Team id={aId} code={m.away_team_code} score={m.away_score} dim={homeWon} align="away" /></div>
                <div className="flex flex-col items-center justify-center px-2 shrink-0 gap-1.5">
                    <span className="px-3 py-1 rounded-md bg-white/10 text-white text-[10px] uppercase tracking-[0.25em] font-bold" data-testid="pgc-status">{status}</span>
                    <span className="text-[#94A3B8] text-lg font-mono-stat">@</span>
                    {venueLines[0] && <div className="text-center text-[11px] text-[#94A3B8] leading-tight">{venueLines.map((l, i) => <div key={i}>{l}</div>)}</div>}
                    {(m.overtime || m.shootout) && <span className="text-[10px] uppercase tracking-widest text-[#FDE68A] font-bold">{m.shootout ? "SO" : "OT"}</span>}
                </div>
                <div className="md:justify-self-end"><Team id={hId} code={m.home_team_code} score={m.home_score} dim={!homeWon} align="home" /></div>
            </div>
            {/* Mobile scoreboard — Rosterly style (matches desktop, no puck) */}
            <div className="relative md:hidden" data-testid="pgc-scoreboard-mobile">
                <div className="flex items-center justify-center mb-4">
                    <span className="px-3 py-1 rounded-full bg-white/10 text-white text-[10px] uppercase tracking-[0.25em] font-bold" data-testid="pgc-status-mobile">{status}{(m.overtime || m.shootout) ? ` · ${m.shootout ? "SO" : "OT"}` : ""}</span>
                </div>
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                    <Link to={aId ? `/teams/${aId}` : "#"} className="flex flex-col items-center gap-2 min-w-0">
                        <TeamBadge teamId={aId} size="lg" asLink={false} />
                        <span className="font-heading text-base font-black uppercase text-white truncate max-w-full leading-none">{nickOf(aId, m.away_team_code)}</span>
                        {records[aId] && <span className="font-mono-stat text-[10px] text-[#64748B]">{records[aId]}</span>}
                    </Link>
                    <div className="flex flex-col items-center shrink-0 px-1">
                        {showScore ? (
                            <div className="flex items-baseline gap-2 font-mono-stat font-black leading-none tabular-nums">
                                <span className={`text-5xl ${homeWon ? "text-white/30" : "text-white"}`} data-testid="pgc-score-away">{m.away_score ?? 0}</span>
                                <span className="text-white/25 text-2xl">–</span>
                                <span className={`text-5xl ${!homeWon ? "text-white/30" : "text-white"}`} data-testid="pgc-score-home">{m.home_score ?? 0}</span>
                            </div>
                        ) : (
                            <span className="text-[#94A3B8] text-3xl font-mono-stat">@</span>
                        )}
                    </div>
                    <Link to={hId ? `/teams/${hId}` : "#"} className="flex flex-col items-center gap-2 min-w-0">
                        <TeamBadge teamId={hId} size="lg" asLink={false} />
                        <span className="font-heading text-base font-black uppercase text-white truncate max-w-full leading-none">{nickOf(hId, m.home_team_code)}</span>
                        {records[hId] && <span className="font-mono-stat text-[10px] text-[#64748B]">{records[hId]}</span>}
                    </Link>
                </div>
                {venueLines[0] && <div className="mt-4 text-center text-[10px] text-[#64748B] leading-tight">{venueLines.join(" · ")}</div>}
            </div>
        </div>
    );
}

// Game navigation strip (logo-strip style) — sits BELOW the scoreboard.
// Per-team Roster | Lineup highlight in that team's colour; Summary in purple.
function GameNav({ m, gameView, setGameView, data, presentPeriods, insPeriod, setInsPeriod, gsMode, setGsMode, gsSingle, setGsSingle, gsShow, gsToggle, admin, flags, toggleForce }) {
    const aId = m.away_team_id, hId = m.home_team_id;
    const aCol = teamColor(aId) || "#9D4CDD", hCol = teamColor(hId) || "#9D4CDD";
    const Seg = ({ view, label, color, badgeId }) => {
        const active = gameView === view;
        return (
            <button type="button" onClick={() => setGameView(view)} data-testid={`pgc-view-${view}`}
                className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-[10px] sm:text-[11px] font-black uppercase tracking-wider whitespace-nowrap border transition-colors shrink-0 ${active ? "text-white border-transparent shadow-md" : "text-[#94A3B8] border-white/10 bg-white/[0.03] hover:text-white hover:border-white/25"}`}
                style={active ? { backgroundColor: color } : undefined}>
                {badgeId && <TeamBadge teamId={badgeId} size="sm" asLink={false} />}
                {label}
            </button>
        );
    };
    const onSummary = gameView === "summary";
    return (
        <div className="space-y-2" data-testid="pgc-game-nav-wrap">
            <div className="rounded-2xl bg-[#0E0F16] border border-white/10 shadow-xl shadow-black/40 p-2 flex items-center justify-center flex-nowrap gap-1 sm:gap-1.5 overflow-x-auto" data-testid="pgc-game-nav">
                <Seg view="away_roster" label="Team & Roster Summary" color={aCol} badgeId={aId} />
                <Seg view="away_lineup" label="Lineup" color={aCol} badgeId={aId} />
                <Seg view="summary" label="Game Recap" color="#9D4CDD" />
                <Seg view="home_roster" label="Team & Roster Summary" color={hCol} badgeId={hId} />
                <Seg view="home_lineup" label="Lineup" color={hCol} badgeId={hId} />
            </div>
            {onSummary && (
                <SummaryControls data={data} m={m} presentPeriods={presentPeriods}
                    insPeriod={insPeriod} setInsPeriod={setInsPeriod}
                    gsMode={gsMode} setGsMode={setGsMode} gsSingle={gsSingle} setGsSingle={setGsSingle}
                    gsShow={gsShow} gsToggle={gsToggle}
                    admin={admin} flags={flags} toggleForce={toggleForce} />
            )}
        </div>
    );
}

// Unified Game-Summary control bar (shown ONLY on the Game Summary view). The
// PERIOD toggle drives BOTH the Game Summary event list and the Stats & Insights
// tables; the event-type + mode controls apply only to the event list.
function SummaryControls({ data, m, presentPeriods, insPeriod, setInsPeriod, gsMode, setGsMode, gsSingle, setGsSingle, gsShow, gsToggle, admin, flags, toggleForce }) {
    const forced = [...(flags?.force_ot ? [4] : []), ...(flags?.force_so ? [5] : [])];
    // Base OT/SO detection on the SAME signal as the OT/SO toggle visibility
    // (periods actually present + admin force flags), so "show Regulation" stays
    // consistent with whether an OT/SO toggle exists. The game-level overtime
    // meta flag is unreliable here and caused regulation games to show both.
    const hasSo = presentPeriods.has(5) || forced.includes(5) || (data.shootout || []).length > 0 || !!m.shootout;
    const hasOt = presentPeriods.has(4) || forced.includes(4);
    const hasOtOrSo = hasOt || hasSo;
    // When the game never went to OT/SO, "Combined" === "Regulation": drop the
    // Regulation toggle and rename Combined → Regulation. Otherwise show both.
    let periods = PERIOD_TOGGLES.filter((p) => p.k === "combined" || (p.k === "regulation" && hasOtOrSo) || presentPeriods.has(p.p) || forced.includes(p.p) || (p.p === 5 && hasSo));
    if (!hasOtOrSo) periods = periods.map((p) => (p.k === "combined" ? { ...p, label: "Regulation" } : p));
    const availTypes = new Set((data.pxp || []).map((e) => e.type).concat((data.goals || []).length ? ["goal"] : []).concat((data.penalties || []).length ? ["penalty"] : []));
    return (
        <div className="w-full rounded-2xl bg-[#0E0F16] border border-white/10 shadow-lg shadow-black/30 p-2.5 flex flex-col items-center gap-2" data-testid="pgc-summary-controls">
            {/* Period toggles — drive Game Summary + Stats & Insights */}
            <div className="flex flex-wrap items-center justify-center gap-1.5" data-testid="pgc-summary-periods">
                <span className="text-[9px] uppercase tracking-[0.14em] text-[#475569] font-bold mr-0.5">Period</span>
                <div className="inline-flex items-center rounded-lg border border-[#1E2233] bg-[#0A0B12] p-0.5 gap-0.5">
                    {periods.map(({ k, label }) => {
                        const c = PERIOD_META[k].color;
                        const on = insPeriod === k;
                        return (
                            <button key={k} type="button" onClick={() => setInsPeriod(k)} data-testid={`pgc-insperiod-${k}`}
                                style={on ? { backgroundColor: c, color: "#0A0B12" } : { color: c }}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider transition-all ${on ? "shadow-sm" : "opacity-70 hover:opacity-100"}`}>
                                {!on && <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: c }} />}{label}
                            </button>
                        );
                    })}
                </div>
                {admin && (
                    <div className="inline-flex items-center gap-1.5 ml-1" data-testid="pgc-force-toggles">
                        <span className="text-[9px] uppercase tracking-wider text-[#475569] font-bold">Force</span>
                        {[["force_ot", "OT"], ["force_so", "SO"]].map(([fk, lbl]) => (
                            <button key={fk} type="button" onClick={() => toggleForce(fk)} data-testid={`pgc-force-${fk}`}
                                className={`px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider transition-colors ${flags?.[fk] ? "border-[#9D4CDD] bg-[#9D4CDD]/20 text-white" : "border-[#1E2233] text-[#64748B] hover:text-white"}`}>{lbl}</button>
                        ))}
                    </div>
                )}
            </div>
            {/* Event-type + mode — apply to the Game Summary event list only */}
            <div className="flex flex-wrap items-center justify-center gap-1.5" data-testid="pgc-summary-events">
                {gsMode !== "all" && GS_TOGGLE_DEFS.filter((t) => availTypes.has(t.key)).map((t) => {
                    const on = gsMode === "single" ? gsSingle === t.key : gsShow[t.key];
                    return (
                        <button key={t.key} type="button" data-testid={`pgc-gs-filter-${t.key}`}
                            onClick={() => (gsMode === "single" ? setGsSingle(t.key) : gsToggle(t.key))}
                            className={`px-2.5 py-1 rounded-md border text-[11px] font-bold uppercase tracking-wider transition-colors ${on ? "border-[#9D4CDD] bg-[#9D4CDD]/15 text-white" : "border-[#1E2233] text-[#64748B] hover:text-white"}`}>{t.label}</button>
                    );
                })}
                <span className="inline-flex rounded-md border border-[#1E2233] overflow-hidden ml-1" data-testid="pgc-gs-mode">
                    {[["single", "Single"], ["select", "Select"], ["all", "All"]].map(([mo, lbl]) => (
                        <button key={mo} type="button" onClick={() => setGsMode(mo)} data-testid={`pgc-gs-mode-${mo}`}
                            className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${gsMode === mo ? "bg-[#5BD1B7] text-[#062b24]" : "text-[#64748B] hover:text-white"}`}>{lbl}</button>
                    ))}
                </span>
            </div>
        </div>
    );
}

function Info({ icon: Icon, label, value, testId }) {
    if (!value) return null;
    return (
        <div className="flex items-center gap-2.5" data-testid={testId}>
            <Icon size={16} className="text-[#9D4CDD] shrink-0" />
            <div>
                <div className="text-[9px] uppercase tracking-wider text-[#64748B] font-bold">{label}</div>
                <div className="text-white font-semibold text-sm">{value}</div>
            </div>
        </div>
    );
}

function Detail({ label, value }) {
    return (
        <div className="flex items-center justify-between gap-3">
            <dt className="text-[10px] uppercase tracking-wider text-[#64748B] font-bold">{label}</dt>
            <dd className="text-white font-semibold text-right">{value}</dd>
        </div>
    );
}

// Horizontal Game Details strip (above the roster｜lineup｜summary nav).
function GameDetailsStrip({ m, data, gameId, admin }) {
    const items = [["Status", m.final ? "Final" : (m.status && m.status.toLowerCase() !== "scheduled" ? "Live" : "Scheduled")]];
    if (m.date) items.push(["Date", formatDate(m.date, { fallback: m.date })]);
    if (m.venue) items.push(["Arena", m.venue.split("|")[0].trim()]);
    if (m.attendance > 0) items.push(["Attendance", Number(m.attendance).toLocaleString()]);
    if (m.start_time) items.push(["Start", m.start_time.slice(0, 5)]);
    if (m.end_time) items.push(["End", m.end_time.slice(0, 5)]);
    if (m.duration) items.push(["Duration", m.duration]);
    return (
        <div className="rounded-xl border border-white/10 bg-[#0E0F16] px-4 py-2.5 mb-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-1.5" data-testid="pgc-details-strip">
            {items.map(([l, v]) => (
                <div key={l} className="flex items-center gap-1.5">
                    <span className="text-[9px] uppercase tracking-wider text-[#64748B] font-bold">{l}</span>
                    <span className="text-sm text-white font-semibold">{v}</span>
                </div>
            ))}
            {admin && (
                <div className="flex items-center justify-center gap-2 w-full mt-1 pt-1.5 border-t border-white/[0.06] text-[10px] text-[#9D4CDD]">
                    <span className="inline-flex items-center gap-1"><Hash size={11} /><span className="font-mono-stat">{gameId}</span></span>
                    <span className="text-[#475569]">PWHL {data.pwhl_game_id || "—"}</span>
                    <span className="inline-flex items-center gap-1 text-[#22C55E]"><span className="w-1.5 h-1.5 rounded-full bg-[#22C55E]" />{data.sync_status || "synced"}</span>
                </div>
            )}
        </div>
    );
}

const _staffRole = (c) => c.role || c.custom_role || "";
function StaffRow({ label, c }) {
    return (
        <div className="flex items-center justify-between gap-3 py-1.5 border-b border-[#1E2233] last:border-0">
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">{label}</span>
            <span className="text-sm text-white text-right">{c ? c.name : <span className="text-[#475569]">—</span>}</span>
        </div>
    );
}
function StaffTeam({ teamId }) {
    const { data: coaches = [] } = useQuery({
        queryKey: ["team-coaches", teamId],
        queryFn: () => api.get(`/coaches?team_id=${teamId}`).then((r) => r.data),
        enabled: !!teamId,
    });
    const active = coaches.filter((c) => c.active !== false);
    const gm = active.find((c) => /general manager|gm/i.test(_staffRole(c)));
    const hc = active.find((c) => /head coach/i.test(_staffRole(c)));
    return (
        <div data-testid={`pgc-staff-team-${teamId}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5 text-[#94A3B8]"><TeamBadge teamId={teamId} size="xs" asLink={false} /> {teamAbbr(teamId) || teamId?.toUpperCase()}</div>
            <StaffRow label="General Manager" c={gm} />
            <StaffRow label="Head Coach" c={hc} />
        </div>
    );
}
function GameStaffBoth({ homeId, awayId, officials = [] }) {
    const refs = (officials || []).filter((o) => /referee/i.test(o.role || ""));
    const lines = (officials || []).filter((o) => /lines/i.test(o.role || ""));
    const other = (officials || []).filter((o) => !/referee|lines/i.test(o.role || ""));
    const InnerCard = ({ title, icon: Ic, children }) => (
        <div className="rounded-xl border border-[#1E2233] bg-[#0B0C13] p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider mb-2.5 flex items-center gap-1.5 text-[#94A3B8]"><Ic size={11} className="text-[#9D4CDD]" /> {title}</div>
            {children}
        </div>
    );
    return (
        <div className="grid md:grid-cols-3 gap-3 items-start" data-testid="pgc-staff-grid">
            <InnerCard title="Coaches" icon={Users}>
                <div className="space-y-3">
                    <StaffTeam teamId={awayId} />
                    <StaffTeam teamId={homeId} />
                </div>
            </InnerCard>
            <InnerCard title="Commentators" icon={Mic}>
                <StaffRow label="Commentators" c={null} />
            </InnerCard>
            <InnerCard title="Referees" icon={Flag}>
                {(officials || []).length ? (
                    <div className="space-y-3" data-testid="pgc-staff-officials">
                        <OfficialGroup label="Referees" list={refs} k="referees" />
                        <OfficialGroup label="Linespersons" list={lines} k="linespersons" />
                        {other.length > 0 && <OfficialGroup label="Officials" list={other} k="other" />}
                    </div>
                ) : <div className="text-xs text-[#475569] italic">No officials listed.</div>}
            </InnerCard>
        </div>
    );
}
function GameStaff({ teamId, side }) {
    return (
        <Card title="Coaching Staff" icon={Users} testId={`pgc-staff-${side}`}>
            <StaffTeam teamId={teamId} />
        </Card>
    );
}

// ===================== Period Summary (3 comparison boxes, per-period aware) =====================
function PeriodSummary({ m, data, homeId, awayId, shotsByPeriod }) {
    const ts = data.team_stats || {};
    const hS = ts.home || {}, aS = ts.away || {};
    const hCol = teamColor(homeId) || "#9D4CDD";
    const aCol = teamColor(awayId) || "#5BD1B7";
    const hasOT = !!m.overtime || (data.periods || []).some((p) => p.period >= 4);

    const derived = useMemo(() => {
        const blocks = { home: 0, away: 0 }, shg = { home: 0, away: 0 };
        (data.skaters || []).forEach((s) => {
            const side = s.team_id === homeId ? "home" : s.team_id === awayId ? "away" : null;
            if (!side) return;
            blocks[side] += s.blocks || 0;
            shg[side] += s.shg || 0;
        });
        return { blocks, shg };
    }, [data.skaters, homeId, awayId]);

    const goals = { home: m.home_score ?? 0, away: m.away_score ?? 0 };
    const shots = { home: hS.shots ?? 0, away: aS.shots ?? 0 };
    const foPct = (S) => (S.fo_att ? Math.round((S.fo_won / S.fo_att) * 100) : 0);
    const spg = (s, g) => (g ? (s / g).toFixed(1) : "—");
    const shPct = (g, s) => (s ? `${((g / s) * 100).toFixed(1)}%` : "—");
    const cols = [1, 2, 3, ...(hasOT ? [4] : [])];
    const perLabel = (c) => (c <= 3 ? `P${c}` : "OT");
    const perGoals = (c) => { const r = (data.periods || []).find((p) => p.period === c); return { away: r?.away ?? 0, home: r?.home ?? 0 }; };
    const perShots = (c) => ({ away: (shotsByPeriod[c] || {}).away ?? 0, home: (shotsByPeriod[c] || {}).home ?? 0 });

    const [stage, setStage] = useState("all");
    const isAll = stage === "all";
    const stages = ["all", 1, 2, 3, ...(hasOT ? [4] : []), ...(m.shootout ? [5] : [])];
    const stageLabel = (s) => (s === "all" ? "Combined" : s <= 3 ? `P${s}` : s === 4 ? "OT" : "SO");
    const periodAgg = useMemo(() => {
        const mk = () => ({ home: 0, away: 0 });
        const pim = {}, fow = {};
        (data.penalties || []).forEach((p) => { const per = p.period || 1; pim[per] = pim[per] || mk(); const min = p.minutes || 2; if (p.team_id === homeId) pim[per].home += min; else if (p.team_id === awayId) pim[per].away += min; });
        (data.pxp || []).filter((e) => e.type === "faceoff").forEach((e) => { const per = e.period || 1; fow[per] = fow[per] || mk(); if (e.team_id === homeId) fow[per].home += 1; else if (e.team_id === awayId) fow[per].away += 1; });
        return { pim, fow };
    }, [data.penalties, data.pxp, homeId, awayId]);
    const stageGoals = isAll ? goals : perGoals(stage);
    const stageShots = isAll ? shots : perShots(stage);
    const stagePim = isAll ? { away: aS.pim ?? 0, home: hS.pim ?? 0 } : (periodAgg.pim[stage] || { away: 0, home: 0 });
    const stageFow = isAll ? { away: aS.fo_won ?? 0, home: hS.fo_won ?? 0 } : (periodAgg.fow[stage] || { away: 0, home: 0 });

    const Box = ({ title, icon: Ic, color, children, testId }) => (
        <div className="rounded-2xl border border-white/10 bg-[#0B0C13] overflow-hidden flex flex-col" data-testid={testId}>
            <div className="h-1" style={{ background: color }} />
            <div className="p-4 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-3">
                    <Ic size={14} style={{ color }} />
                    <span className="text-[11px] uppercase tracking-[0.16em] font-black text-white">{title}</span>
                </div>
                {children}
            </div>
        </div>
    );

    const Bar = ({ label, away, home, fmtAway, fmtHome }) => {
        const a = Number(away) || 0, h = Number(home) || 0, tot = a + h || 1;
        return (
            <div className="mb-3.5 last:mb-0" data-testid={`pgc-ps-bar-${label.toLowerCase().replace(/[^a-z]/g, "")}`}>
                <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="font-mono-stat font-bold text-white tabular-nums">{fmtAway ?? a}</span>
                    <span className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">{label}</span>
                    <span className="font-mono-stat font-bold text-white tabular-nums">{fmtHome ?? h}</span>
                </div>
                <div className="flex h-2 rounded-full overflow-hidden bg-white/[0.04]">
                    <div style={{ width: `${(a / tot) * 100}%`, background: aCol }} />
                    <div style={{ width: `${(h / tot) * 100}%`, background: hCol }} className="ml-auto" />
                </div>
            </div>
        );
    };

    const TeamGoal = ({ id, val, align }) => (
        <div className={`flex items-center gap-2.5 ${align === "right" ? "flex-row-reverse" : ""}`}>
            <TeamBadge teamId={id} size="sm" />
            <div className="font-mono-stat text-4xl font-black text-white leading-none tabular-nums">{val}</div>
        </div>
    );

    const ST = ({ id, ppg, ppo, pkK, pkO, color }) => {
        const ppPct = ppo ? Math.round((ppg / ppo) * 100) : null;
        const pkPct = pkO ? Math.round((pkK / pkO) * 100) : null;
        const Row = ({ label, made, att, pct }) => (
            <div className="flex items-center justify-between py-1">
                <span className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">{label}</span>
                <span className="flex items-baseline gap-2">
                    <span className="font-mono-stat text-sm font-black text-white tabular-nums">{made ?? 0}<span className="text-[#64748B]">/{att ?? 0}</span></span>
                    <span className="font-mono-stat text-[11px] font-bold text-white bg-white/10 rounded px-1.5 py-0.5 min-w-[40px] text-center">{pct == null ? "—" : `${pct}%`}</span>
                </span>
            </div>
        );
        return (
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3" data-testid={`pgc-ps-st-${id}`}>
                <div className="flex items-center gap-1.5 mb-2 pb-2 border-b border-white/[0.06]">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
                    <TeamBadge teamId={id} size="sm" /><span className="text-xs font-bold text-white">{teamAbbr(id)}</span>
                </div>
                <Row label="Power Play" made={ppg} att={ppo} pct={ppPct} />
                <Row label="Penalty Kill" made={pkK} att={pkO} pct={pkPct} />
            </div>
        );
    };

    return (
        <div data-testid="pgc-period-summary">
            <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="pgc-ps-stages">
                <span className="text-[9px] uppercase tracking-wider text-[#475569] font-bold mr-1">View</span>
                {stages.map((s) => (
                    <button key={s} type="button" onClick={() => setStage(s)} data-testid={`pgc-ps-stage-${s}`}
                        className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-colors ${stage === s ? "bg-[#38BDF8] text-[#062733]" : "bg-white/5 text-[#64748B] hover:text-white"}`}>{stageLabel(s)}</button>
                ))}
            </div>
            <div className="grid md:grid-cols-3 gap-4 items-stretch">
            {/* Box 1 — Goals & Shots */}
            <Box title="Goals & Shots" icon={Goal} color="#5BD1B7" testId="pgc-ps-box-goals">
                <div className="flex items-center justify-between mb-4">
                    <TeamGoal id={awayId} val={stageGoals.away} />
                    <span className="text-[10px] uppercase text-[#475569] font-bold">Goals</span>
                    <TeamGoal id={homeId} val={stageGoals.home} align="right" />
                </div>
                <Bar label="Shots" away={stageShots.away} home={stageShots.home} />
                {isAll && (
                <div className="rounded-xl bg-white/[0.03] border border-white/10 overflow-hidden mb-3">
                    <div className="grid grid-cols-4 text-[9px] uppercase tracking-wider text-[#64748B] font-bold">
                        <div className="py-1.5 pl-2.5">Period</div>
                        <div className="py-1.5 text-center">{teamAbbr(awayId)}</div>
                        <div className="py-1.5 text-center">{teamAbbr(homeId)}</div>
                        <div className="py-1.5 text-center">Shots</div>
                    </div>
                    {cols.map((c) => { const g = perGoals(c); const s = perShots(c); return (
                        <div key={c} className="grid grid-cols-4 text-xs border-t border-white/[0.06] items-center" data-testid={`pgc-ps-period-${c}`}>
                            <div className="py-1.5 pl-2.5 font-bold text-[#94A3B8]">{perLabel(c)}</div>
                            <div className="py-1.5 text-center font-mono-stat text-white tabular-nums">{g.away}</div>
                            <div className="py-1.5 text-center font-mono-stat text-white tabular-nums">{g.home}</div>
                            <div className="py-1.5 text-center font-mono-stat text-[#64748B] tabular-nums text-[11px]">{s.away}-{s.home}</div>
                        </div>
                    ); })}
                </div>
                )}
                <div className="mt-auto grid grid-cols-2 gap-2">
                    <div className="rounded-lg bg-white/[0.03] border border-white/10 p-2 text-center"><div className="font-mono-stat text-sm font-black" style={{ color: aCol }}>{shPct(stageGoals.away, stageShots.away)}</div><div className="text-[9px] text-[#64748B] font-bold uppercase">{teamAbbr(awayId)} SH% · {spg(stageShots.away, stageGoals.away)}/G</div></div>
                    <div className="rounded-lg bg-white/[0.03] border border-white/10 p-2 text-center"><div className="font-mono-stat text-sm font-black" style={{ color: hCol }}>{shPct(stageGoals.home, stageShots.home)}</div><div className="text-[9px] text-[#64748B] font-bold uppercase">{teamAbbr(homeId)} SH% · {spg(stageShots.home, stageGoals.home)}/G</div></div>
                </div>
            </Box>

            {/* Box 2 — Physical & Faceoffs */}
            <Box title="Physical & Faceoffs" icon={Activity} color="#F59E0B" testId="pgc-ps-box-physical">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-[#64748B] font-bold mb-3">
                    <span className="flex items-center gap-1.5"><TeamBadge teamId={awayId} size="sm" />{teamAbbr(awayId)}</span>
                    <span className="flex items-center gap-1.5">{teamAbbr(homeId)}<TeamBadge teamId={homeId} size="sm" /></span>
                </div>
                <Bar label="Hits" away={isAll ? (aS.hits || 0) : 0} home={isAll ? (hS.hits || 0) : 0} fmtAway={isAll ? undefined : "—"} fmtHome={isAll ? undefined : "—"} />
                <Bar label="Blocks" away={isAll ? derived.blocks.away : 0} home={isAll ? derived.blocks.home : 0} fmtAway={isAll ? undefined : "—"} fmtHome={isAll ? undefined : "—"} />
                <Bar label="PIM" away={stagePim.away} home={stagePim.home} />
                <Bar label={isAll ? "Faceoff %" : "Faceoffs Won"} away={stageFow.away} home={stageFow.home} fmtAway={isAll ? (aS.fo_att ? `${foPct(aS)}%` : "—") : undefined} fmtHome={isAll ? (hS.fo_att ? `${foPct(hS)}%` : "—") : undefined} />
                {!isAll && <div className="mt-auto pt-2 text-center text-[9px] text-[#475569] font-bold uppercase tracking-wider">Hits &amp; blocks aren't tracked per period</div>}
            </Box>

            {/* Box 3 — Special Teams (game totals) */}
            <Box title="Special Teams" icon={ShieldAlert} color="#9D4CDD" testId="pgc-ps-box-special">
                {!isAll && <div className="text-[9px] text-[#475569] font-bold uppercase tracking-wider mb-2">Game total</div>}
                <div className="grid gap-3">
                    <ST id={awayId} ppg={aS.pp_goals} ppo={aS.pp_attempts} pkK={(hS.pp_attempts ?? 0) - (hS.pp_goals ?? 0)} pkO={hS.pp_attempts ?? 0} color={aCol} />
                    <ST id={homeId} ppg={hS.pp_goals} ppo={hS.pp_attempts} pkK={(aS.pp_attempts ?? 0) - (aS.pp_goals ?? 0)} pkO={aS.pp_attempts ?? 0} color={hCol} />
                </div>
            </Box>
            </div>
        </div>
    );
}

// ===================== Scoring / Penalties rows =====================
function GoalRow({ g, matchMap, badges = [], photoFor = null }) {
    const pwClk = usePwClock();
    return (
        <div className="flex items-start gap-2 sm:gap-3 py-2.5 border-b border-[#1E2233]/60 last:border-0" data-testid="pgc-goal-row">
            <span className="font-mono-stat text-xs text-[#64748B] w-10 shrink-0 pt-0.5">{pwClk(g.time, g.period)}</span>
            <span className="shrink-0 pt-0.5"><TeamBadge teamId={g.team_id} size="sm" /></span>
            <span className="shrink-0"><PlayerAvatar photo={g.scorer?.photo_url || (photoFor ? photoFor(g.scorer?.pwhl_id) : "")} name={g.scorer?.name} playerId={matchMap[g.scorer?.pwhl_id]} size="sm" showTierRing={false} /></span>
            <div className="min-w-0 text-sm flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <Goal size={13} className="text-[#5BD1B7] shrink-0" />
                    <span className="text-white font-semibold"><PName person={g.scorer} matchMap={matchMap} /></span>
                    {badges.map((code) => <GameBadge key={code} code={code} />)}
                </div>
                <div className="text-xs text-[#94A3B8] mt-0.5">
                    {g.assists?.length ? <>Assists: {g.assists.map((a, j) => <React.Fragment key={j}>{j > 0 ? ", " : ""}<PName person={a} matchMap={matchMap} /></React.Fragment>)}</> : <span className="italic">Unassisted</span>}
                </div>
            </div>
        </div>
    );
}
function PenRow({ p, matchMap }) {
    const pwClk = usePwClock();
    return (
        <div className="flex items-center gap-2 sm:gap-3 py-2.5 border-b border-[#1E2233]/60 last:border-0 text-sm" data-testid="pgc-pen-row">
            <span className="font-mono-stat text-xs text-[#64748B] w-10 shrink-0">{pwClk(p.time, p.period)}</span>
            <span className="shrink-0"><TeamBadge teamId={p.team_id} size="sm" /></span>
            <ShieldAlert size={14} className="text-[#F59E0B] shrink-0" />
            <span className="text-white font-semibold"><PName person={p.player} matchMap={matchMap} /></span>
            <span className="text-[#94A3B8]">{p.infraction}</span>
            <span className="ml-auto font-mono-stat text-[#94A3B8] text-xs">{p.minutes} min</span>
        </div>
    );
}

function GroupedByPeriod({ items, children }) {
    const groups = useMemo(() => {
        const g = {};
        items.forEach((it) => { (g[it.period] = g[it.period] || []).push(it); });
        return Object.entries(g).sort((a, b) => Number(a[0]) - Number(b[0]));
    }, [items]);
    return (
        <div className="space-y-4">
            {groups.map(([per, list]) => (
                <div key={per}>
                    <div className="text-[10px] uppercase tracking-wider text-[#9D4CDD] font-bold mb-1">{periodLong(Number(per))}</div>
                    {list.map((it, i) => children(it, `${per}-${i}`))}
                </div>
            ))}
        </div>
    );
}

// ===================== Game Summary (goals + penalties + full play-by-play) =====================
const _timeSec = (t) => { const [a, b] = String(t || "0:0").split(":").map(Number); return (a || 0) * 60 + (b || 0); };
const GS_TOGGLE_DEFS = [
    { key: "goal", label: "Goals", def: true },
    { key: "penalty", label: "Penalties", def: false },
    { key: "shot", label: "Shots", def: false },
    { key: "faceoff", label: "Faceoffs", def: false },
    { key: "hit", label: "Hits", def: false },
    { key: "blocked_shot", label: "Blocked", def: false },
    { key: "goalie_change", label: "Goalie", def: false },
    { key: "penaltyshot", label: "Pen. Shot", def: false },
    { key: "shootout", label: "Shootout", def: false },
];
// The 5 statistic categories exposed in the Game Centre nav (map to event types
// for the Team-Summary events list, and to column groups for Player Summary).
const GC_CATEGORIES = [
    { key: "goal", label: "Goals" },
    { key: "penalty", label: "Penalties" },
    { key: "shot", label: "Shots" },
    { key: "blocked_shot", label: "Blocks" },
    { key: "hit", label: "Hits" },
    { key: "faceoff", label: "Faceoffs" },
    { key: "goalie_change", label: "Goalie" },
];
const GS_STORE_KEY = "rosterly_gs_toggles";
const GS_MODE_KEY = "rosterly_gs_mode";
const GS_SINGLE_KEY = "rosterly_gs_single";
const loadGsToggles = () => {
    const base = Object.fromEntries(GS_TOGGLE_DEFS.map((t) => [t.key, t.def]));
    try { return { ...base, ...(JSON.parse(localStorage.getItem(GS_STORE_KEY)) || {}) }; } catch { return base; }
};
const GS_PLAY_ICON = { shot: { c: Crosshair, cls: "text-[#94A3B8]" }, faceoff: { c: CircleDot, cls: "text-[#5BD1B7]" }, goalie_change: { c: Shield, cls: "text-[#9D4CDD]" }, blocked_shot: { c: Shield, cls: "text-[#38BDF8]" }, hit: { c: Activity, cls: "text-[#FBBF24]" }, penaltyshot: { c: Crosshair, cls: "text-[#5BD1B7]" }, shootout: { c: Crosshair, cls: "text-[#5BD1B7]" } };
// Compress the verbose feed detail strings into short, scannable phrases.
const shortenPlay = (e) => {
    const d = e.detail || "";
    switch (e.type) {
        case "shot":
            return d.replace(/^Shot on goal by /i, "Shot by ").replace(/\s*\([^)]*\)/, "").replace(/,\s*saved by /i, ", saved by ");
        case "faceoff": {
            const mt = d.match(/Faceoff won by (.+?)(?: against (.+))?$/i);
            return mt ? (mt[2] ? `${mt[1]} FOW vs ${mt[2]}` : `${mt[1]} FOW`) : d;
        }
        case "goalie_change": {
            let mt = d.match(/^(.+?) enters the net for (.+)$/i);
            if (mt) return `G – ${mt[1]} in for ${mt[2]}`;
            mt = d.match(/^(.+?) in net$/i);
            if (mt) return `G – ${mt[1]} in net`;
            mt = d.match(/^(.+?) to the bench$/i);
            if (mt) return `G – ${mt[1]} to bench`;
            mt = d.match(/Goaltender\s*(.*?)\s*enters the game(?:\s+for\s+(.+))?$/i);
            if (mt) {
                const gin = (mt[1] || "").trim(), gout = (mt[2] || "").trim();
                if (gin && gout) return `G – ${gin} in for ${gout}`;
                if (gin) return `G – ${gin} in net`;
                if (gout) return `G – ${gout} to bench`;
            }
            return d;
        }
        case "blocked_shot":
            return d.replace(/^Shot by (.+?)\s*\([^)]*\)\s*blocked by /i, "$1 shot blocked by ").replace(/^Shot by (.+?)\s+blocked by /i, "$1 shot blocked by ");
        case "penaltyshot":
            return d.replace(/^Penalty shot by /i, "Pen shot: ");
        default:
            return d;
    }
};

function PlayLine({ e }) {
    const pwClk = usePwClock();
    const ic = GS_PLAY_ICON[e.type] || { c: Activity, cls: "text-[#64748B]" };
    const Icon = ic.c;
    return (
        <div className="flex items-center gap-2 sm:gap-3 py-1.5 text-sm" data-testid="pgc-gs-play-row">
            <span className="font-mono-stat text-xs text-[#64748B] w-10 shrink-0">{pwClk(e.time, e.period)}</span>
            {e.team_id ? <span className="shrink-0"><TeamBadge teamId={e.team_id} size="sm" /></span> : <span className="w-5 shrink-0" />}
            <Icon size={14} className={`shrink-0 ${ic.cls}`} />
            <span className="text-[#CBD5E1] flex-1 min-w-0 break-words">{shortenPlay(e)}</span>
        </div>
    );
}

// Horizontal game timeline. A minute-accurate per-period axis with a clean vertical
// divider between periods; goals plot as the scorer's photo above the axis and
// penalties as dots below. Two lanes underneath plot faceoffs and shots (team-coloured).
// A "Play Recap" walks every play-by-play event one at a time.
function GameTimelineRow({ data, m, matchMap, photoFor, admin = false, insPeriod = "combined", gameId = "" }) {
    const pwClk = usePwClock();
    const { ownerView } = useAdminMode();   // speed settings gear shows ONLY in Owner View
    const homeId = m?.home_team_id, awayId = m?.away_team_id;   // timeline left = away, right = home
    const [active, setActive] = useState(null);
    const [hoverEvt, setHoverEvt] = useState(null);   // hovering a timeline point → scrub the recap card (no tooltip)
    // Viewer-facing filter for the single-line timeline. Defaults to the meaningful
    // events only (goals, penalties, shootout) so it isn't overwhelming.
    const [rTypes, setRTypes] = useState({ goal: true, penalty: true, shootout: true, shot: false, faceoff: false, hit: false, blocked_shot: false, goalie_change: false });
    const [recapIdx, setRecapIdx] = useState(null);
    const [playing, setPlaying] = useState(false);
    // Recap pacing. Owners tune the per-event-type base durations (persisted);
    // EVERY account gets the 1x → 2x → 0.5x multiplier button.
    const RECAP_SPEEDS_KEY = "pgc-recap-speeds";
    const RECAP_MULT_KEY = "pgc-recap-mult";
    // Per-CATEGORY speeds: each play type has a "to" (glide-in duration) and "from"
    // (dwell at the point before leaving). periodPause = hold at each period end.
    const DEFAULT_SPEEDS = {
        periodPause: 1000,
        periodStartPause: 600,
        goal: { to: 1100, from: 1600 },
        penalty: { to: 1100, from: 1300 },
        shootout: { to: 800, from: 3000 },
        shot: { to: 700, from: 300 },
        faceoff: { to: 700, from: 300 },
        hit: { to: 700, from: 400 },
        blocked_shot: { to: 700, from: 400 },
        goalie_change: { to: 900, from: 800 },
    };
    const [speeds, setSpeeds] = useState(() => {
        try { return { ...DEFAULT_SPEEDS, ...JSON.parse(localStorage.getItem(RECAP_SPEEDS_KEY) || "{}") }; } catch { return DEFAULT_SPEEDS; }
    });
    const [speedMult, setSpeedMult] = useState(() => Number(localStorage.getItem(RECAP_MULT_KEY)) || 1);
    const [showSpeedCfg, setShowSpeedCfg] = useState(false);
    const cycleMult = () => { const next = speedMult === 1 ? 2 : speedMult === 2 ? 0.5 : 1; setSpeedMult(next); try { localStorage.setItem(RECAP_MULT_KEY, String(next)); } catch { /* ignore */ } };
    const setSpeedTF = (cat, field, val) => {
        const n = Math.max(0, Math.round(Number(val) || 0));
        setSpeeds((s) => {
            const next = (cat === "periodPause" || cat === "periodStartPause") ? { ...s, [cat]: n } : { ...s, [cat]: { ...(typeof s[cat] === "object" ? s[cat] : {}), [field]: n } };
            try { localStorage.setItem(RECAP_SPEEDS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
            return next;
        });
    };
    const SPEED_CATS = [["goal", "Goals"], ["penalty", "Penalties"], ["shootout", "Shootout"], ["shot", "Shots"], ["faceoff", "Faceoffs"], ["hit", "Hits"], ["blocked_shot", "Blocks"], ["goalie_change", "Goalies"]];
    const catSp = (cat) => { const d = DEFAULT_SPEEDS[cat] || { to: 900, from: 600 }; const c = (speeds && typeof speeds[cat] === "object") ? speeds[cat] : {}; return { to: Number(c.to ?? d.to), from: Number(c.from ?? d.from) }; };
    // PBP card display prefs (owner): alignment + whether to show the player photo.
    const PBP_DISP_KEY = "pgc-pbp-disp";
    const [pbpDisp, setPbpDisp] = useState(() => { try { return { align: "left", photo: true, ...JSON.parse(localStorage.getItem(PBP_DISP_KEY) || "{}") }; } catch { return { align: "left", photo: true }; } });
    const setPbpDispVal = (k, v) => setPbpDisp((s) => { const n = { ...s, [k]: v }; try { localStorage.setItem(PBP_DISP_KEY, JSON.stringify(n)); } catch { /* ignore */ } return n; });
    // Studio doc drives which PBP types are live for the Game Centre (GC surface)
    // AND the exact template used in the recap — same source as the public feed.
    const { data: studio } = useQuery({ queryKey: ["studio-templates"], queryFn: getStudioTemplates, staleTime: 5 * 60 * 1000 });
    const qc = useQueryClient();
    const saveSurfaces = useMutation({
        mutationFn: (next) => saveStudioTemplates(next),
        onMutate: (next) => { qc.setQueryData(["studio-templates"], next); qc.setQueryData(["studio-templates", "public"], next); },
        onSuccess: () => { qc.invalidateQueries({ queryKey: ["studio-templates"] }); toast.success("Saved"); },
        onError: () => { qc.invalidateQueries({ queryKey: ["studio-templates"] }); toast.error("Couldn't save"); },
    });
    const LANE_KEY = { goal: "pbp_goal_ticker", penalty: "pbp_penalty_ticker", shot: "pbp_shot_ticker", faceoff: "pbp_faceoff_ticker", goalie_change: "pbp_goalie_ticker", hit: "pbp_hit_ticker", blocked_shot: "pbp_block_ticker", shootout: "pbp_shootout_ticker" };
    const gcOn = (type) => pbpSurface(studio, LANE_KEY[type] || "pbp_shot_ticker").gc;
    // Per-template surface ticks — EXACTLY like the Creator live templates:
    // separate Stories (S) and Game Centre (GC) toggles, independent, persisted.
    const setSurface = (key, field, val) => {
        saveSurfaces.mutate({ ...(studio || {}), pbpSurfaces: { ...((studio || {}).pbpSurfaces || {}), [key]: { ...pbpSurface(studio, key), [field]: val } } });
    };
    // Build the EXACT public-feed deck for THIS game (all live GROUP members —
    // result, three stars, next game AND every GC-live PBP type), so the recap
    // plays the same grouped template the public sees.
    const { data: sPlayers } = useQuery({ queryKey: ["players", "stories"], queryFn: () => getPlayers({ limit: 300 }), staleTime: 5 * 60 * 1000 });
    const { data: sSchedule } = useQuery({ queryKey: ["pwhl-schedule", "stories"], queryFn: () => getPwhlSchedule(), staleTime: 5 * 60 * 1000 });
    const { data: sTrades } = useQuery({ queryKey: ["trades", "stories"], queryFn: () => getTrades(), staleTime: 5 * 60 * 1000 });
    const { data: sTransfers } = useQuery({ queryKey: ["transfers", "stories"], queryFn: () => getTransfers(), staleTime: 5 * 60 * 1000 });
    const { data: sContracts } = useQuery({ queryKey: ["contract-history", "stories"], queryFn: () => getContractHistory(), staleTime: 5 * 60 * 1000 });
    const { data: sNews } = useQuery({ queryKey: ["news", "stories"], queryFn: () => getNews({ limit: 300 }), staleTime: 5 * 60 * 1000 });
    const { data: sThreeStars } = useQuery({ queryKey: ["three-stars", "stories"], queryFn: getPwhlThreeStars, staleTime: 5 * 60 * 1000 });
    const { data: sScoring } = useQuery({ queryKey: ["pwhl-scoring", "stories"], queryFn: () => getPwhlScoring(), staleTime: 5 * 60 * 1000 });
    const { data: sPxp } = useQuery({ queryKey: ["pwhl-pxp", "stories"], queryFn: () => getPwhlPxp(), staleTime: 5 * 60 * 1000 });
    const { data: sGoalieLogs } = useQuery({ queryKey: ["goalie-game-logs", "stories"], queryFn: () => getGoalieGameLogs(), staleTime: 5 * 60 * 1000 });
    const sList = (d) => (Array.isArray(d) ? d : (d?.items || []));
    const deckIdx = useMemo(() => buildPlayerIndex(sPlayers || []), [sPlayers]);
    const deckSchedule = useMemo(() => sList(sSchedule), [sSchedule]);
    const deckReal = useMemo(() => buildRealStories({
        schedule: deckSchedule, trades: sList(sTrades), transfers: sList(sTransfers), contracts: sList(sContracts),
        news: sList(sNews), players: sPlayers || [], threeStars: Array.isArray(sThreeStars) ? sThreeStars : [],
        scoring: Array.isArray(sScoring) ? sScoring : [], pxp: Array.isArray(sPxp) ? sPxp : [],
        goalieLogs: Array.isArray(sGoalieLogs) ? sGoalieLogs : [],
    }), [deckSchedule, sTrades, sTransfers, sContracts, sNews, sPlayers, sThreeStars, sScoring, sPxp, sGoalieLogs]);
    // The full ordered slide list for this game = the public deck, expanded.
    const gcOrdered = useMemo(() => {
        const st = studio || {};
        const groups = st.groups || [];
        // A "PBP group" contains PBP ticker templates; every OTHER group (e.g. the
        // "Game Recap" row of team-vs-team / three-stars / next / standings) is a
        // RECAP group. This is fully AUTOMATIC — driven by the same studio groups
        // config the public Stories feed uses, so the Game Centre mirrors Stories.
        const pbpGroupIds = groups.filter((g) => (g.keys || []).some((k) => PBP_SURFACE_KEYS.includes(k))).map((g) => g.id);
        const groupOf = (it) => { const k = pubKeyForItem(it); if (!k) return null; const g = groups.find((gg) => (gg.keys || []).includes(k)); return g ? g.id : null; };
        const dOnly = (x) => String(x || "").slice(0, 10);
        const gameRow = (deckSchedule || []).find((g) => String(g.id) === String(gameId) || String(g.game_id) === String(gameId));
        const gameDate = dOnly(gameRow?.date);
        const mine = (deckReal.items || []).filter((it) => {
            if ((it.orientation || "v") !== "v") return false;
            // Keep grouped templates (recap + PBP) AND every PBP ticker/spot even when
            // it isn't in a studio group — the Game Centre recap must always play the
            // play-by-play. (Still gated below by each template's GC surface toggle.)
            if (groupOf(it) == null && !PBP_SURFACE_KEYS.includes(pubKeyForItem(it))) return false;
            if (pubKeyForItem(it) === "game_today_preview") return false;                                      // GC: no "scheduled games" card here
            const gid = it.s?._gameId;
            if (gid != null && String(gid) === String(gameId)) return true;                                   // this game's items
            if (gid == null && gameDate && dOnly(it.date) === gameDate) return true;                           // day-level group member
            return false;
        }).filter((it) => !isItemHiddenGC(it, st)).map((it) => applyStudioToItem(it, st));
        const ordered = applyGroupsAndPriority(mine, st);
        // In the Game Centre the PBP footer links BACK to the public Stories feed
        // (anchored to this game's day) — not to the Game Centre you're already on.
        const href = gameDate ? `/stories?date=${gameDate}` : "/stories";
        const tag = (sv) => (sv ? { ...sv, _gcMode: true, _storiesHref: href } : sv);
        const tagged = ordered.map((it) => {
            let sv = tag(it.s);
            if (sv && Array.isArray(sv.items)) sv = { ...sv, items: sv.items.map((sl) => ({ ...sl, s: tag(sl.s) })) };
            if (sv && Array.isArray(sv.slides)) sv = { ...sv, slides: sv.slides.map((sl) => ({ ...sl, item: sl.item ? { ...sl.item, s: tag(sl.item.s) } : sl.item })) };
            return { ...it, s: sv };
        });
        const k = (it) => pubKeyForItem(it);
        const isPbp = (it) => pbpGroupIds.includes(it.s?._groupId) || PBP_SURFACE_KEYS.includes(k(it));
        // "Starting In Net" (goalie duo) leads the PBP recap section as its FIRST card.
        const duo = tagged.filter((it) => k(it) === "game_goalie_duo");
        const pbp = [...duo, ...tagged.filter(isPbp)];
        const recap = tagged.filter((it) => !isPbp(it) && k(it) !== "game_goalie_duo");
        return { pbp, recap };
    }, [deckReal.items, studio, gameId, deckSchedule]);
    const deckItems = gcOrdered.pbp;
    const recapRowItems = gcOrdered.recap;
    // The S|GC toggle rows = EVERY member of the group(s) that make up this
    // game's deck (all 8 in Play-by-Play), each with its own S + GC tick.
    const surfaceRows = useMemo(() => {
        const gids = [...new Set(deckItems.map((it) => it.s?._groupId).filter(Boolean))];
        const grps = ((studio && studio.groups) || []).filter((g) => gids.includes(g.id));
        const keys = grps.length
            ? [...new Set(grps.flatMap((g) => g.keys || []))]
            : ["pbp_goal_spot", "pbp_penalty_spot", "pbp_shot_ticker", "pbp_faceoff_ticker", "pbp_goalie_ticker"];
        return keys.map((k) => [k, TPL_LABEL[k] || k]);
    }, [deckItems, studio]);
    const recapSlides = useMemo(() => {
        const KT = { goal: "goal", penalty: "penalty", shot: "shot", faceoff: "faceoff", goalie: "goalie_change", hit: "hit", block: "blocked_shot", shootout: "shootout" };
        const okKind = (sv) => { const k = sv?._pbpKind; if (!k) return true; if (k === "shootout") return false; return !!rTypes[KT[k]]; };
        const out = [];
        deckItems.forEach((it) => {
            if (it.tpl?.layout === "slides" && Array.isArray(it.s?.slides)) it.s.slides.forEach((sl) => { if (okKind(sl.item?.s)) out.push(sl.item); });
            else if (it.tpl?.layout === "group_row" && Array.isArray(it.s?.items)) it.s.items.forEach((sl) => { if (okKind(sl.s)) out.push(sl); });
            else if (okKind(it.s)) out.push({ tpl: it.tpl, s: it.s, hideEls: it.hideEls || [], square: it.square, skin: it.skin });
        });
        return out;
    }, [deckItems, rTypes]);
    // Rich shootout cards built from data.shootout (the pxp shootout events are nameless).
    // The GAME-DECIDING attempt is computed (not the raw is_gwg go-ahead flag): the
    // earliest attempt after which the trailing team can no longer catch up. If that
    // decider is a GOAL → the shooter won it; if it's a SAVE → the WINNER's goalie
    // gets a "Game-Winning Save" goalie variant.
    const shootoutSlides = useMemo(() => {
        if (!rTypes.shootout) return [];
        const so = data.shootout || [];
        if (!so.length) return [];
        const winnerTeam = (m.home_score || 0) > (m.away_score || 0) ? homeId : awayId;
        let decOrder = null, wg = 0, lg = 0;
        for (let i = 0; i < so.length; i++) {
            const e = so[i];
            if (e.is_goal) { if (e.team_id === winnerTeam) wg++; else lg++; }
            const loserRemaining = so.slice(i + 1).filter((x) => x.team_id !== winnerTeam).length;
            if (wg - lg > loserRemaining) { decOrder = e.order; break; }
        }
        if (decOrder == null) decOrder = so[so.length - 1].order;
        return so.map((e) => {
            const isDecider = e.order === decOrder;
            const gwSave = isDecider && !e.is_goal;       // game won on a save by the winner's goalie
            const teamId = gwSave ? winnerTeam : e.team_id;
            const subject = gwSave
                ? { kind: "player", id: e.goalie_pwhl_id || e.goalie_name, name: e.goalie_name, photo_url: (photoFor ? photoFor(e.goalie_pwhl_id, e.goalie_name) : null) }
                : { kind: "player", id: e.pwhl_id || e.name, name: e.name, photo_url: (photoFor ? photoFor(e.pwhl_id, e.name) : null) };
            const label = gwSave ? "Game-Winning Save" : (isDecider && e.is_goal ? "Shootout Winner" : "Shootout");
            const detail = gwSave
                ? `Stopped ${e.name} to seal it · GAME WINNER`
                : `${e.is_goal ? "Scored" : "Stopped"}${e.goalie_name ? ` ${e.is_goal ? "past" : "by"} ${e.goalie_name}` : ""}${isDecider && e.is_goal ? " · GAME WINNER" : ""}`;
            return {
                _soOrder: e.order,
                _soSave: gwSave,
                tpl: { key: `so-${e.order}`, layout: "pbp_card", v1Key: "pbp_shootout_ticker" },
                s: { _pbpKind: "shootout", _soSave: gwSave, _soOrder: e.order, period: 5, time: `SO ${e.order}`, label,
                     teamColor: teamColor(teamId), teamId, subject, detail },
            };
        });
    }, [data.shootout, rTypes.shootout, homeId, awayId, m.home_score, m.away_score]); // eslint-disable-line react-hooks/exhaustive-deps
    // "Starting In Net" duo = the IDLE state of the single recap card (not a play).
    const duoSlide = useMemo(() => recapSlides.find((sl) => !sl.s?._pbpKind && (sl.s?.home || sl.s?.away)) || null, [recapSlides]);
    // Playback = STRICTLY chronological (P1 0:00 → SO end), ONLY the toggled-on
    // event kinds (recapSlides is already rTypes-filtered), duo excluded.
    const _chrono = (a, b) => {
        const pa = a.s?.period ?? 99, pb = b.s?.period ?? 99;
        if (pa !== pb) return pa - pb;
        const sa = a.s?._soOrder != null ? a.s._soOrder : _timeSec(a.s?.time);
        const sb = b.s?._soOrder != null ? b.s._soOrder : _timeSec(b.s?.time);
        return sa - sb;
    };
    const recapSlidesAll = useMemo(() => {
        const evs = recapSlides.filter((sl) => !!sl.s?._pbpKind);
        return [...evs, ...shootoutSlides].sort(_chrono);
    }, [recapSlides, shootoutSlides]); // eslint-disable-line react-hooks/exhaustive-deps
    const goals = data.goals || [];
    const goalMeta = useMemo(() => {
        const gs = goals;
        const order = gs.map((g, i) => i).sort((a, b) => (gs[a].period - gs[b].period) || (_timeSec(gs[a].time) - _timeSec(gs[b].time)));
        const runByGi = {}; const running = {};
        order.forEach((gi) => { const id = gs[gi].scorer?.pwhl_id; if (id) { running[id] = (running[id] || 0) + 1; runByGi[gi] = running[id]; } });
        return { firstIdx: order[0], lastIdx: order[order.length - 1], runByGi };
    }, [goals]);
    const goalBadges = (g) => {
        const b = [];
        if (g._gi === goalMeta.firstIdx) b.push("FG");
        if (g.strength === "PP") b.push("PPG");
        if (g.strength === "SH") b.push("SHG");
        if (g.empty_net || g.strength === "EN") b.push("ENG");
        if (g.game_winning) b.push("GWG");
        if (g._gi === goalMeta.lastIdx) b.push("LG");
        const run = goalMeta.runByGi[g._gi] || 0;
        if (run === 3) b.push("HAT"); else if (run >= 4) b.push(`${run}G`);
        return b;
    };
    const PLAY_COL = { shot: "#94A3B8", blocked_shot: "#38BDF8", goalie_change: "#9D4CDD", hit: "#FBBF24", penaltyshot: "#F472B6", shootout: "#F472B6", faceoff: "#5BD1B7" };
    const primary = useMemo(() => {
        const evs = [];
        goals.forEach((g, i) => evs.push({ ...g, _type: "goal", _secs: _timeSec(g.time), _gi: i, _id: `g${i}` }));
        (data.penalties || []).forEach((p, i) => evs.push({ ...p, _type: "penalty", _secs: _timeSec(p.time), _id: `pen${i}` }));
        return evs;
    }, [data.goals, data.penalties]);
    const shotEvs = useMemo(() => (data.pxp || []).filter((e) => ["shot", "goal"].includes(e.type) && e.period).map((e, i) => ({ ...e, _type: e.type, _secs: _timeSec(e.time), _id: `s${i}` })), [data.pxp]);
    const foEvs = useMemo(() => (data.pxp || []).filter((e) => e.type === "faceoff" && e.period).map((e, i) => ({ ...e, _type: "faceoff", _secs: _timeSec(e.time), _id: `f${i}` })), [data.pxp]);
    const goalieEvs = useMemo(() => (data.pxp || []).filter((e) => e.type === "goalie_change" && e.period).map((e, i) => ({ ...e, _type: "goalie_change", _secs: _timeSec(e.time), _id: `gc${i}` })), [data.pxp]);
    const hitEvs = useMemo(() => (data.pxp || []).filter((e) => e.type === "hit" && e.period).map((e, i) => ({ ...e, _type: "hit", _secs: _timeSec(e.time), _id: `h${i}` })), [data.pxp]);
    const blockEvs = useMemo(() => (data.pxp || []).filter((e) => e.type === "blocked_shot" && e.period).map((e, i) => ({ ...e, _type: "blocked_shot", _secs: _timeSec(e.time), _id: `bl${i}` })), [data.pxp]);
    // Shootout is clockless — it gets its OWN small band AFTER overtime (period 5);
    // attempts are spread evenly across it, in order.
    const hasSO = (data.shootout || []).length > 0;
    const soLen = Math.max(420, (data.shootout || []).length * 80);   // wide enough that attempts don't stack into one dot
    const shootoutEvs = useMemo(() => {
        const so = data.shootout || [];
        return so.map((e) => ({ ...e, _type: "shootout", period: 5, order: e.order, _secs: Math.round(soLen * e.order / (so.length + 1)), time: `SO ${e.order}`, _id: `so${e.order}`, team_id: e.team_id }));
    }, [data.shootout, soLen]);
    // Unified, ordered recap points = exactly what's plotted (all event kinds).
    const allPoints = useMemo(() => [...primary, ...shotEvs.filter((e) => e._type === "shot"), ...foEvs, ...goalieEvs, ...hitEvs, ...blockEvs, ...shootoutEvs].sort((a, b) => (a.period - b.period) || (a._secs - b._secs)), [primary, shotEvs, foEvs, goalieEvs, hitEvs, blockEvs, shootoutEvs]);
    // Buckets by kind → the single-line timeline draws whichever are toggled ON.
    const tlBuckets = useMemo(() => ({
        goal: primary.filter((e) => e._type === "goal"),
        penalty: primary.filter((e) => e._type === "penalty"),
        shootout: shootoutEvs,
        shot: shotEvs.filter((e) => e._type === "shot"),
        faceoff: foEvs,
        hit: hitEvs,
        blocked_shot: blockEvs,
        goalie_change: goalieEvs,
    }), [primary, shotEvs, foEvs, goalieEvs, hitEvs, blockEvs, shootoutEvs]);
    const timelineEvents = useMemo(() => {
        const out = [];
        Object.entries(tlBuckets).forEach(([t, arr]) => { if (rTypes[t]) out.push(...arr); });
        return out.sort((a, b) => (a.period - b.period) || (a._secs - b._secs));
    }, [tlBuckets, rTypes]);
    const TL_TYPES = [["goal", "Goals", "#34D399"], ["penalty", "Penalties", "#F87171"], ["shootout", "Shootout", "#F472B6"], ["shot", "Shots", "#38BDF8"], ["faceoff", "Faceoffs", "#A78BFA"], ["hit", "Hits", "#FBBF24"], ["blocked_shot", "Blocks", "#22D3EE"], ["goalie_change", "Goalies", "#94A3B8"]];

    const pset = new Set(); allPoints.forEach((e) => { if (e.period) pset.add(e.period); });
    const isPlayoff = data.stage === "playoffs";
    const playedMax = Math.max(3, ...(pset.size ? [...pset] : [3]));
    const hasOT = !!m.overtime || playedMax >= 4;
    // Playoffs / any non-shootout game → periods 4..playedMax are ALL sudden-death
    // OTs (OT1, OT2, OT3, OT4 …). Regular season → period 4 is the 3-on-3 OT and
    // period 5 is the shootout. A playoff period 5/6/7 is NEVER a "shootout".
    const otPeriods = hasSO ? [4] : (hasOT ? Array.from({ length: playedMax - 3 }, (_, i) => i + 4) : []);
    const basePeriods = [1, 2, 3, ...otPeriods, ...(hasSO ? [5] : [])];
    const isMultiOT = !hasSO && otPeriods.length > 1;
    const perShort = (p) => (p <= 3 ? `P${p}` : (hasSO && p === 5) ? "SO" : (isMultiOT ? `OT${p - 3}` : "OT"));
    const perFull = (p) => (p <= 3 ? `Period ${p}` : (hasSO && p === 5) ? "Shootout" : (isMultiOT ? `Overtime ${p - 3}` : "Overtime"));
    // The timeline follows the page's Period filter: a single period zooms to full
    // width; Regulation shows P1-3; Combined shows everything.
    const periodFilter = { p1: [1], p2: [2], p3: [3], ot: [4], so: [5], regulation: [1, 2, 3] }[insPeriod] || null;
    const periods = periodFilter ? basePeriods.filter((p) => periodFilter.includes(p)) : basePeriods;
    const shownPeriods = periods.length ? periods : basePeriods;
    // OT is 20 min in the playoffs, 5 min in the regular season. The Shootout (5)
    // is a compact fixed band with no clock.
    const perLen = (p) => (p <= 3 ? 1200 : (hasSO && p === 5) ? soLen : (isPlayoff ? 1200 : 300));
    // Intermission gap sized to ONE 5-minute grid cell so the axis stays a uniform
    // grid across boundaries (20:00 → next 0:00 spaced like any other tick), divider CENTRED.
    const GAP_SECS = 300;
    const totalSecs = shownPeriods.reduce((s, p) => s + perLen(p), 0) + GAP_SECS * Math.max(0, shownPeriods.length - 1);
    const startSecs = {}; { let acc = 0; shownPeriods.forEach((p, i) => { startSecs[p] = acc; acc += perLen(p) + (i < shownPeriods.length - 1 ? GAP_SECS : 0); }); }
    const absPct = (absSecs) => (absSecs / totalSecs) * 100;
    const firstShown = shownPeriods[0];
    const lastShown = shownPeriods[shownPeriods.length - 1];
    const periodInView = (p) => shownPeriods.includes(p);
    // Positions are computed against the CURRENT view. Out-of-view periods map to
    // just off the near edge so a period-filter change ZOOMS smoothly for EVERY
    // toggle — every element stays mounted and animates its `left` (nothing
    // remounts), so P2/P3/OT glide in exactly like Regulation→P1 does.
    const pctAt = (p, secs) => (periodInView(p)
        ? ((startSecs[p] + Math.min(perLen(p), Math.max(0, secs || 0))) / totalSecs) * 100
        : (p < firstShown ? -12 : 112));
    const widthPct = (p) => (periodInView(p) ? (perLen(p) / totalSecs) * 100 : 0);
    // Clamp an EVENT dot's x% so it stays fully INSIDE its own period (dots at
    // 0:00 / 20:00 would otherwise straddle a divider). Axis ticks are NOT clamped
    // so minute spacing stays perfectly even up to the period boundary.
    const clampInPeriod = (p, left, insetPct) => {
        if (left == null) return null;
        if (!periodInView(p)) return left;   // off-canvas — leave at the edge
        const start = (startSecs[p] / totalSecs) * 100;
        const end = start + widthPct(p);
        return Math.min(end - insetPct, Math.max(start + insetPct, left));
    };
    const inView = (e) => periodInView(e.period);

    // ===== RECAP PLAYBACK MODEL (per the owner's spec) =====
    // • NOT one continuous sweep. The playhead hops POINT → POINT.
    // • Every hop takes the SAME time (speeds.hop), whatever the seconds gap.
    // • The playhead GLIDES smoothly A→B (no instant jump within a period).
    // • It does NOT cross the intermission wall: it reaches the period END, holds
    //   (speeds.periodPause), then TELEPORTS to the next period's START and repeats.
    // • Each shootout attempt dwells speeds.shootout (default 3s).
    // recapStops = per shown period: [ pstart, …events, pend ]. Shootout (P5) adds
    // attempt stops (kind "so"). Reg/preseason: the playhead reaches OT 5:00 and (if
    // no SO) disappears; if a SO exists it continues into the SO band then vanishes.
    // Playoffs: sweep OT until the winning GOAL (no pend on the last period).
    const capPeriod = lastShown;
    const blockPeriods = (shownPeriods || []).filter((p) => p <= capPeriod);
    const recapStops = useMemo(() => {
        if (!recapSlidesAll.length) return [];
        const out = [];
        blockPeriods.forEach((p, bi) => {
            const isLast = bi === blockPeriods.length - 1;
            const evs = recapSlidesAll.filter((sl) => (sl.s?.period ?? 0) === p);
            if (hasSO && p === 5) {
                evs.forEach((sl) => out.push({ kind: "so", period: p, secs: sl.s?._soOrder ?? 0, slide: sl }));
                return;
            }
            out.push({ kind: "pstart", period: p, secs: 0, slide: { s: { _boundary: "start", period: p } } });
            evs.forEach((sl) => out.push({ kind: "event", period: p, secs: _timeSec(sl.s?.time), slide: sl }));
            if (!(isPlayoff && isLast)) out.push({ kind: "pend", period: p, secs: perLen(p), slide: { s: { _boundary: "end", period: p } } });
        });
        return out;
    }, [recapSlidesAll, blockPeriods, isPlayoff]); // eslint-disable-line react-hooks/exhaustive-deps
    const recapCount = recapStops.length;
    const stopLefts = useMemo(() => recapStops.map((st) => (st.period != null ? pctAt(st.period, st.secs) : 0)), [recapStops]); // eslint-disable-line react-hooks/exhaustive-deps
    const recapActive = recapIdx != null;
    const curStop = recapActive ? recapStops[recapIdx] : null;
    const cur = curStop?.slide || null;
    const curS = ((curStop?.kind === "event" || curStop?.kind === "so") ? cur?.s : null) || null;
    const curPeriod = curStop?.period ?? null;
    const curCol = (curS && curS.teamColor) || teamColor(homeId) || "#C9A6F0";
    const curTimeLabel = curStop?.kind === "pstart" ? "Start" : curStop?.kind === "pend" ? "End" : pwClk(curS?.time || "", curS?.period);
    // The playhead lives ON the rink clock. Shootout is clockless → the line FADES
    // OUT at the end of regulation/OT and the SO cards then play one-by-one with no
    // line. lastRinkIdx = the final on-ice stop (OT 5:00 in reg season); the line
    // freezes there and fades while the "so" stops advance.
    const lastRinkIdx = useMemo(() => { let idx = 0; recapStops.forEach((st, i) => { if (st.kind !== "so") idx = i; }); return idx; }, [recapStops]);
    const soPhase = curStop?.kind === "so";
    const travellerLeft = soPhase ? (stopLefts[lastRinkIdx] ?? 0) : (stopLefts[recapIdx] ?? 0);
    const KIND_TO_CAT = { goal: "goal", penalty: "penalty", shot: "shot", faceoff: "faceoff", goalie: "goalie_change", hit: "hit", block: "blocked_shot", shootout: "shootout" };
    const _mult = speedMult || 1;
    const isTeleport = (i) => i > 0 && (recapStops[i]?.kind === "pstart" || (recapStops[i]?.kind === "so" && recapStops[i - 1]?.kind !== "so"));
    const stopCat = (i) => (recapStops[i]?.kind === "so" ? "shootout" : KIND_TO_CAT[recapStops[i]?.slide?.s?._pbpKind]);
    const glideInto = (i) => {                                               // ms to glide INTO stop i ("speed to")
        if (i <= 0 || isTeleport(i)) return 0;
        if (recapStops[i]?.kind === "pend") return Math.round(900 / _mult);
        const c = stopCat(i);
        return c ? Math.max(80, Math.round(catSp(c).to / _mult)) : Math.round(900 / _mult);
    };
    const holdAt = (i) => {                                                  // ms to dwell at stop i ("speed from" / pauses)
        const k = recapStops[i]?.kind;
        if (k === "pend") return Math.max(0, Math.round((Number(speeds.periodPause) || 1000) / _mult));
        if (k === "pstart") return Math.max(0, Math.round((Number(speeds.periodStartPause) || 600) / _mult));
        const c = stopCat(i);
        return c ? Math.max(0, Math.round(catSp(c).from / _mult)) : 0;
    };
    const [travelDurMs, setTravelDurMs] = useState(0);
    // Advance: glide into stop i (glideInto), hold (holdAt), then move to i+1. Last
    // stop → hold then CLEAR (playhead completely disappears).
    useEffect(() => {
        if (!recapActive || !playing) return undefined;
        const i = recapIdx;
        const wait = glideInto(i) + holdAt(i);
        if (i >= recapCount - 1) {
            const t = setTimeout(() => { setRecapIdx(null); setPlaying(false); }, wait);
            return () => clearTimeout(t);
        }
        const t = setTimeout(() => { setTravelDurMs(glideInto(i + 1)); setRecapIdx(i + 1); }, wait);
        return () => clearTimeout(t);
    }, [recapIdx, playing, recapCount, speeds, speedMult]); // eslint-disable-line react-hooks/exhaustive-deps
    // Highlight the matching lane dot while a PBP slide is showing.
    const curKey = (curS && curS.period != null && curS.time != null) ? `${curS.period}-${curS.time}` : null;
    const KIND_TO_TYPE = { goal: "goal", penalty: "penalty", shot: "shot", faceoff: "faceoff", goalie: "goalie_change", hit: "hit", block: "blocked_shot", shootout: "shootout" };
    const slideIdxForEvent = (e) => {
        if (!e) return -1;
        if (e._type === "shootout") return recapStops.findIndex((st) => st.slide?.s?._soOrder === e.order);
        return recapStops.findIndex((st) => (st.kind === "event") && st.slide?.s?.period === e.period && st.slide?.s?.time === e.time && KIND_TO_TYPE[st.slide?.s?._pbpKind] === e._type);
    };
    const hoverIdx = useMemo(() => slideIdxForEvent(hoverEvt), [hoverEvt, recapStops]); // eslint-disable-line react-hooks/exhaustive-deps
    const hoverSlide = hoverIdx >= 0 ? recapStops[hoverIdx]?.slide : null;
    const activeSlide = hoverSlide || (recapActive ? cur : null);
    const travelKey = activeSlide ? (hoverSlide ? `h${hoverEvt?._id}` : `r${recapIdx}`) : "idle";
    const _seekToStop = (stopIdx) => { setPlaying(false); setTravelDurMs(320); setRecapIdx(stopIdx); };
    const jumpToEvent = (e) => { const i = slideIdxForEvent(e); if (i >= 0) _seekToStop(i); };
    const onPt = (e) => jumpToEvent(e);
    const stepRecap = (delta) => _seekToStop(Math.max(0, Math.min(recapCount - 1, (recapIdx ?? 0) + delta)));
    const startRecap = () => { if (recapCount) { setTravelDurMs(0); setRecapIdx(0); setPlaying(true); } };
    const stopRecap = () => { setRecapIdx(null); setPlaying(false); setActive(null); setHoverEvt(null); };
    useEffect(() => { setRecapIdx(null); setPlaying(false); setActive(null); setHoverEvt(null); }, [insPeriod]);


    // One integrated row (coloured dots, or scorer photos when variant="goal").
    // No box — shares the container's full-height dividers and x-axis. Positions
    // animate (transition-[left]) so period-filter zooms glide smoothly.
    // HOVER a dot → scrub the recap card to that play (no tooltip). CLICK → pin it.
    const Lane = ({ label, events, testid, variant = "dot" }) => {
        const inset = variant === "goal" ? 1.4 : 0.8;
        return (
            <div className="relative mt-1" style={{ height: variant === "goal" ? 46 : 30 }} onMouseLeave={() => setHoverEvt(null)} data-testid={testid}>
                <span className="absolute left-0 top-0 z-20 text-[8px] uppercase tracking-[0.16em] font-black text-white/30 leading-none pointer-events-none">{label} <span className="text-white/15">{events.filter(inView).length}</span></span>
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-white/[0.06]" />
                {events.map((e) => {
                    const vis = inView(e);
                    const l = clampInPeriod(e.period, pctAt(e.period, e._secs), inset); if (l == null) return null;
                    const isGoal = e._type === "goal", isPen = e._type === "penalty";
                    const col = isGoal ? (teamColor(e.team_id) || "#22C55E") : isPen ? "#EF4444" : (teamColor(e.team_id) || PLAY_COL[e._type] || "#64748B");
                    const on = active === e._id || (hoverEvt && hoverEvt._id === e._id) || (curKey && `${e.period}-${e.time}` === curKey);
                    return (
                        <button key={e._id} type="button" data-testid={`pgc-timeline-lane-${e._id}`} onMouseEnter={() => { if (vis) setHoverEvt(e); }} onClick={() => { if (vis) onPt(e); }}
                            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 transition-[left,opacity] duration-500 ease-out" style={{ left: `${l}%`, opacity: vis ? 1 : 0, pointerEvents: vis ? "auto" : "none" }}>
                            {variant === "goal" ? (
                                <span className="grid place-items-center rounded-full transition-transform overflow-hidden" style={{ width: 30, height: 30, background: col, transform: on ? "scale(1.15)" : "scale(1)", boxShadow: on ? `0 0 0 3px ${col}66, 0 0 10px ${col}` : `0 0 7px ${col}99` }}>
                                    <span className="rounded-full overflow-hidden" style={{ width: 26, height: 26 }}>
                                        <PlayerAvatar photo={photoFor ? photoFor(e.scorer?.pwhl_id) : ""} name={e.scorer?.name} playerId={matchMap[e.scorer?.pwhl_id]} size="xs" showTierRing={false} sizePx={26} />
                                    </span>
                                </span>
                            ) : (
                                <span className="block rounded-full transition-transform" style={{ background: col, width: isPen ? 12 : 9, height: isPen ? 12 : 9, transform: on ? "scale(1.6)" : "scale(1)", opacity: on ? 1 : 0.82, border: isPen ? "2px solid #0A0B10" : "none", boxShadow: on ? `0 0 9px ${col}` : "none" }} />
                            )}
                        </button>
                    );
                })}
            </div>
        );
    };

    // ===== Slim HORIZONTAL play-by-play strip (replaces the 4:5 recap deck) =====
    const KIND_COL = { goal: "#34D399", penalty: "#F87171", shootout: "#F472B6", shot: "#38BDF8", faceoff: "#A78BFA", hit: "#FBBF24", block: "#22D3EE", goalie: "#94A3B8" };
    const _da = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[øØ]/g, "o");
    const perLbl = (p, t) => `${perShort(p)} · ${t || ""}`;
    const MiniFace = ({ url, col, px = 30 }) => (
        url ? <img src={url} alt="" className="rounded-full object-cover shrink-0" style={{ width: px, height: px, border: `2px solid ${col}` }} />
            : <span className="rounded-full shrink-0" style={{ width: px, height: px, background: `${col}33`, border: `2px solid ${col}` }} />
    );
    const SlimPbp = ({ sl }) => {
        const s = sl.s || {};
        const isDuo = !s._pbpKind && (s.home || s.away);
        const col = s.teamColor || KIND_COL[s._pbpKind] || "#64748B";
        const on = (hoverSlide && hoverSlide === sl) || (recapActive && cur === sl);
        const evt = allPoints.find((e) => s.period === e.period && s.time === e.time && KIND_TO_TYPE[s._pbpKind] === e._type);
        const ring = on ? { borderColor: col, boxShadow: `0 0 0 1px ${col}, 0 0 14px ${col}55` } : {};
        if (isDuo) {
            const gh = s.home?.subject, ga = s.away?.subject;
            return (
                <div className="shrink-0 w-[236px] rounded-xl border border-white/12 bg-white/[0.03] px-3 py-2 flex items-center gap-2.5 transition-all" style={ring} data-testid="pgc-slim-duo">
                    <span className="text-[9px] font-black uppercase tracking-wider shrink-0" style={{ color: col }}>Starting<br />In Net</span>
                    <div className="flex items-center gap-1.5 min-w-0">
                        <div className="flex flex-col items-center min-w-0"><MiniFace url={ga?.photo_url} col={s.away?.teamColor || col} px={30} /><span className="text-[9px] text-white/70 truncate max-w-[52px] mt-0.5">{_da((ga?.name || "").split(" ").slice(-1)[0])}</span></div>
                        <span className="text-[8px] text-white/30 font-black">@</span>
                        <div className="flex flex-col items-center min-w-0"><MiniFace url={gh?.photo_url} col={s.home?.teamColor || col} px={30} /><span className="text-[9px] text-white/70 truncate max-w-[52px] mt-0.5">{_da((gh?.name || "").split(" ").slice(-1)[0])}</span></div>
                    </div>
                </div>
            );
        }
        return (
            <button type="button" onMouseEnter={() => { if (evt) setHoverEvt(evt); }} onMouseLeave={() => setHoverEvt(null)}
                className="shrink-0 w-[212px] rounded-xl border border-white/10 bg-white/[0.02] pl-2 pr-3 py-2 flex items-center gap-2 text-left transition-all" style={{ borderLeft: `3px solid ${col}`, ...ring }} data-testid="pgc-slim-pbp">
                <MiniFace url={s.subject?.photo_url} col={col} px={34} />
                <div className="min-w-0">
                    <div className="text-[8px] font-black uppercase tracking-wider" style={{ color: col }}>{s.label} · {evt ? perLbl(s.period, s.time) : (s.period ? perLbl(s.period, s.time) : "")}</div>
                    <div className="text-[12px] font-bold text-white leading-tight truncate">{_da(s.subject?.name)}</div>
                    {s.detail && <div className="text-[10px] text-white/55 leading-tight truncate">{_da(s.detail)}</div>}
                </div>
            </button>
        );
    };

    // ===== ONE horizontal recap card (idle = Starting Goalies; recap travels) =====
    const RecapGoalieChip = ({ subj, col, label, stat }) => (
        <div className="flex flex-col items-center min-w-0">
            <span className="rounded-full grid place-items-center" style={{ padding: 2, background: `${col}22`, boxShadow: `0 0 0 2px ${col}, 0 0 18px ${col}66` }}>
                <MiniFace url={subj?.photo_url} col="transparent" px={54} />
            </span>
            <span className="text-[12px] font-black text-white truncate max-w-[110px] mt-1.5 leading-tight">{_da(subj?.name)}</span>
            {label && <span className="text-[8px] font-bold uppercase tracking-[0.18em]" style={{ color: col }}>{label}</span>}
            {stat && <span className="text-[9px] font-mono-stat text-white/55 mt-0.5">{stat}</span>}
        </div>
    );
    const RecapTravellerCard = ({ slide, duo, travelKey }) => {
        const show = slide || duo;
        if (!show) return <div className="min-h-[116px] grid place-items-center text-white/30 text-xs rounded-2xl border border-white/10 bg-black/25" data-testid="pgc-recap-traveller-empty">Press Play Recap to walk through the game.</div>;
        const s = show.s || {};
        const isBoundary = !!s._boundary;
        const isDuo = !s._pbpKind && !isBoundary && (s.home || s.away);
        const col = s.teamColor || (isBoundary ? (teamColor(homeId) || "#9D4CDD") : KIND_COL[s._pbpKind]) || "#64748B";
        const gh = s.home?.subject, ga = s.away?.subject;
        const perName = (p) => perFull(p);
        const justify = pbpDisp.align === "middle" ? "justify-center" : pbpDisp.align === "right" ? "justify-end" : "justify-start";
        return (
            <div className="relative overflow-hidden rounded-2xl border min-h-[116px] backdrop-blur-xl" style={{ borderColor: `${col}44`, background: `linear-gradient(115deg, ${col}22 0%, rgba(10,11,16,0.85) 42%, rgba(10,11,16,0.92) 100%)` }} data-testid="pgc-recap-traveller">
                <span className="absolute -left-10 -top-10 h-40 w-40 rounded-full blur-3xl pointer-events-none" style={{ background: `${col}55` }} />
                <span className="absolute top-0 inset-x-0 h-[3px]" style={{ background: `linear-gradient(90deg, ${col}, transparent)` }} />
                <div key={travelKey} className={`pgc-travel-in relative flex items-center gap-5 px-6 py-4 min-h-[116px] ${(isDuo || isBoundary) ? "justify-center" : justify}`}>
                    {isBoundary ? (
                        <div className="flex flex-col items-center text-center" data-testid="pgc-recap-boundary">
                            <span className="text-[11px] font-black uppercase tracking-[0.28em]" style={{ color: col }}>{perName(s.period)}</span>
                            <span className="text-2xl font-black text-white uppercase tracking-[0.12em] mt-0.5">{s._boundary === "start" ? "Start" : "End"}</span>
                        </div>
                    ) : isDuo ? (
                        <>
                            <span className="text-[13px] font-black uppercase tracking-[0.14em] leading-tight shrink-0 text-white text-center">Starting<br /><span style={{ color: col }}>In Net</span></span>
                            <div className="flex items-center gap-6 min-w-0">
                                <RecapGoalieChip subj={ga} col={s.away?.teamColor || teamColor(s.away?.teamId) || col} label={teamAbbr(s.away?.teamId) || ""} stat={s.away?.statLine} />
                                <span className="text-[11px] text-white/25 font-black italic">vs</span>
                                <RecapGoalieChip subj={gh} col={s.home?.teamColor || teamColor(s.home?.teamId) || col} label={teamAbbr(s.home?.teamId) || ""} stat={s.home?.statLine} />
                            </div>
                        </>
                    ) : (
                        <>
                            {pbpDisp.photo && (
                                <span className="relative shrink-0 rounded-full grid place-items-center" style={{ padding: 3, background: `${col}22`, boxShadow: `0 0 0 2px ${col}, 0 0 22px ${col}55` }}>
                                    <MiniFace url={s.subject?.photo_url || (photoFor ? photoFor(s.subject?.id) : null)} col="transparent" px={64} />
                                </span>
                            )}
                            <div className="min-w-0">
                                <div className="inline-flex items-center gap-2 mb-1">
                                    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[9px] font-black uppercase tracking-[0.16em] text-white" style={{ background: col, boxShadow: `0 0 12px ${col}88` }}>{s._soSave && <Shield className="h-2.5 w-2.5" />}{s.label}</span>
                                    {s.period != null && <span className="font-mono-stat text-[10px] text-white/55">{perLbl(s.period, s.time)}</span>}
                                </div>
                                <div className="text-xl font-black text-white leading-tight truncate">{_da(s.subject?.name)}</div>
                                {s.detail && <div className="text-[13px] text-white/75 leading-snug mt-0.5 line-clamp-2">{_da(s.detail)}</div>}
                            </div>
                            {teamAbbr(s.teamId) && (
                                <span className="shrink-0 self-start text-[10px] font-black uppercase tracking-[0.16em] rounded-md px-2 py-1" style={{ color: col, background: `${col}18`, border: `1px solid ${col}44` }}>{teamAbbr(s.teamId)}</span>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    };


    return (
        <div data-testid="pgc-timeline-row">
            <div className="flex items-center gap-2 mb-3">
                <span className="h-6 w-1 rounded-full bg-[#9D4CDD]" />
                <span className="text-sm font-black uppercase tracking-[0.2em] text-white">Game Timeline</span>
            </div>
            {ownerView && allPoints.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-3 rounded-xl border border-[#9D4CDD]/25 bg-[#9D4CDD]/[0.06] px-3 py-2" data-testid="pgc-timeline-surfaces">
                    <span className="text-[9px] font-black uppercase tracking-widest text-[#C9A6F0] mr-1">Live on</span>
                    {surfaceRows.map(([key, lbl]) => {
                        const surf = pbpSurface(studio, key);
                        const Tick = ({ field }) => {
                            const on = surf[field];
                            return (
                                <button type="button" onClick={() => setSurface(key, field, !on)} disabled={saveSurfaces.isPending} data-testid={`pgc-surface-${field}-${key}`}
                                    title={`${field === "s" ? "Stories timeline" : "Game Centre"} — ${on ? "showing" : "hidden"}`}
                                    className={`inline-flex items-center gap-0.5 rounded-md border px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider transition-colors ${on ? "border-[#EC4899]/60 bg-[#EC4899]/15 text-[#F9A8D4]" : "border-white/15 text-white/35 hover:text-white"}`}>
                                    {on ? <Check className="h-2.5 w-2.5" /> : <span className="h-2.5 w-2.5 inline-block" />}{field === "s" ? "S" : "GC"}
                                </button>
                            );
                        };
                        return (
                            <span key={key} className="inline-flex items-center gap-1" data-testid={`pgc-surfaces-${key}`}>
                                <span className="text-[9px] font-bold uppercase tracking-wide text-white/45 mr-0.5">{lbl}</span>
                                <Tick field="s" /><Tick field="gc" />
                            </span>
                        );
                    })}
                </div>
            )}
            {recapRowItems.length > 0 && (
                <div className="mb-4 space-y-3" data-testid="pgc-recap-row">
                    {recapRowItems.map((it) => (
                        <StoryTemplateView key={it.id} tpl={it.tpl} s={it.s} orientation="v" hideEls={it.hideEls} square={it.square} skin={it.skin} idx={deckIdx} studio={studio} schedule={deckSchedule} testId={`pgc-recaprow-${it.id}`} />
                    ))}
                </div>
            )}
            {/* Traditional horizontal play-by-play removed per redesign. */}
            {allPoints.length === 0 && recapCount === 0 ? (
                <div className="rounded-2xl border border-white/10 bg-black/20 p-8 text-sm text-white/40 grid place-items-center" data-testid="pgc-timeline-empty">Nothing to plot yet.</div>
            ) : (
                <div className="space-y-5" data-testid="pgc-timeline-canvas">
                    {/* TIMELINE — its own full-width row */}
                    <div className="min-w-0">
                    {allPoints.length === 0 ? (
                        <div className="rounded-2xl border border-white/10 bg-black/20 p-8 text-sm text-white/40 grid place-items-center min-h-[180px]" data-testid="pgc-timeline-empty">No timeline events.</div>
                    ) : (<>
                    {/* Recap + speed controls — moved ONTO the timeline (was in the header) */}
                    <div className="flex items-center justify-end gap-2 mb-2 relative">
                        {allPoints.length > 0 && (
                            <button type="button" data-testid="pgc-recap-speed" onClick={cycleMult} title="Playback speed"
                                className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-black/25 px-2.5 py-1 text-[11px] font-black tabular-nums text-white/80 hover:text-white hover:border-white/30 transition-colors">
                                <Timer size={12} />{speedMult === 0.5 ? "0.5" : speedMult}x
                            </button>
                        )}
                        {allPoints.length > 0 && ownerView && (
                            <button type="button" data-testid="pgc-recap-speed-cfg-btn" onClick={() => setShowSpeedCfg((s) => !s)} title="Owner View: recap speed settings"
                                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-wider transition-colors ${showSpeedCfg ? "border-[#9D4CDD] bg-[#9D4CDD]/30 text-white" : "border-[#9D4CDD]/60 bg-[#9D4CDD]/15 text-[#E9D5FF] hover:bg-[#9D4CDD]/25"}`}>
                                <Settings2 size={13} /> Speeds
                            </button>
                        )}
                        {showSpeedCfg && ownerView && (
                            <div className="absolute right-0 top-9 z-50 w-72 rounded-xl border border-[#9D4CDD]/40 bg-[#12121C] p-3 shadow-2xl max-h-[70vh] overflow-y-auto" data-testid="pgc-recap-speed-cfg">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-[10px] font-black uppercase tracking-wider text-[#C9A6F0]">Recap speed (ms)</span>
                                    <button type="button" data-testid="pgc-recap-speed-reset" onClick={() => { setSpeeds(DEFAULT_SPEEDS); try { localStorage.setItem(RECAP_SPEEDS_KEY, JSON.stringify(DEFAULT_SPEEDS)); } catch { /* ignore */ } }}
                                        className="text-[9px] uppercase font-bold text-white/40 hover:text-white transition-colors">Reset</button>
                                </div>
                                <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-2 gap-y-1.5">
                                    <span className="text-[9px] uppercase tracking-wider text-white/35" />
                                    <span className="text-[9px] uppercase tracking-wider text-white/35 text-center w-16">To</span>
                                    <span className="text-[9px] uppercase tracking-wider text-white/35 text-center w-16">From</span>
                                    {SPEED_CATS.map(([k, label]) => (
                                        <React.Fragment key={k}>
                                            <span className="text-[11px] text-white/70">{label}</span>
                                            <input type="number" min="0" step="100" value={catSp(k).to} data-testid={`pgc-recap-speed-${k}-to`}
                                                onChange={(e) => setSpeedTF(k, "to", e.target.value)}
                                                className="w-16 rounded-md border border-white/15 bg-black/40 px-2 py-1 text-[11px] text-white text-right font-mono-stat focus:border-[#9D4CDD] focus:outline-none" />
                                            <input type="number" min="0" step="100" value={catSp(k).from} data-testid={`pgc-recap-speed-${k}-from`}
                                                onChange={(e) => setSpeedTF(k, "from", e.target.value)}
                                                className="w-16 rounded-md border border-white/15 bg-black/40 px-2 py-1 text-[11px] text-white text-right font-mono-stat focus:border-[#9D4CDD] focus:outline-none" />
                                        </React.Fragment>
                                    ))}
                                    <span className="text-[11px] text-white/70 mt-1">Period-Start Pause</span>
                                    <input type="number" min="0" step="100" value={Number(speeds.periodStartPause) || 600} data-testid="pgc-recap-speed-periodStartPause"
                                        onChange={(e) => setSpeedTF("periodStartPause", "", e.target.value)}
                                        className="w-16 rounded-md border border-white/15 bg-black/40 px-2 py-1 text-[11px] text-white text-right font-mono-stat focus:border-[#9D4CDD] focus:outline-none mt-1" />
                                    <span className="mt-1" />
                                    <span className="text-[11px] text-white/70 mt-1">Period-End Pause</span>
                                    <input type="number" min="0" step="100" value={Number(speeds.periodPause) || 1000} data-testid="pgc-recap-speed-periodPause"
                                        onChange={(e) => setSpeedTF("periodPause", "", e.target.value)}
                                        className="w-16 rounded-md border border-white/15 bg-black/40 px-2 py-1 text-[11px] text-white text-right font-mono-stat focus:border-[#9D4CDD] focus:outline-none mt-1" />
                                    <span className="mt-1" />
                                </div>
                                <div className="mt-3 pt-2 border-t border-white/10">
                                    <span className="text-[10px] font-black uppercase tracking-wider text-[#C9A6F0]">PBP card display</span>
                                    <div className="flex items-center justify-between mt-1.5">
                                        <span className="text-[11px] text-white/70">Align</span>
                                        <div className="inline-flex rounded-lg border border-white/15 overflow-hidden" data-testid="pgc-pbp-align">
                                            {[["left", "Left"], ["middle", "Middle"], ["right", "Right"]].map(([v, lbl]) => (
                                                <button key={v} type="button" data-testid={`pgc-pbp-align-${v}`} onClick={() => setPbpDispVal("align", v)}
                                                    className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${pbpDisp.align === v ? "bg-[#9D4CDD]/40 text-white" : "text-white/50 hover:text-white"}`}>{lbl}</button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between mt-1.5">
                                        <span className="text-[11px] text-white/70">Player photo</span>
                                        <button type="button" data-testid="pgc-pbp-photo-toggle" onClick={() => setPbpDispVal("photo", !pbpDisp.photo)}
                                            className={`px-2.5 py-1 rounded-lg border text-[10px] font-bold uppercase tracking-wider transition-colors ${pbpDisp.photo ? "border-[#9D4CDD]/60 bg-[#9D4CDD]/30 text-white" : "border-white/15 text-white/50 hover:text-white"}`}>{pbpDisp.photo ? "On" : "Off"}</button>
                                    </div>
                                </div>
                                <p className="mt-2 text-[9px] text-white/35 leading-tight"><b>To</b> = glide time onto each play. <b>From</b> = how long it lingers before leaving. Start/End Pause = hold at each period boundary. All accounts also apply the {speedMult === 0.5 ? "0.5" : speedMult}x multiplier.</p>
                            </div>
                        )}
                        {recapCount > 0 && (recapActive ? (
                            <div className="inline-flex items-center gap-1 rounded-full border border-[#9D4CDD]/45 bg-[#9D4CDD]/[0.12] pl-1 pr-2 py-1" data-testid="pgc-timeline-recap-controls">
                                <button type="button" data-testid="pgc-recap-prev" onClick={() => stepRecap(-1)} className="h-6 w-6 grid place-items-center rounded-full text-white/70 hover:text-white transition-colors"><ArrowLeft size={13} /></button>
                                <button type="button" data-testid="pgc-recap-play" onClick={() => setPlaying((p) => !p)} className="h-6 w-6 grid place-items-center rounded-full border border-[#9D4CDD]/60 bg-[#9D4CDD]/25 text-white hover:bg-[#9D4CDD]/40 transition-colors">{playing ? <Pause size={12} /> : <Play size={12} />}</button>
                                <button type="button" data-testid="pgc-recap-next" onClick={() => stepRecap(1)} className="h-6 w-6 grid place-items-center rounded-full text-white/70 hover:text-white transition-colors"><ArrowRight size={13} /></button>
                                <span className="font-mono-stat text-[10px] text-white/45 px-1 tabular-nums">{recapIdx + 1}/{recapCount}</span>
                                <button type="button" data-testid="pgc-recap-close" onClick={stopRecap} className="h-6 w-6 grid place-items-center rounded-full text-white/40 hover:text-white transition-colors"><X size={13} /></button>
                            </div>
                        ) : (
                            <button type="button" data-testid="pgc-timeline-recap-btn" onClick={startRecap}
                                className="inline-flex items-center gap-1.5 rounded-full border border-[#9D4CDD]/50 bg-[#9D4CDD]/15 px-3 py-1.5 text-[11px] font-black uppercase tracking-wider text-[#E9D5FF] hover:bg-[#9D4CDD]/25 transition-colors">
                                <Zap size={12} /> Play Recap
                            </button>
                        ))}
                    </div>
                    {/* Filter chips — control what the single-line timeline shows */}
                    <div className="flex flex-wrap items-center justify-center gap-1.5 mb-3" data-testid="pgc-timeline-toggles">
                        {TL_TYPES.map(([t, label, col]) => {
                            const evs = (tlBuckets[t] || []).filter(inView);
                            if (t === "shootout" && !shootoutEvs.length) return null;
                            const on = !!rTypes[t];
                            // Head-to-head count (away-home, matching the timeline's left-right).
                            let aC = 0, hC = 0, oC = 0;
                            evs.forEach((e) => { if (e.team_id === awayId) aC++; else if (e.team_id === homeId) hC++; else oC++; });
                            const isH2H = t !== "goalie_change" && (aC + hC) > 0;
                            const countTxt = isH2H ? `${aC}-${hC}` : String(evs.length);
                            return (
                                <button key={t} type="button" onClick={() => setRTypes((s) => ({ ...s, [t]: !s[t] }))} data-testid={`pgc-tl-toggle-${t}`}
                                    className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-wider transition-all duration-200"
                                    style={on ? { borderColor: `${col}88`, background: `${col}1F`, color: "#fff" } : { borderColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.4)" }}>
                                    <span className="h-2 w-2 rounded-full transition-colors" style={{ background: on ? col : "#475569", boxShadow: on ? `0 0 6px ${col}` : "none" }} />
                                    {label}<span className="text-white/40 font-mono-stat tabular-nums">{countTxt}</span>
                                </button>
                            );
                        })}
                    </div>
                    {/* Period labels ABOVE the component (all periods; out-of-view fade) */}
                    <div className="relative h-4 mb-1.5 overflow-x-clip">
                        {basePeriods.map((p) => (
                            <span key={p} className="absolute -translate-x-1/2 text-[9px] uppercase tracking-[0.16em] font-black text-white/45 whitespace-nowrap transition-[left,opacity] duration-500" style={{ left: `${pctAt(p, 0) + widthPct(p) / 2}%`, opacity: periodInView(p) ? 1 : 0 }}>{perFull(p)}</span>
                        ))}
                    </div>
                    {/* ONE second-by-second line. All toggled events ride a single glowing baseline. */}
                    <div className="relative rounded-2xl border border-white/10 bg-gradient-to-b from-[#0E0F16] to-[#080910] px-3 sm:px-5 pt-5 pb-3 overflow-x-clip" onMouseLeave={() => setHoverEvt(null)} data-testid="pgc-timeline-single">
                      {/* Content coordinate wrapper — dividers, line & axis all share the
                          SAME (un-padded) coordinate space so the divider sits EXACTLY
                          halfway between a period's 20:00 tick and the next 0:00 tick. */}
                      <div className="relative">
                        {/* full-height period dividers — centred in the intermission gap */}
                        {basePeriods.map((p) => (
                            <span key={`div${p}`} className="absolute top-0 bottom-9 w-px bg-white/[0.08] z-0 transition-[left,opacity] duration-500" style={{ left: `${absPct(startSecs[p] - GAP_SECS / 2)}%`, opacity: (periodInView(p) && p !== firstShown) ? 1 : 0 }} />
                        ))}
                        {/* THE line */}
                        <div className="relative" style={{ height: 74 }} data-testid="pgc-timeline-line">
                            {/* glowing baseline — per-period segments (NO line across the intermission gap) */}
                            {shownPeriods.map((p) => (
                                <div key={`base${p}`} className="absolute top-1/2 -translate-y-1/2 h-[3px] rounded-full transition-[left,width] duration-500" style={{ left: `${pctAt(p, 0)}%`, width: `${widthPct(p)}%`, background: "linear-gradient(90deg, rgba(255,255,255,0.04), rgba(255,255,255,0.22) 50%, rgba(255,255,255,0.04))", boxShadow: "0 0 12px rgba(157,76,221,0.25)" }} />
                            ))}
                            {/* traveller — glides point-to-point on the rink clock; FADES OUT
                                at OT 5:00 and stays hidden through the (clockless) shootout */}
                            {recapActive && (
                                <span className="absolute top-0 bottom-0 z-40 w-px pointer-events-none" style={{ left: `${travellerLeft}%`, opacity: soPhase ? 0 : 1, background: `linear-gradient(180deg, transparent, ${curCol} 28%, ${curCol} 72%, transparent)`, boxShadow: `0 0 10px ${curCol}`, transitionProperty: "left, opacity", transitionDuration: `${travelDurMs}ms, 600ms`, transitionTimingFunction: "ease-in-out", willChange: "left, opacity" }} data-testid="pgc-recap-traveller">
                                    {!soPhase && <span className="absolute -top-0.5 left-1/2 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md bg-[#0B0C12] border px-1.5 py-0.5 font-mono-stat text-[9px] text-white shadow-lg" style={{ borderColor: `${curCol}66` }}>{perLbl(curPeriod ?? firstShown, curTimeLabel)}</span>}
                                </span>
                            )}
                            {/* HOVER HALO — soft vertical guide + time label that tracks the cursor's play */}
                            {hoverEvt && (() => {
                                const hl = clampInPeriod(hoverEvt.period, pctAt(hoverEvt.period, hoverEvt._secs), 0.8);
                                if (hl == null) return null;
                                return (
                                    <span className="absolute top-0 bottom-0 z-[35] w-px pointer-events-none transition-[left] duration-150 ease-out" style={{ left: `${hl}%`, background: "linear-gradient(180deg, transparent, rgba(255,255,255,0.35) 30%, rgba(255,255,255,0.35) 70%, transparent)" }} data-testid="pgc-hover-halo">
                                        <span className="absolute -top-0.5 left-1/2 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md bg-[#0B0C12] border border-white/15 px-1.5 py-0.5 font-mono-stat text-[9px] text-white/80 shadow-lg">{perLbl(hoverEvt.period, hoverEvt.time)}</span>
                                    </span>
                                );
                            })()}
                            {timelineEvents.length === 0 && (
                                <div className="absolute inset-0 grid place-items-center text-[11px] italic text-white/30">No events for the selected filters.</div>
                            )}
                            {timelineEvents.map((e) => {
                                const vis = inView(e);
                                const l = clampInPeriod(e.period, pctAt(e.period, e._secs), 0.8); if (l == null) return null;
                                const isGoal = e._type === "goal", isPen = e._type === "penalty";
                                const col = isGoal ? (teamColor(e.team_id) || "#34D399") : isPen ? "#F87171" : (teamColor(e.team_id) || PLAY_COL[e._type] || "#64748B");
                                const on = (hoverEvt && hoverEvt._id === e._id) || (curKey && `${e.period}-${e.time}` === curKey);
                                const z = isGoal ? 30 : isPen ? 20 : 10;
                                return (
                                    <button key={e._id} type="button" data-testid={`pgc-tl-pt-${e._id}`} onMouseEnter={() => { if (vis) setHoverEvt(e); }} onClick={() => { if (vis) onPt(e); }}
                                        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 transition-[left,opacity,transform] duration-500 ease-out"
                                        style={{ left: `${l}%`, opacity: vis ? 1 : 0, pointerEvents: vis ? "auto" : "none", zIndex: on ? 45 : z }}>
                                        {isGoal ? (
                                            <span className="grid place-items-center rounded-full transition-transform" style={{ width: on ? 30 : 25, height: on ? 30 : 25, background: col, border: "2px solid rgba(255,255,255,0.9)", boxShadow: on ? `0 0 0 4px ${col}55, 0 0 16px ${col}` : `0 0 9px ${col}cc` }}>
                                                <Goal className="text-white" style={{ width: 13, height: 13 }} />
                                            </span>
                                        ) : isPen ? (
                                            <span className="block" style={{ width: on ? 15 : 12, height: on ? 15 : 12, background: col, transform: "rotate(45deg)", borderRadius: 2, boxShadow: on ? `0 0 0 3px ${col}55, 0 0 12px ${col}` : `0 0 7px ${col}` }} />
                                        ) : (
                                            <span className="block rounded-full transition-transform" style={{ width: on ? 13 : 9, height: on ? 13 : 9, background: col, border: "1.5px solid rgba(255,255,255,0.55)", boxShadow: on ? `0 0 0 3px ${col}44, 0 0 10px ${col}` : `0 0 6px ${col}aa` }} />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        {/* MINUTE AXIS — even 5-min ticks; each period keeps its 20:00 mark */}
                        <div className="relative h-8" data-testid="pgc-timeline-axis">
                            {/* per-period axis segments (NO line across the intermission gap) */}
                            {shownPeriods.map((p) => (
                                <div key={`axis${p}`} className="absolute top-1/2 -translate-y-1/2 h-px bg-white/10 transition-[left,width] duration-500" style={{ left: `${pctAt(p, 0)}%`, width: `${widthPct(p)}%` }} />
                            ))}
                            {basePeriods.map((p) => {
                                const vis = periodInView(p);
                                if (p === 5) {
                                    return (
                                        <React.Fragment key="t-so">
                                            <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-px transition-[left,opacity] duration-500" style={{ left: `${pctAt(p, 0) + widthPct(p) / 2}%`, height: 10, background: "rgba(255,255,255,0.24)", opacity: vis ? 1 : 0 }} />
                                            <span className="absolute -translate-x-1/2 font-mono-stat text-white/35 transition-[left,opacity] duration-500" style={{ left: `${pctAt(p, 0) + widthPct(p) / 2}%`, top: "calc(50% + 7px)", fontSize: 8, opacity: vis ? 1 : 0 }}>SO</span>
                                        </React.Fragment>
                                    );
                                }
                                const maxMin = perLen(p) / 60;
                                return Array.from({ length: maxMin + 1 }).map((_, min) => {
                                    const big = min % 5 === 0;
                                    if (!big) return null;   // cleaner axis: only 5-min ticks
                                    const lp = pctAt(p, min * 60);
                                    const markLeft = lp;   // real intermission gap already separates 20:00 from the next 0:00
                                    const tx = (p === firstShown && min === 0) ? "translate-x-0" : (p === lastShown && min === maxMin) ? "-translate-x-full" : "-translate-x-1/2";
                                    return (
                                        <React.Fragment key={`t${p}-${min}`}>
                                            <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-px transition-[left,opacity] duration-500" style={{ left: `${markLeft}%`, height: 9, background: "rgba(255,255,255,0.24)", opacity: vis ? 1 : 0 }} />
                                            <span className={`absolute ${tx} font-mono-stat text-white/35 transition-[left,opacity] duration-500`} style={{ left: `${markLeft}%`, top: "calc(50% + 7px)", fontSize: 8, opacity: vis ? 1 : 0 }}>{min}</span>
                                        </React.Fragment>
                                    );
                                });
                            })}
                        </div>
                      </div>
                    </div>
                    </>)}
                    </div>
                    {/* ONE horizontal recap card — idle shows Starting Goalies; Play Recap
                        travels through the toggled plays chronologically, one at a time. */}
                    {(duoSlide || recapCount > 0) && (
                        <div className="rounded-2xl border border-white/10 bg-black/20 p-3" data-testid="pgc-recap-card-wrap">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-white/45">Game Recap</span>
                                {recapActive && <span className="font-mono-stat text-[10px] text-white/30 tabular-nums">{recapIdx + 1}/{recapCount}</span>}
                            </div>
                            <RecapTravellerCard slide={activeSlide} duo={duoSlide} travelKey={travelKey} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function GameSummary({ data, m, matchMap, photoFor, statByPwhl, period = "combined", show = {}, bare = false, hideStars = false, horizontal = false }) {
    const pwClk = usePwClock();
    const events = useMemo(() => {
        const gs = (data.goals || []).map((g, i) => ({ ...g, _type: "goal", _secs: _timeSec(g.time), _gi: i }));
        const ps = (data.penalties || []).map((p) => ({ ...p, _type: "penalty", _secs: _timeSec(p.time) }));
        const plays = (data.pxp || [])
            .filter((e) => !["goal", "penalty"].includes(e.type))
            .map((e) => ({ ...e, _type: e.type, _secs: e.secs != null ? e.secs : _timeSec(e.time) }));
        return [...gs, ...ps, ...plays].sort((a, b) => (a.period - b.period) || (a._secs - b._secs));
    }, [data.goals, data.penalties, data.pxp]);

    // Which highlight badges apply to each specific goal (shown next to the scorer).
    const goalMeta = useMemo(() => {
        const gs = data.goals || [];
        const order = gs.map((g, i) => i).sort((a, b) => (gs[a].period - gs[b].period) || (_timeSec(gs[a].time) - _timeSec(gs[b].time)));
        const counts = {};
        gs.forEach((g) => { const id = g.scorer?.pwhl_id; if (id) counts[id] = (counts[id] || 0) + 1; });
        // Running goal number per scorer, in chronological order — so HAT / 4G can
        // be pinned to the EXACT goal where the milestone was reached (not every goal).
        const runByGi = {};
        const running = {};
        order.forEach((gi) => { const id = gs[gi].scorer?.pwhl_id; if (id) { running[id] = (running[id] || 0) + 1; runByGi[gi] = running[id]; } });
        return { firstIdx: order[0], lastIdx: order[order.length - 1], counts, runByGi };
    }, [data.goals]);
    const goalBadges = (g) => {
        const b = [];
        if (g._gi === goalMeta.firstIdx) b.push("FG");
        if (g.strength === "PP") b.push("PPG");
        if (g.strength === "SH") b.push("SHG");
        if (g.empty_net || g.strength === "EN") b.push("ENG");
        if (g.game_winning) b.push("GWG");
        if (g._gi === goalMeta.lastIdx) b.push("LG");
        // HAT only on the scorer's 3rd goal of the game; 4G+ on each subsequent one.
        const run = goalMeta.runByGi[g._gi] || 0;
        if (run === 3) b.push("HAT");
        else if (run >= 4) b.push(`${run}G`);
        return b;
    };

    const anySel = Object.values(show).some(Boolean);
    const shown = useMemo(() => (anySel ? events.filter((e) => show[e._type]) : events), [events, show, anySel]);

    const present = useMemo(() => new Set(events.map((e) => e.period)), [events]);
    const isPlayoff = data.stage === "playoffs";
    const playedMax = Math.max(3, ...(present.size ? [...present].map(Number).filter(Boolean) : [3]));
    const hasSO = !isPlayoff && (m.shootout || present.has(5));
    const otPeriods = hasSO ? [4] : ((m.overtime || playedMax >= 4) ? Array.from({ length: playedMax - 3 }, (_, i) => i + 4) : []);
    const isMultiOT = !hasSO && otPeriods.length > 1;
    const perShort = (p) => (p <= 3 ? `P${p}` : (hasSO && p === 5) ? "SO" : (isMultiOT ? `OT${p - 3}` : "OT"));
    const tabs = ["all", 1, 2, 3, ...otPeriods, ...(hasSO ? [5] : [])];
    const tab = ({ combined: "all", p1: 1, p2: 2, p3: 3, ot: 4, so: 5 }[period]) || "all";
    const inTab = tab === "all" ? shown : shown.filter((e) => e.period === tab);
    const groups = tab === "all"
        ? tabs.filter((t) => t !== "all").map((p) => [p, inTab.filter((e) => e.period === p)]).filter(([, l]) => l.length)
        : [[tab, inTab]];

    const Line = ({ e }) => {
        if (e._type === "goal") return <GoalRow g={e} matchMap={matchMap} badges={goalBadges(e)} photoFor={photoFor} />;
        if (e._type === "penalty") return <PenRow p={e} matchMap={matchMap} />;
        return <PlayLine e={e} />;
    };

    // HORIZONTAL "traditional play-by-play" — the scoring & penalty events flow
    // left→right in compact cards (used in the Game Centre under the recap row).
    if (horizontal) {
        return (
            <Wrap bare={bare}>
                {inTab.length === 0 ? <Empty>No scoring or penalties.</Empty> : (
                    <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1" data-testid="pgc-recap-timeline-h">
                        {inTab.map((e, i) => {
                            const isGoal = e._type === "goal";
                            const col = isGoal ? ((TEAM_INFO[e.team_id] || {}).color || "#22C55E") : e._type === "penalty" ? "#EF4444" : "#64748B";
                            const per = perShort(e.period);
                            return (
                                <div key={`h${i}`} className="shrink-0 w-[236px] rounded-xl border border-white/10 bg-white/[0.02] p-3" style={{ borderTop: `2px solid ${col}` }} data-testid={`pgc-recap-hcard-${i}`}>
                                    <div className="text-[9px] uppercase tracking-[0.16em] font-black text-white/40 mb-1.5">{per} · {pwClk(e.time, e.period)}</div>
                                    <Line e={e} />
                                </div>
                            );
                        })}
                    </div>
                )}
            </Wrap>
        );
    }

    return (
        <Wrap bare={bare}>
            {!hideStars && (data.three_stars || []).length > 0 && (
                <div className="mb-4 pb-4 border-b border-[#1E2233]" data-testid="pgc-gs-three-stars">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#FBBF24] mb-1">Three Stars</div>
                    <div className="grid sm:grid-cols-3 gap-x-4 items-start">
                        {data.three_stars.map((s) => <StarRow key={s.star} s={s} photo={photoFor(s.player?.pwhl_id)} stat={statByPwhl[s.player?.pwhl_id]} matchMap={matchMap} />)}
                    </div>
                </div>
            )}
            {/* Event filters + period tabs moved to the unified nav control bar. */}
            {inTab.length === 0 ? <Empty>No events match the selected filters.</Empty> : (
                <div className="space-y-5 max-h-[620px] overflow-y-auto pr-1" data-testid="pgc-recap-timeline">
                    {groups.map(([per, list]) => (
                        <div key={per} data-testid={`pgc-recap-period-${per}`}>
                            <div className="flex items-center gap-2 mb-2">
                                <span className="inline-flex items-center h-5 px-2 rounded-full bg-[#9D4CDD]/15 text-[10px] uppercase tracking-wider text-[#C9A6F0] font-black">{periodLong(Number(per))}</span>
                                <span className="flex-1 h-px bg-white/10" />
                            </div>
                            <div className="relative pl-6">
                                <span className="absolute left-[8px] top-2 bottom-2 w-[2px] bg-gradient-to-b from-[#9D4CDD]/50 via-white/12 to-transparent" aria-hidden />
                                {list.map((e, i) => {
                                    const isGoal = e._type === "goal";
                                    const col = isGoal ? ((TEAM_INFO[e.team_id] || {}).color || "#22C55E") : e._type === "penalty" ? "#EF4444" : "#64748B";
                                    return (
                                        <div key={`${per}-${i}`} className="relative" data-testid={`pgc-recap-node-${per}-${i}`}>
                                            <span className="absolute -left-[19px] top-[11px] h-3.5 w-3.5 rounded-full border-2 border-[#0A0B10]"
                                                style={{ background: col, boxShadow: isGoal ? `0 0 0 3px ${col}33` : "none" }} aria-hidden />
                                            <Line e={e} />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Wrap>
    );
}
const Wrap = ({ bare, children }) => (bare ? <>{children}</> : <Card title="Game Recap" icon={Goal} testId="pgc-game-summary">{children}</Card>);

// ===================== Three star row =====================
function StarRow({ s, photo, stat, matchMap }) {
    const teamCol = (TEAM_INFO[s.team_id] || {}).color || "#9D4CDD";
    const gold = "#FBBF24";
    const isG = stat?.isG;
    const pos = s.player?.position || (isG ? "G" : "");
    return (
        <div className="flex flex-col items-center text-center rounded-xl border border-white/10 bg-white/[0.02] px-3 py-3" style={{ borderTop: `2px solid ${teamCol}` }} data-testid={`pgc-star-${s.star}`}>
            <div className="flex items-center gap-1 mb-2" style={{ color: gold }}>
                <Star size={12} fill={gold} style={{ color: gold }} />
                <span className="font-mono-stat text-lg font-black leading-none">{s.star}</span>
            </div>
            <PlayerAvatar photo={photo} name={s.player?.name} playerId={matchMap[s.player?.pwhl_id]} size="lg" showTierRing={false} />
            <div className="font-bold text-white leading-tight truncate max-w-full mt-2"><PName person={s.player} matchMap={matchMap} /></div>
            <div className="text-[11px] text-[#94A3B8] mt-0.5 inline-flex items-center gap-1 justify-center">
                <TeamBadge teamId={s.team_id} size="xs" asLink={false} />{pos}{pos ? " · " : ""}{s.player?.jersey ? `#${s.player.jersey}` : ""}
            </div>
            {stat && (
                <div className="flex justify-center gap-1.5 mt-2" data-testid={`pgc-star-${s.star}-stats`}>
                    {isG ? (
                        <>
                            <StatChip label="SV%" value={stat.sa ? (stat.saves / stat.sa).toFixed(3).replace(/^0/, "") : "—"} accent />
                            <StatChip label="SV" value={`${stat.saves ?? 0}/${stat.sa ?? 0}`} />
                        </>
                    ) : (
                        <>
                            <StatChip label="P" value={stat.p ?? 0} accent />
                            <StatChip label="G" value={stat.g ?? 0} />
                            <StatChip label="A" value={stat.a ?? 0} />
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// ===================== Goalie table =====================
const _mmss = (s) => { s = Math.max(0, Math.round(s || 0)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };

function GoalieTable({ rows, matchMap }) {
    if (!rows.length) return <Empty>No goaltenders.</Empty>;
    // Merge multiple stints for the same goalie (pulled & returned mid-game) into
    // ONE row: sum SA / SV / GA and total the TOI.
    const byKey = {}; const order = [];
    rows.forEach((g) => {
        const key = `${g.team_id}:${g.player?.pwhl_id || g.player?.name}`;
        if (!byKey[key]) { byKey[key] = { ...g, saves: 0, shots_against: 0, goals_against: 0, toi_seconds: 0, _dec: [] }; order.push(key); }
        const a = byKey[key];
        a.saves += g.saves || 0; a.shots_against += g.shots_against || 0; a.goals_against += g.goals_against || 0;
        a.toi_seconds += g.toi_seconds || 0;
        if (g.decision) a._dec.push(g.decision);
    });
    const merged = order.map((k) => {
        const g = byKey[k];
        return { ...g, decision: g._dec[0] || g.decision || "—", toi: g.toi_seconds ? _mmss(g.toi_seconds) : (g.toi || "—"), shutout: g.goals_against === 0 };
    });
    return (
        <div className="overflow-x-auto">
            <table className="text-sm table-fixed" data-testid="pgc-goalie-table" style={{ minWidth: 560 }}>
                <colgroup>
                    <col style={{ width: 44 }} /><col style={{ width: 150 }} /><col style={{ width: 44 }} />
                    <col style={{ width: 60 }} /><col style={{ width: 44 }} /><col style={{ width: 44 }} />
                    <col style={{ width: 44 }} /><col style={{ width: 64 }} /><col style={{ width: 44 }} />
                </colgroup>
                <thead><tr className="text-[#64748B] text-[10px] uppercase tracking-wider">
                    <th className="text-left py-2 px-1">Team</th><th className="text-left px-1">Goalie</th><th className="px-1">Dec</th>
                    <th className="text-right px-1">TOI</th><th className="text-right px-1">SA</th><th className="text-right px-1">SV</th>
                    <th className="text-right px-1">GA</th><th className="text-right px-1">SV%</th><th className="text-center px-1">SO</th>
                </tr></thead>
                <tbody>
                    {merged.map((g, i) => (
                        <tr key={i} className="border-t border-[#1E2233]" data-testid={`pgc-goalie-${i}`}>
                            <td className="py-2 px-1"><TeamBadge teamId={g.team_id} size="sm" /></td>
                            <td className="stat-emph text-white px-1 truncate"><PName person={g.player} matchMap={matchMap} /></td>
                            <td className="text-center px-1 font-mono-stat text-[#CBD5E1]">{g.decision || "—"}</td>
                            <td className="text-right px-1 font-mono-stat text-xs">{g.toi || "—"}</td>
                            <td className="text-right px-1 font-mono-stat">{g.shots_against}</td>
                            <td className="text-right px-1 font-mono-stat">{g.saves}</td>
                            <td className="text-right px-1 font-mono-stat">{g.goals_against}</td>
                            <td className="text-right px-1 font-mono-stat">{fmtSv(g.shots_against, g.saves)}</td>
                            <td className="text-center px-1 font-mono-stat">{g.shutout ? "✓" : "0"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ===================== Skater table =====================
// Columns mirror the official box score import: G A P PIM Shots TOI FO Hits +/-.
const SK_COLS = [
    ["g", "G"], ["a", "A"], ["p", "P"], ["pim", "PIM"], ["sog", "Shots"],
    ["toi", "TOI", (s) => s.toi || "—"],
    ["fo", "FO", (s) => `${s.fow || 0}/${s.foa || 0}`],
    ["hits", "Hits"],
    ["plus_minus", "+/-", (s) => (s.plus_minus > 0 ? `+${s.plus_minus}` : s.plus_minus)],
];
function SkaterTable({ rows, matchMap }) {
    if (!rows.length) return <Empty>No skaters.</Empty>;
    const sorted = [...rows].sort((a, b) => b.p - a.p || b.g - a.g);
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-xs table-fixed" data-testid="pgc-skater-table" style={{ minWidth: 520 }}>
                <colgroup>
                    <col style={{ width: 32 }} />
                    <col style={{ width: 150 }} />
                    <col style={{ width: 34 }} />
                    {SK_COLS.map(([k]) => <col key={k} style={{ width: k === "toi" ? 52 : 38 }} />)}
                </colgroup>
                <thead><tr className="text-[#64748B] text-[10px] uppercase tracking-wider">
                    <th className="text-left py-2">#</th><th className="text-left">Skater</th><th className="text-center">Pos</th>
                    {SK_COLS.map(([, l]) => <th key={l} className="text-right px-1">{l}</th>)}
                </tr></thead>
                <tbody>
                    {sorted.map((s, i) => (
                        <tr key={i} className="border-t border-[#1E2233]/70" data-testid={`pgc-skater-row-${s.player?.pwhl_id}`}>
                            <td className="py-1.5 font-mono-stat text-[#64748B] text-center">{s.player?.jersey}</td>
                            <td className="stat-emph text-white truncate"><PName person={s.player} matchMap={matchMap} /></td>
                            <td className="text-center text-[#94A3B8]">{s.player?.position}</td>
                            {SK_COLS.map(([k, l, fn]) => (
                                <td key={k} className="text-right px-1 font-mono-stat text-[#CBD5E1] tabular-nums">{fn ? fn(s) : (s[k] ?? 0)}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ===================== Shot map =====================
function ShotMap({ pxp, m, photoFor, matchMap }) {
    const [show, setShow] = useState({ shot: true, goal: true, blocked_shot: false });
    const [heat, setHeat] = useState(true);
    const events = (pxp || []).filter((e) => e.x != null && show[e.type]);
    const aCol = teamColor(m.away_team_id), hCol = teamColor(m.home_team_id);
    const colorFor = (tid) => (tid === m.home_team_id ? hCol : aCol);
    const W = 600, H = 300;
    // Rink geometry (HockeyTech 0–600 × 0–300 feed space). Goal lines sit ~11ft
    // from the boards → ~5.5% of the playing surface in from each end.
    const PAD = 8, IW = W - PAD * 2;
    const GL_L = PAD + IW * 0.055, GL_R = W - PAD - IW * 0.055;
    const BL_L = PAD + IW * 0.375, BL_R = W - PAD - IW * 0.375;
    // Goals can never be scored from behind the net — clamp their x to the goal line.
    const gx = (e) => (e.type === "goal" ? Math.max(GL_L, Math.min(GL_R, e.x)) : e.x);
    const toggle = (k) => setShow((s) => ({ ...s, [k]: !s[k] }));
    const FaceoffDot = ({ cx, cy }) => (
        <g opacity="0.5"><circle cx={cx} cy={cy} r="16" fill="none" stroke="#7f1d2e" strokeWidth="1.2" /><circle cx={cx} cy={cy} r="2.5" fill="#7f1d2e" /></g>
    );
    return (
        <div data-testid="pgc-shotmap-body">
            <div className="flex flex-wrap items-center gap-2 mb-3 text-[11px]">
                {[["shot", "Shots"], ["goal", "Goals"], ["blocked_shot", "Blocked"]].map(([k, l]) => (
                    <button key={k} type="button" onClick={() => toggle(k)} data-testid={`pgc-shotmap-toggle-${k}`}
                        className={`px-2.5 py-1 rounded-md border uppercase tracking-wider font-bold transition-colors ${show[k] ? "border-[#9D4CDD] bg-[#9D4CDD]/15 text-white" : "border-[#1E2233] text-[#64748B] hover:text-white"}`}>{l}</button>
                ))}
                <button type="button" onClick={() => setHeat((v) => !v)} data-testid="pgc-shotmap-toggle-heat"
                    className={`px-2.5 py-1 rounded-md border uppercase tracking-wider font-bold transition-colors ${heat ? "border-[#FB7185] bg-[#FB7185]/15 text-white" : "border-[#1E2233] text-[#64748B] hover:text-white"}`}>Heat</button>
                <span className="ml-auto flex items-center gap-3 text-[10px] uppercase tracking-wider text-[#94A3B8]">
                    <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: aCol }} />{teamAbbr(m.away_team_id) || m.away_team_code}</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: hCol }} />{teamAbbr(m.home_team_id) || m.home_team_code}</span>
                </span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full rounded-2xl" style={{ aspectRatio: "2/1" }} data-testid="pgc-shotmap-svg">
                <defs>
                    <linearGradient id="rinkice" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0F1320" /><stop offset="100%" stopColor="#0A0C14" />
                    </linearGradient>
                    <filter id="heatblur" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="13" /></filter>
                    {events.filter((e) => e.type === "goal").map((e, i) => {
                        const photo = photoFor ? photoFor(e.player_id) : null;
                        if (!photo) return null;
                        return <clipPath key={i} id={`goalclip-${i}`}><circle cx={gx(e)} cy={e.y} r="11" /></clipPath>;
                    })}
                </defs>
                {/* ice + boards */}
                <rect x={PAD} y={PAD} width={IW} height={H - PAD * 2} rx="44" fill="url(#rinkice)" stroke="#2A3044" strokeWidth="2" />
                {/* zone lines */}
                <line x1={W / 2} y1={PAD} x2={W / 2} y2={H - PAD} stroke="#9f2536" strokeWidth="3" />
                <line x1={BL_L} y1={PAD} x2={BL_L} y2={H - PAD} stroke="#2563eb" strokeWidth="3" opacity="0.8" />
                <line x1={BL_R} y1={PAD} x2={BL_R} y2={H - PAD} stroke="#2563eb" strokeWidth="3" opacity="0.8" />
                {/* goal lines */}
                <line x1={GL_L} y1="22" x2={GL_L} y2={H - 22} stroke="#9f2536" strokeWidth="1.5" opacity="0.8" />
                <line x1={GL_R} y1="22" x2={GL_R} y2={H - 22} stroke="#9f2536" strokeWidth="1.5" opacity="0.8" />
                {/* creases */}
                <path d={`M ${GL_L} ${H / 2 - 16} A 16 16 0 0 1 ${GL_L} ${H / 2 + 16}`} fill="#1d4ed8" opacity="0.25" stroke="#2563eb" strokeWidth="1" />
                <path d={`M ${GL_R} ${H / 2 - 16} A 16 16 0 0 0 ${GL_R} ${H / 2 + 16}`} fill="#1d4ed8" opacity="0.25" stroke="#2563eb" strokeWidth="1" />
                {/* nets */}
                <rect x={GL_L - 7} y={H / 2 - 8} width="7" height="16" rx="2" fill="none" stroke="#94A3B8" strokeWidth="1.2" />
                <rect x={GL_R} y={H / 2 - 8} width="7" height="16" rx="2" fill="none" stroke="#94A3B8" strokeWidth="1.2" />
                {/* center + faceoff circles */}
                <circle cx={W / 2} cy={H / 2} r="26" fill="none" stroke="#243049" strokeWidth="1.5" />
                <circle cx={W / 2} cy={H / 2} r="2.5" fill="#9f2536" />
                {[[BL_L - 52, H * 0.27], [BL_L - 52, H * 0.73], [BL_R + 52, H * 0.27], [BL_R + 52, H * 0.73]].map(([cx, cy], i) => <FaceoffDot key={i} cx={cx} cy={cy} />)}
                {/* heat layer */}
                {heat && (
                    <g filter="url(#heatblur)" opacity="0.5">
                        {events.map((e, i) => <circle key={i} cx={gx(e)} cy={e.y} r="16" fill={colorFor(e.team_id)} opacity={e.type === "goal" ? 0.7 : 0.28} />)}
                    </g>
                )}
                {/* markers */}
                {events.map((e, i) => {
                    const c = colorFor(e.team_id);
                    const X = gx(e);
                    if (e.type === "goal") {
                        const photo = photoFor ? photoFor(e.player_id) : null;
                        const goalIdx = events.filter((x, j) => x.type === "goal" && j <= i).length - 1;
                        return (
                            <g key={i} data-testid={`pgc-shot-${i}`}>
                                <title>{e.detail}</title>
                                {photo ? (
                                    <>
                                        <circle cx={X} cy={e.y} r="13" fill={c} />
                                        <image href={photo} x={X - 11} y={e.y - 11} width="22" height="22" clipPath={`url(#goalclip-${goalIdx})`} preserveAspectRatio="xMidYMid slice" />
                                        <circle cx={X} cy={e.y} r="11" fill="none" stroke="#fff" strokeWidth="2" />
                                    </>
                                ) : (
                                    <>
                                        <circle cx={X} cy={e.y} r="7" fill={c} stroke="#fff" strokeWidth="1.5" />
                                        <circle cx={X} cy={e.y} r="11" fill="none" stroke={c} strokeWidth="1.5" opacity="0.5" />
                                    </>
                                )}
                            </g>
                        );
                    }
                    if (e.type === "blocked_shot") return <rect key={i} x={X - 3} y={e.y - 3} width="6" height="6" fill="none" stroke={c} strokeWidth="1.5" opacity="0.8" transform={`rotate(45 ${X} ${e.y})`} data-testid={`pgc-shot-${i}`} />;
                    return <circle key={i} cx={X} cy={e.y} r="4" fill={c} opacity="0.7" stroke="#0A0C14" strokeWidth="0.5" data-testid={`pgc-shot-${i}`} />;
                })}
            </svg>
            <p className="text-[10px] text-[#64748B] mt-2">● shot · ◎ goal (scorer photo) · ◇ blocked · Heat = shot density. Goals are pinned to the goal line. Coordinates from the official PWHL feed.</p>
        </div>
    );
}

// ===================== Play-by-play =====================
const PXP_TYPE_LABEL = { goal: "Goals", shot: "Shots", penalty: "Penalties", hit: "Hits", faceoff: "Faceoffs", blocked_shot: "Blocked", goalie_change: "Goalie", penaltyshot: "Pen. Shot" };
function PlayByPlay({ pxp, m }) {
    const pwClk = usePwClock();
    const periods = useMemo(() => [...new Set(pxp.map((e) => e.period))].sort((a, b) => a - b), [pxp]);
    const types = useMemo(() => [...new Set(pxp.map((e) => e.type))], [pxp]);
    const [per, setPer] = useState(periods[0] || 1);
    const [typeFilter, setTypeFilter] = useState("all");
    const ICON = { goal: "text-[#22C55E]", penalty: "text-[#FB7185]", shot: "text-[#94A3B8]", hit: "text-[#FBBF24]", faceoff: "text-[#64748B]", blocked_shot: "text-[#64748B]", goalie_change: "text-[#9D4CDD]", penaltyshot: "text-[#22C55E]" };
    const rows = pxp.filter((e) => e.period === per && (typeFilter === "all" || e.type === typeFilter));
    return (
        <div data-testid="pgc-pxp-body">
            <div className="flex flex-wrap gap-1.5 mb-2">
                {periods.map((p) => (
                    <button key={p} type="button" onClick={() => setPer(p)} data-testid={`pgc-pxp-period-${p}`}
                        className={`px-3 py-1 rounded text-xs font-bold uppercase tracking-wider ${per === p ? "bg-[#9D4CDD] text-white" : "bg-white/5 text-[#94A3B8] hover:text-white"}`}>{periodTab(p)}</button>
                ))}
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3 border-t border-[#1E2233] pt-3" data-testid="pgc-pxp-type-filters">
                <button type="button" onClick={() => setTypeFilter("all")} data-testid="pgc-pxp-type-all"
                    className={`px-2.5 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider border ${typeFilter === "all" ? "border-[#9D4CDD] bg-[#9D4CDD]/15 text-white" : "border-[#1E2233] text-[#64748B] hover:text-white"}`}>All</button>
                {types.map((t) => (
                    <button key={t} type="button" onClick={() => setTypeFilter(t)} data-testid={`pgc-pxp-type-${t}`}
                        className={`px-2.5 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider border ${typeFilter === t ? "border-[#9D4CDD] bg-[#9D4CDD]/15 text-white" : "border-[#1E2233] text-[#64748B] hover:text-white"}`}>{PXP_TYPE_LABEL[t] || t.replace("_", " ")}</button>
                ))}
            </div>
            <div className="space-y-1 max-h-[460px] overflow-y-auto pr-1">
                {rows.length === 0 && <Empty>No events for this filter.</Empty>}
                {rows.map((e, i) => (
                    <div key={i} className="flex items-start gap-2 sm:gap-3 py-1.5 px-2 rounded hover:bg-white/5 text-sm" data-testid={`pgc-pxp-row-${i}`}>
                        <span className="font-mono-stat text-xs text-[#64748B] w-9 sm:w-10 shrink-0 pt-0.5">{pwClk(e.time, e.period)}</span>
                        {e.team_id ? <span className="shrink-0 pt-0.5"><TeamBadge teamId={e.team_id} size="sm" /></span> : <span className="w-5 shrink-0" />}
                        <span className={`text-[10px] uppercase tracking-wider font-bold w-14 sm:w-20 shrink-0 leading-tight pt-0.5 ${ICON[e.type] || "text-[#64748B]"}`}>{(PXP_TYPE_LABEL[e.type] || e.type.replace("_", " ")).replace(/s$/, "")}</span>
                        <span className="text-[#CBD5E1] flex-1 min-w-0 break-words">{e.detail}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function StatChip({ label, value, accent = false }) {
    return (
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono-stat text-[10px] font-bold ${accent ? "bg-[#9D4CDD]/25 text-[#E9D5FF]" : "bg-white/5 text-[#CBD5E1]"}`}>
            <span className="text-[8px] uppercase tracking-wider opacity-70">{label}</span>{value}
        </span>
    );
}

// ===================== Game highlight badges & officials =====================
const BADGE_LABEL = { HAT: "Hat Trick", GWG: "Game-Winning Goal", FG: "First Goal", LG: "Last Goal", PPG: "Power-Play Goal", SHG: "Short-Handed Goal", ENG: "Empty-Net Goal" };
const BADGE_TONE = { GWG: "gold", FG: "teal", LG: "blue", PPG: "purple", SHG: "orange", ENG: "slate" };
function GameBadge({ code }) {
    const tone = /^\d+G$/.test(code) || code === "HAT" ? "gold" : (BADGE_TONE[code] || "gold");
    const cls = {
        gold: "bg-[#FBBF24]/20 text-[#FDE68A] border-[#FBBF24]/30",
        teal: "bg-[#5BD1B7]/20 text-[#5BD1B7] border-[#5BD1B7]/30",
        blue: "bg-[#38BDF8]/20 text-[#7DD3FC] border-[#38BDF8]/30",
        purple: "bg-[#9D4CDD]/25 text-[#E9D5FF] border-[#9D4CDD]/40",
        orange: "bg-[#F59E0B]/20 text-[#FDBA74] border-[#F59E0B]/30",
        slate: "bg-white/10 text-[#CBD5E1] border-white/20",
    }[tone];
    return <span className={`inline-flex items-center px-1 py-0.5 rounded text-[8px] font-black uppercase tracking-wide border font-mono-stat ${cls}`} title={BADGE_LABEL[code] || code} data-testid={`pgc-badge-${code}`}>{code}</span>;
}

function GoalieStatsTable({ rows, period = "combined", periodColor = null, photoFor = null, matchMap = {}, metaFor = null }) {
    if (!rows.length) return <Empty>No goalies.</Empty>;
    // (single active-period row per goalie; every shown row is the active one)
    const recCells = (dec) => ([
        { g: "REG", w: dec === "W", l: dec === "L" },
        { g: "OT", w: dec === "OTW", l: dec === "OTL" },
        { g: "SO", w: dec === "SOW", l: dec === "SOL" },
    ]);
    const yn = (b) => (b ? 1 : 0);
    return (
        <div className="space-y-3" data-testid="pgc-goalie-stats">
            {rows.map((gl, i) => {
                const dec = gl.decision || "—";
                const r = goalieRowStats(gl, period);
                const aff = !!periodColor;
                const pid = gl.player?.pwhl_id;
                const photo = photoFor ? photoFor(pid) : (metaFor ? metaFor(pid)?.photo_url : "");
                return (
                    <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid={`pgc-goalie-row-${i}`}>
                        {/* Name + image + game outcome (all inline on one row) */}
                        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                            <div className="flex items-center gap-2 min-w-0">
                                <PlayerAvatar photo={photo} name={gl.player?.name} playerId={matchMap[pid]} size="sm" showTierRing={false} />
                                <span className="text-white font-bold text-sm truncate">{abbrevName(gl.player?.name)}</span>
                            </div>
                            <div className="flex items-center gap-1.5" data-testid={`pgc-goalie-record-${i}`}>
                                {recCells(dec).map((c) => (
                                    <div key={c.g} className="rounded-lg border border-white/10 overflow-hidden">
                                        <div className="text-[8px] font-black uppercase tracking-wider text-center text-[#64748B] bg-white/[0.03] py-0.5">{c.g}</div>
                                        <div className="grid grid-cols-2">
                                            <div className={`text-center text-[10px] font-black py-1 px-1.5 ${c.w ? "bg-[#5BD1B7]/25 text-[#5BD1B7]" : "text-[#3A3F55]"}`} title="Win">W</div>
                                            <div className={`text-center text-[10px] font-black py-1 px-1.5 border-l border-white/10 ${c.l ? "bg-[#F87171]/25 text-[#F87171]" : "text-[#3A3F55]"}`} title="Loss">L</div>
                                        </div>
                                    </div>
                                ))}
                                <div className="rounded-lg border border-white/10 overflow-hidden text-center" title="Shutout" data-testid={`pgc-goalie-so-${i}`}>
                                    <div className="text-[8px] font-black uppercase tracking-wider text-[#64748B] bg-white/[0.03] py-0.5 px-1.5">SO</div>
                                    <div className="text-[11px] font-black py-1 px-1.5 leading-none">{yn(!!gl.shutout)}</div>
                                </div>
                            </div>
                        </div>
                        {/* Saves + Scoring + Shootout — one row */}
                        <div className="flex flex-wrap gap-x-5 gap-y-2" data-testid={`pgc-goalie-${i}`}>
                            <TSec title="Saves" Icon={Shield}>
                                <Well label="SV/SA" value={r.svsa} tip="Saves / shots against (per period)" accent={periodColor} affected={aff} />
                                <Well label="SV%" value={r.svp} tip="Save percentage (per period)" accent={periodColor} affected={aff} />
                                <Well label="GA" value={r.ga} tip="Goals against (per period)" accent={periodColor} affected={aff} />
                            </TSec>
                            <TSec title="Time" Icon={Clock}>
                                <Well label="TOI" value={r.toi} tip="Time on ice (game total)" />
                            </TSec>
                            <TSec title="Scoring" Icon={Goal}>
                                <Well label="P" value={r.p} tip="Points (per period)" accent={periodColor} affected={aff} />
                                <Well label="G" value={r.g} tip="Goals (per period)" accent={periodColor} affected={aff} />
                                <Well label="A" value={r.a} tip="Assists (per period)" accent={periodColor} affected={aff} />
                                <Well label="PIM" value={r.pim} tip="Penalty minutes (per period)" accent={periodColor} affected={aff} />
                            </TSec>
                            <TSec title="Shootout" Icon={Crosshair}>
                                <Well label="SOA/SOGA" value={r.soaSoga} tip="Shootout shots against / goals against" />
                                <Well label="SO%" value={r.soPct} tip="Shootout save % (saves ÷ shootout shots against)" />
                            </TSec>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function OfficialGroup({ label, list, k }) {
    if (!list.length) return null;
    return (
        <div data-testid={`pgc-officials-${k}`}>
            <div className="text-[9px] uppercase tracking-wider text-[#64748B] font-bold mb-1">{label}</div>
            <div className="space-y-0.5">
                {list.map((o, i) => (
                    <div key={i} className="text-white text-sm" data-testid={`pgc-official-${k}-${i}`}>
                        {abbrevName(o.name)}{o.number ? <span className="text-[#64748B]"> (#{o.number})</span> : null}
                    </div>
                ))}
            </div>
        </div>
    );
}

const _pm = (r) => (r.plus_minus > 0 ? `+${r.plus_minus}` : r.plus_minus);
// Percentage formatter — drops a trailing ".0" so 0.0% → 0%, 50.0% → 50%.
const _pct = (x) => `${(+x || 0).toFixed(1).replace(/\.0$/, "")}%`;
// Which period the toggle key maps to (1-3, 4=OT, 5=SO), and readers that pull a
// stat's value from a player's per-period bucket (built from the play-by-play).
const PERIOD_NUM = { p1: 1, p2: 2, p3: 3, ot: 4, so: 5, ot1: 4, ot2: 5, ot3: 6 };
export const _EB = { g: 0, a: 0, a1: 0, a2: 0, evg: 0, ppg: 0, shg: 0, eng: 0, psg: 0, psa: 0, eva: 0, ppa: 0, sha: 0, ena: 0, gwg: 0, uag: 0, pim: 0, sog: 0, fow: 0, fol: 0, hits: 0, blocks: 0, fg: 0, lg: 0, gwga: 0, fga: 0, lga: 0 };
export const PERIODIZED = {
    g: (b) => b.g, a: (b) => b.a, p: (b) => b.g + b.a, a1: (b) => b.a1, a2: (b) => b.a2,
    evg: (b) => b.evg, ppg: (b) => b.ppg, shg: (b) => b.shg, eng: (b) => b.eng,
    eva: (b) => b.eva, ppa: (b) => b.ppa, sha: (b) => b.sha, ena: (b) => b.ena,
    evp: (b) => b.evg + b.eva, ppp: (b) => b.ppg + b.ppa, shp: (b) => b.shg + b.sha, enp: (b) => b.eng + b.ena,
    gwga: (b) => b.gwga || 0, gwgp: (b) => (b.gwg || 0) + (b.gwga || 0),
    fga: (b) => b.fga || 0, fgp: (b) => (b.fg || 0) + (b.fga || 0),
    lga: (b) => b.lga || 0, lgp: (b) => (b.lg || 0) + (b.lga || 0),
    gwg: (b) => b.gwg, uag: (b) => b.uag, pim: (b) => b.pim, shots: (b) => b.sog, fow: (b) => b.fow,
    hits: (b) => b.hits || 0, blocks: (b) => b.blocks || 0, fol: (b) => b.fol || 0,
    psg: (b) => b.psg || 0, psa: (b) => b.psa || 0, psm: (b) => Math.max(0, (b.psa || 0) - (b.psg || 0)),
    psPct: (b) => ((b.psa || 0) ? _pct(((b.psg || 0) / b.psa) * 100) : "—"),
    fg: (b) => b.fg || 0, lg: (b) => b.lg || 0,
    foPct: (b) => ((b.fow + b.fol) ? _pct((b.fow / (b.fow + b.fol)) * 100) : "—"),
    fo: (b) => `${b.fow || 0}/${(b.fow || 0) + (b.fol || 0)}`,
    ps: (b) => `${b.psg || 0}/${b.psa || 0}`,
    shPct: (b) => (b.sog ? _pct((b.g / b.sog) * 100) : "—"),
};
// Merge a player's per-period buckets for the active period toggle. "regulation"
// sums P1-3; specific periods read their own bucket.
export function periodBucket(r, period) {
    const per = r.per || {};
    if (period === "regulation") {
        const out = { g: 0, a: 0, a1: 0, a2: 0, evg: 0, ppg: 0, shg: 0, eng: 0, psg: 0, psa: 0, eva: 0, ppa: 0, sha: 0, ena: 0, gwg: 0, uag: 0, pim: 0, sog: 0, fow: 0, fol: 0, hits: 0, blocks: 0, fg: 0, lg: 0, gwga: 0, fga: 0, lga: 0 };
        [1, 2, 3].forEach((p) => { const b = per[p]; if (b) for (const k in out) out[k] += b[k] || 0; });
        return out;
    }
    return per[PERIOD_NUM[period]] || _EB;
}
// Period membership for a play-by-play period number under the active toggle.
const PERIOD_SETS = { regulation: [1, 2, 3], p1: [1], p2: [2], p3: [3], ot: [4], so: [5], ot1: [4], ot2: [5], ot3: [6] };
const inPeriod = (period, p) => (period === "combined" ? true : (PERIOD_SETS[period] || []).includes(Number(p) || 1));
// Sum a goalie's per-period SA/GA over the periods the active toggle covers.
const goaliePeriodSum = (gl, period) => (PERIOD_SETS[period] || []).reduce((o, pn) => { const x = (gl.periods || {})[pn] || {}; o.sa += x.sa || 0; o.ga += x.ga || 0; return o; }, { sa: 0, ga: 0 });
// Master stat list — admins pick/reorder/rename from these for the Insights tabs.
// fn = optional accessor/formatter; width = default column width (px).
// Per-period bucket readers: _perSum totals periods 1-4 (OT included, SO out);
// _perOT reads the OT bucket (period 4). Used by the OT/GWG/FG/LG assist+point
// triplets which derive from the canonical `per` bucket.
const _perSum = (r, k) => { const p = r.per || {}; return (Number((p[1] || p["1"] || {})[k]) || 0) + (Number((p[2] || p["2"] || {})[k]) || 0) + (Number((p[3] || p["3"] || {})[k]) || 0) + (Number((p[4] || p["4"] || {})[k]) || 0); };
const _perOT = (r, k) => { const p = r.per || {}; return Number((p[4] || p["4"] || {})[k]) || 0; };
export const MASTER_STATS = {
    g: { label: "G", fn: (r) => r.g || 0 }, a: { label: "A", fn: (r) => r.a || 0 }, p: { label: "P", fn: (r) => r.p || 0 }, pim: { label: "PIM", fn: (r) => r.pim || 0 },
    shots: { label: "Shots", fn: (r) => r.shots || r.sog || 0, width: 48 },
    toi: { label: "TOI", fn: (r) => r.toi || "—", width: 52 },
    fo: { label: "FO", fn: (r) => `${r.fow || 0}/${(r.fow || 0) + (r.fol || 0)}`, width: 56 },
    ps: { label: "PS", fn: (r) => `${r.psg || 0}/${r.psa || 0}`, width: 50 },
    hits: { label: "H", fn: (r) => r.hits || 0, width: 40 },
    plus_minus: { label: "+/-", fn: _pm, width: 46 },
    a1: { label: "A1", fn: (r) => r.a1 || 0 }, a2: { label: "A2", fn: (r) => r.a2 || 0 }, hat: { label: "HAT", fn: (r) => (r.hat != null ? Number(r.hat) || 0 : ((r.g || 0) >= 3 ? 1 : 0)) }, eng: { label: "ENG", fn: (r) => r.eng || 0 }, gwg: { label: "GWG", fn: (r) => r.gwg || 0 },
    fg: { label: "FG", fn: (r) => (r.fg || (r.firstGoal ? 1 : 0)) },
    lg: { label: "LG", fn: (r) => (r.lg || (r.lastGoal ? 1 : 0)) },
    evp: { label: "EVP", fn: (r) => r.evp || 0 }, evg: { label: "EVG", fn: (r) => r.evg || 0 }, eva: { label: "EVA", fn: (r) => r.eva || 0 },
    ppp: { label: "PPP", fn: (r) => r.ppp || 0 }, ppg: { label: "PPG", fn: (r) => r.ppg || 0 }, ppa: { label: "PPA", fn: (r) => r.ppa || 0 },
    shp: { label: "SHP", fn: (r) => r.shp || 0 }, shg: { label: "SHG", fn: (r) => r.shg || 0 }, sha: { label: "SHA", fn: (r) => r.sha || 0 },
    blocks: { label: "B", fn: (r) => r.blocks || 0, width: 40 }, fow: { label: "FOW", fn: (r) => r.fow || 0 }, fol: { label: "FOL", fn: (r) => r.fol || 0 }, foa: { label: "FOA", fn: (r) => (r.fow || 0) + (r.fol || 0) },
    foa: { label: "FOA", fn: (r) => (Number(r.fow) || 0) + (Number(r.fol) || 0), width: 48 },
    sogw: { label: "SOWG", fn: (r) => Number(r.sowg) || 0, width: 56 },
    foPct: { label: "FO%", fn: (r) => (r.foa ? _pct(r.foPct) : "—"), width: 52 },
    g_p1: { label: "G1", fn: (r) => r.g_p1 || 0 },
    g_p2: { label: "G2", fn: (r) => r.g_p2 || 0 },
    g_p3: { label: "G3", fn: (r) => r.g_p3 || 0 },
    g_ot: { label: "OTG", fn: (r) => r.g_ot || 0 },
    g_so: { label: "SOG", fn: (r) => r.g_so || 0 },
    p_p1: { label: "P", fn: (r) => (r.g_p1 || 0) + (r.a_p1 || 0) },
    p_p2: { label: "P", fn: (r) => (r.g_p2 || 0) + (r.a_p2 || 0) },
    p_p3: { label: "P", fn: (r) => (r.g_p3 || 0) + (r.a_p3 || 0) },
    p_ot: { label: "P", fn: (r) => (r.g_ot || 0) + (r.a_ot || 0) },
    shPct: { label: "SH%", fn: (r) => (r.shots ? _pct((r.g / r.shots) * 100) : "—"), width: 52 },
    shPct_p1: { label: "SH%", fn: (r) => (r.sog_p1 ? _pct((r.g_p1 / r.sog_p1) * 100) : "—"), width: 52 },
    shPct_p2: { label: "SH%", fn: (r) => (r.sog_p2 ? _pct((r.g_p2 / r.sog_p2) * 100) : "—"), width: 52 },
    shPct_p3: { label: "SH%", fn: (r) => (r.sog_p3 ? _pct((r.g_p3 / r.sog_p3) * 100) : "—"), width: 52 },
    ena: { label: "ENA", fn: (r) => r.ena || 0 },
    a_p1: { label: "A1", fn: (r) => r.a_p1 || 0 },
    a_p2: { label: "A2", fn: (r) => r.a_p2 || 0 },
    a_p3: { label: "A3", fn: (r) => r.a_p3 || 0 },
    a_ot: { label: "AOT", fn: (r) => r.a_ot || 0 },
    sog_p1: { label: "S1", fn: (r) => r.sog_p1 || 0 },
    sog_p2: { label: "S2", fn: (r) => r.sog_p2 || 0 },
    sog_p3: { label: "S3", fn: (r) => r.sog_p3 || 0 },
    fow_p1: { label: "FO1", fn: (r) => r.fow_p1 || 0, width: 48 },
    fow_p2: { label: "FO2", fn: (r) => r.fow_p2 || 0, width: 48 },
    fow_p3: { label: "FO3", fn: (r) => r.fow_p3 || 0, width: 48 },
    uag: { label: "UAG", fn: (r) => r.uag || 0 },
    enp: { label: "ENP", fn: (r) => (r.eng || 0) + (r.ena || 0) },
    psg: { label: "PSG", fn: (r) => r.psg || 0, width: 46 },
    psa: { label: "PSA", fn: (r) => r.psa || 0, width: 46 },
    psm: { label: "PSM", fn: (r) => r.psm || 0, width: 46 },
    psPct: { label: "PS%", fn: (r) => { const a = _perSum(r, "psa"); return a ? _pct((_perSum(r, "psg") / a) * 100) : "—"; }, width: 52 },
    soAtt: { label: "SOA", fn: (r) => r.soAtt || 0, width: 46 },
    soGoal: { label: "SOG", fn: (r) => r.soGoal || 0, width: 46 },
    soMiss: { label: "SOM", fn: (r) => r.soMiss || 0, width: 46 },
    soPct: { label: "SO%", fn: (r) => (r.soAtt ? _pct((r.soGoal / r.soAtt) * 100) : "—"), width: 52 },
    // ---- Insights triplets (Points | Goals | Assists), derived from `per` ----
    // Overtime (period 4 bucket)
    otp: { label: "OTP", fn: (r) => _perOT(r, "g") + _perOT(r, "a"), width: 46 },
    otg: { label: "OTG", fn: (r) => _perOT(r, "g"), width: 46 },
    ota: { label: "OTA", fn: (r) => _perOT(r, "a"), width: 46 },
    // Game-winning (goal reuses flat gwg; assists/points from `per`)
    gwgp: { label: "GWGP", fn: (r) => (Number(r.gwg) || 0) + _perSum(r, "gwga"), width: 52 },
    gwga: { label: "GWGA", fn: (r) => _perSum(r, "gwga"), width: 52 },
    // First-goal-of-game
    fgp: { label: "FGP", fn: (r) => (Number(r.fg) || (r.firstGoal ? 1 : 0)) + _perSum(r, "fga"), width: 46 },
    fga: { label: "FGA", fn: (r) => _perSum(r, "fga"), width: 46 },
    // Last-goal-of-game
    lgp: { label: "LGP", fn: (r) => (Number(r.lg) || (r.lastGoal ? 1 : 0)) + _perSum(r, "lga"), width: 46 },
    lga: { label: "LGA", fn: (r) => _perSum(r, "lga"), width: 46 },
    // Penalty shots TAKEN — Goals / Attempts (a made PS is also a real goal; this
    // is a separate tracking stat). Shootout is NOT a penalty shot.
    ps: { label: "PS", fn: (r) => { const a = _perSum(r, "psa"); return a ? `${_perSum(r, "psg")}/${a}` : "—"; }, width: 54 },
    psg: { label: "PSG", fn: (r) => _perSum(r, "psg"), width: 46 },
    psa: { label: "PSA", fn: (r) => _perSum(r, "psa"), width: 46 },
};
// Human-readable tooltips per stat label — shown on hover of any column header
// (public + admin). Keyed by the column's display label.
export const STAT_TIPS = {
    G: "Goals", A: "Assists", P: "Points (G + A)", PIM: "Penalty Minutes",
    Shots: "Shots on goal", Sh: "Shots on goal", TOI: "Time on Ice", FO: "Faceoffs won / taken",
    Hits: "Hits", H: "Hits", B: "Blocks", "+/-": "Plus / Minus", BLK: "Blocked Shots", Blocks: "Blocks",
    "FO%": "Faceoff Win %", "SH%": "Shooting %",
    A1: "Primary (first) assists", A2: "Secondary assists", "1A": "Primary assists", "2A": "Secondary assists", HAT: "Hat trick (3+ goals)",
    FG: "First goal of the game", LG: "Last goal of the game", GWG: "Game-winning goals",
    EVP: "Even-strength points", ESP: "Even-strength points", EVG: "Even-strength goals",
    ESA: "Even-strength assists", EVA: "Even-strength assists",
    PPP: "Power-play points", PPG: "Power-play goals", PPA: "Power-play assists",
    SHP: "Short-handed points", SHG: "Short-handed goals", SHA: "Short-handed assists",
    ENG: "Empty-net goals", ENA: "Empty-net assists", ENP: "Empty-net points",
    OTG: "Overtime goals", OTA: "Overtime assists", OTP: "Overtime points (G + A)",
    GWGA: "Assists on game-winning goals", GWGP: "Game-winning points (GWG + assists)",
    FGA: "Assists on the first goal of the game", FGP: "First-goal points (FG + assists)",
    LGA: "Assists on the last goal of the game", LGP: "Last-goal points (LG + assists)",
    PS: "Penalty shots taken — goals / attempts (a made PS is also a real goal)", PSG: "Penalty shot goals", PSA: "Penalty shot attempts",
    SOG: "Shootout goals", UAG: "Unassisted goals",
    SOA: "Shootout attempts", SOM: "Shootout misses", "SO%": "Shootout save % (saves ÷ shots against)",
    FOW: "Faceoffs won", FOL: "Faceoffs lost", "FO%": "Faceoff win %",
    "SH%": "Shooting % (goals / shots)", "SV%": "Save %", SV: "Saves", SA: "Shots against",
    GA: "Goals against", SO: "Shutouts", Dec: "Decision (W / L / OTL)",
    PSG: "Penalty-shot goals", PSA: "Penalty-shot attempts", PSM: "Penalty-shot misses", "PS%": "Penalty-shot %", PS: "Penalty shots — goals / attempts", FOA: "Faceoff attempts (won + lost)",
    GF: "Goals for", SF: "Shots for", PPO: "Power-play opportunities", "PP%": "Power-play %",
    PPGA: "Power-play goals against", "PK%": "Penalty-kill %", SHGA: "Short-handed goals against", Blocks: "Blocked shots",
};
// Player Stats & Insights structure: categories with nested sub-groups.
// "Periods" is a Combined | P1 | P2 | P3 | OT | SO toggle (period-splittable
// columns remap live). Only stats present in the PWHL feed are included.
const INSIGHTS_VIEWS = [
    { id: "main", label: "Main Stats", groups: [
        { id: "basic", label: "Basic", cols: [{ key: "p" }, { key: "g" }, { key: "a" }, { key: "pim" }, { key: "toi" }, { key: "plus_minus" }, { key: "hits" }, { key: "blocks" }, { key: "fow" }, { key: "fol" }, { key: "foPct" }, { key: "shots" }, { key: "shPct" }] },
    ] },
    { id: "offence", label: "Offence", groups: [
        { id: "points", label: "Points", cols: [{ key: "p" }, { key: "evp", label: "ESP" }, { key: "ppp" }, { key: "shp" }, { key: "enp" }] },
        { id: "goals", label: "Goals", cols: [{ key: "g" }, { key: "fg" }, { key: "lg" }, { key: "gwg" }, { key: "evg" }, { key: "ppg" }, { key: "shg" }, { key: "eng" }, { key: "g_ot", label: "OTG" }, { key: "uag" }] },
        { id: "assists", label: "Assists", cols: [{ key: "a" }, { key: "a1", label: "1A" }, { key: "a2", label: "2A" }, { key: "eva", label: "ESA" }, { key: "ppa" }, { key: "sha" }, { key: "ena" }, { key: "a_ot", label: "OTA" }] },
        { id: "shooting", label: "Shooting", cols: [{ key: "shots", label: "Sh" }, { key: "shPct" }, { key: "psg" }, { key: "psa" }, { key: "psm" }, { key: "psPct" }] },
    ] },
    { id: "physical", label: "Physical", groups: [
        { id: "physical", label: "Physical", cols: [{ key: "hits" }, { key: "pim" }, { key: "toi" }, { key: "plus_minus" }, { key: "blocks", label: "BLK" }] },
        { id: "faceoffs", label: "Faceoffs", cols: [{ key: "fow" }, { key: "fol" }, { key: "foPct" }] },
    ] },
    { id: "goalie", label: "Goaltending", type: "goalie" },
];
// Flattened, single-row leaf tabs (categories + sub-groups combined, always
// shown). Shootout lives on the SO period toggle, not as its own tab.
const DEFAULT_INSIGHTS_GROUPS = INSIGHTS_VIEWS.flatMap((v) =>
    v.type === "goalie"
        ? [{ id: v.id, label: v.label, type: "goalie", cols: [] }]
        : (v.groups || []).map((g) => ({ id: `${v.id}:${g.id}`, label: g.id === "basic" ? "Main" : g.label, cols: g.cols.map((c) => ({ key: c.key, ...(c.label ? { label: c.label } : {}), ...(c.width ? { width: c.width } : {}) })) })));
// Turn a saved/default group config into the render tabs.
const buildInsTabs = (groups) => (groups || []).map((g) => (g.type === "goalie" ? { id: g.id, label: g.label, type: "goalie" } : { id: g.id, label: g.label, cols: g.cols || [] }));
const INSIGHTS_TABS = buildInsTabs(DEFAULT_INSIGHTS_GROUPS);
// Period toggles — each a distinct colour; "so" only rendered when a shootout
// happened. `p` maps a toggle to the play-by-play period number.
export const PERIOD_META = {
    combined: { color: "#38BDF8" }, regulation: { color: "#38BDF8" }, p1: { color: "#5BD1B7" }, p2: { color: "#F6C453" },
    p3: { color: "#FB7185" }, ot: { color: "#9D4CDD" }, so: { color: "#F472B6" },
    ot1: { color: "#9D4CDD" }, ot2: { color: "#C084FC" }, ot3: { color: "#E9D5FF" },
};
export const PERIOD_TOGGLES = [
    { k: "combined", label: "Combined", p: 0 }, { k: "regulation", label: "Regulation", p: -1 }, { k: "p1", label: "P1", p: 1 },
    { k: "p2", label: "P2", p: 2 }, { k: "p3", label: "P3", p: 3 },
    { k: "ot", label: "OT", p: 4 }, { k: "so", label: "SO", p: 5 },
];
// Playoffs go to multiple sudden-death OT periods (no shootout) — OT1/OT2/OT3
// read per-period buckets 4/5/6. Used only when the Stage toggle is "playoffs".
export const PERIOD_TOGGLES_PLAYOFF = [
    { k: "combined", label: "Combined", p: 0 }, { k: "regulation", label: "Regulation", p: -1 }, { k: "p1", label: "P1", p: 1 },
    { k: "p2", label: "P2", p: 2 }, { k: "p3", label: "P3", p: 3 },
    { k: "ot1", label: "OT1", p: 4 }, { k: "ot2", label: "OT2", p: 5 }, { k: "ot3", label: "OT3", p: 6 },
];
// Resolve a tab's stored cols into render-ready objects. When a specific period
// is active, period-splittable columns are remapped to that period's value and
// flagged `affected` so the table can tint just those columns (the view — its
// column set — never changes).
const resolveCols = (tab, period = "combined") => (tab?.cols || []).map((c) => {
    const base = MASTER_STATS[c.key];
    if (!base) return null;
    // Period selected + this stat is periodizable → read from the player's
    // per-period bucket (regulation sums P1-3) and flag it as affected (tinted).
    if (period !== "combined" && PERIODIZED[c.key]) {
        const rd = PERIODIZED[c.key];
        return { key: `${c.key}_${period}`, baseKey: c.key, label: c.label || base.label || c.key, width: c.width || base.width || 44, fn: (r) => rd(periodBucket(r, period)), affected: true };
    }
    return { key: c.key, baseKey: c.key, label: c.label || base.label || c.key, width: c.width || base.width || 44, fn: base.fn, affected: false };
}).filter(Boolean);
// Fixed shootout column set — shown for EVERY player-stats tab when the SO period
// toggle is active (shootout stats don't vary by the normal category tabs).
const SO_SKATER_COLS = ["soAtt", "soGoal", "soMiss", "soPct", "sogw"].map((k) => ({
    key: k, baseKey: k, label: MASTER_STATS[k].label, width: MASTER_STATS[k].width || 46, fn: MASTER_STATS[k].fn, affected: true, sec: "shootout",
}));
export { SO_SKATER_COLS };
// Player Summary column sections (icon + title) and per-tab column sets.
// Three toggles: Main · Special Teams · Insights.
export const PS_SECTIONS = {
    info: { title: "Player Info", Icon: Users },
    scoring: { title: "Scoring", Icon: Star },
    impact: { title: "Impact", Icon: Activity },
    physical: { title: "Physical", Icon: Shield },
    faceoffs: { title: "Faceoffs", Icon: CircleDot },
    time: { title: "Time", Icon: Timer },
    shooting: { title: "Shooting", Icon: Crosshair },
    evenstrength: { title: "Even Strength", Icon: CircleDot },
    powerplay: { title: "Power Play", Icon: Zap },
    shorthanded: { title: "Short Handed", Icon: ShieldAlert },
    penaltyshot: { title: "Penalty Shot", Icon: Goal },
    insights: { title: "Insights", Icon: Flag },
    shootout: { title: "Shootout", Icon: Goal },
};
export const PS_TABS = [
    { id: "all", label: "Stats" },
];
export const PS_COLS = {
    main: [["p", "scoring"], ["g", "scoring"], ["a", "scoring"], ["plus_minus", "impact"], ["pim", "impact"], ["hits", "physical"], ["blocks", "physical"], ["fo", "faceoffs"], ["foPct", "faceoffs"], ["toi", "time"], ["shots", "shooting"], ["shPct", "shooting"]],
    // Merged "Special Teams and Insights" — special-teams columns (incl. the single
    // PS = goals/attempts pair) followed by every insight. PS appears ONCE.
    special_insights: [
        ["evp", "evenstrength"], ["evg", "evenstrength"], ["eva", "evenstrength"],
        ["ppp", "powerplay"], ["ppg", "powerplay"], ["ppa", "powerplay"],
        ["shp", "shorthanded"], ["shg", "shorthanded"], ["sha", "shorthanded"],
        ["ps", "penaltyshot"], ["psPct", "penaltyshot"],
        ["hat", "insights"], ["a1", "insights"], ["a2", "insights"], ["uag", "insights"],
        ["otp", "insights"], ["otg", "insights"], ["ota", "insights"],
        ["enp", "insights"], ["eng", "insights"], ["ena", "insights"],
        ["gwgp", "insights"], ["gwg", "insights"], ["gwga", "insights"],
        ["fgp", "insights"], ["fg", "insights"], ["fga", "insights"],
        ["lgp", "insights"], ["lg", "insights"], ["lga", "insights"]],
};
// Merged single stat-type: Main + Special Teams and Insights shown together everywhere.
PS_COLS.all = [...PS_COLS.main, ...PS_COLS.special_insights];
const PS_PERIOD_CAPABLE = new Set(["p", "g", "a", "a1", "a2", "pim", "shots", "shPct", "hits", "blocks", "fow", "fo", "ps", "evp", "ppp", "shp", "evg", "ppg", "shg", "eva", "ppa", "sha", "gwg"]);
// Build Player-Summary columns. When a real period is selected, every truly
// per-period stat is remapped to its per-period value (from the player's `per`
// bucket) AND tinted — so the numbers change and sort by that period.
export function buildPsCols(tabIds, period = "combined") {
    return buildPsColsFromPairs(tabIds.flatMap((id) => PS_COLS[id] || []), period);
}
// Same as buildPsCols but from an explicit [ [statKey, groupId], ... ] list — used
// by the owner-configurable Leaderboards layout (custom views/groups/columns).
export function buildPsColsFromPairs(pairs, period = "combined") {
    const active = period !== "combined";
    const seen = new Set();
    const p2 = (pairs || []).filter(([k]) => (seen.has(k) ? false : seen.add(k)));
    return p2.map(([k, sec]) => {
        const base = MASTER_STATS[k];
        if (!base) return null;
        if (active && PERIODIZED[k]) {
            return { key: `${k}_${period}`, baseKey: k, label: base.label || k, width: base.width || 44, fn: (r) => PERIODIZED[k](periodBucket(r, period)), affected: true, sec };
        }
        return { key: k, baseKey: k, label: base.label || k, width: base.width || 44, fn: base.fn, affected: active && PS_PERIOD_CAPABLE.has(k), sec };
    }).filter((c) => c && c.fn);
}
// Player-Summary columns per statistic category. "All"/multi selection unions
// them (in canonical order); period-capable columns get the pill tint.
const CAT_COLS = {
    goal: ["g", "a", "p", "ppg", "shg", "gwg"],
    penalty: ["pim"],
    shot: ["shots", "shPct"],
    faceoff: ["fow", "fol", "foPct"],
    goalie_change: [],
};
const CAT_COL_ORDER = ["g", "a", "p", "ppg", "shg", "gwg", "shots", "shPct", "pim", "fow", "fol", "foPct"];
const CAT_PERIOD_CAPABLE = new Set(["g", "a", "p", "shots", "shPct", "pim", "fow", "fol"]);
function buildCatCols(activeCats, periodActive) {
    let keys = [...new Set(activeCats.flatMap((c) => CAT_COLS[c] || []))];
    if (!keys.length) keys = CAT_COLS.goal;   // never render an empty table
    return CAT_COL_ORDER.filter((k) => keys.includes(k)).map((k) => ({
        key: k, baseKey: k, label: MASTER_STATS[k].label, width: MASTER_STATS[k].width || 44,
        fn: MASTER_STATS[k].fn, affected: periodActive && CAT_PERIOD_CAPABLE.has(k),
    }));
}
// Tiny per-column admin control (shown on the real table header in admin view):
// move ◀ ▶, shrink −, expand +, remove ✕. Persists to the global layout.
function ColMenu({ colKey, groupId, onAction }) {
    const [open, setOpen] = useState(false);
    const btn = "p-1 rounded hover:bg-white/15 text-white/70 hover:text-white";
    return (
        <span className="relative inline-flex align-middle ml-0.5" onClick={(e) => e.stopPropagation()}>
            <button type="button" data-testid={`pgc-colmenu-${groupId}-${colKey}`} onClick={() => setOpen((v) => !v)}
                className="p-0.5 rounded text-[#64748B] hover:text-[#9D4CDD]" title="Column options"><Settings2 className="h-3 w-3" /></button>
            {open && (
                <span className="absolute z-30 top-full right-0 mt-1 flex items-center gap-0.5 rounded-md border border-[#2A2F45] bg-[#12141F] p-1 shadow-xl normal-case">
                    <button type="button" className={btn} title="Move left" onClick={() => { onAction(groupId, colKey, "left"); }}><ArrowLeft className="h-3 w-3" /></button>
                    <button type="button" className={btn} title="Move right" onClick={() => { onAction(groupId, colKey, "right"); }}><ArrowRight className="h-3 w-3" /></button>
                    <button type="button" className={btn} title="Shrink" onClick={() => onAction(groupId, colKey, "shrink")}><Minus className="h-3 w-3" /></button>
                    <button type="button" className={btn} title="Expand" onClick={() => onAction(groupId, colKey, "expand")}><Plus className="h-3 w-3" /></button>
                    <button type="button" className={btn} title="Move to new section" onClick={() => { onAction(groupId, colKey, "new_section"); setOpen(false); }}><ClipboardList className="h-3 w-3" /></button>
                    <button type="button" className={`${btn} hover:text-red-400`} title="Remove" onClick={() => { onAction(groupId, colKey, "remove"); setOpen(false); }}><X className="h-3 w-3" /></button>
                </span>
            )}
        </span>
    );
}
function InsightsTable({ rows, matchMap, cols, badges = {}, periodColor = null, admin = false, groupId = null, onColAction = null, testId = "pgc-insights-table", soMode = false, photoFor = null, metaFor = null, starMap = {} }) {
    const hl = useHl();
    const hasStars = starMap && Object.keys(starMap).length > 0;
    const dragFrom = useRef(null);
    const [overIdx, setOverIdx] = useState(null);
    const [sort, setSort] = useState({ key: null, dir: -1 });
    if (!rows.length) return <Empty>{soMode ? "No shootout attempts." : "No skaters."}</Empty>;
    const numOf = (r, c) => { const v = c.fn ? c.fn(r) : r[c.key]; const n = parseFloat(String(v).replace(/[^0-9.\-]/g, "")); return isNaN(n) ? -Infinity : n; };
    const sortCol = sort.key ? cols.find((c) => (c.baseKey || c.key) === sort.key) : null;
    const sorted = soMode
        ? [...rows].sort((a, b) => (b.soGoal || 0) - (a.soGoal || 0) || (b.soAtt || 0) - (a.soAtt || 0))
        : sortCol
            ? [...rows].sort((a, b) => (numOf(a, sortCol) - numOf(b, sortCol)) * sort.dir || (b.p || 0) - (a.p || 0))
            : [...rows].sort((a, b) => (Number(a.player?.jersey) || 9999) - (Number(b.player?.jersey) || 9999) || (b.p || 0) - (a.p || 0));
    const toggleSort = (c) => setSort((s) => { const k = c.baseKey || c.key; return s.key === k ? (s.dir === -1 ? { key: k, dir: 1 } : { key: null, dir: -1 }) : { key: k, dir: -1 }; });
    const headStyle = (c) => (periodColor && c.affected ? { color: periodColor, backgroundColor: `${periodColor}1A`, boxShadow: `inset 0 -2px 0 ${periodColor}` } : undefined);
    const canDnd = admin && onColAction;
    const dropOn = (ci) => { const from = dragFrom.current; if (from != null && from !== ci) onColAction(groupId, cols[from].baseKey || cols[from].key, "reorder", ci); dragFrom.current = null; setOverIdx(null); };
    // Grouped section super-headers (icon + title) computed from each column's `.sec`.
    const showSecs = cols.some((c) => c.sec && PS_SECTIONS[c.sec]);
    const secGroups = [];
    if (showSecs) {
        cols.forEach((c) => {
            const sec = c.sec || "_";
            const last = secGroups[secGroups.length - 1];
            if (last && last.sec === sec) last.span += 1;
            else secGroups.push({ sec, span: 1 });
        });
    }
    // Player-info columns stay DARK (#0A0B10); every stat column uses the GREY
    // column background. Only the column background changes — the stat pill boxes
    // keep their own (BOX_BASE) design.
    let _lastSec = null;
    const colDiv = cols.map((c) => { const sec = c.sec || "_"; const isNew = _lastSec === null || sec !== _lastSec; _lastSec = sec; return isNew; });
    const colBg = cols.map(() => "bg-[#0E0F16]");
    const DIV = "border-l border-white/[0.07]";
    const LEAD_BG = "bg-[#0A0B10]";
    // Team-colour highlight mode (admin) — every stat box takes the team's crest
    // colour with strip-like opacity.
    const teamMode = hl === "team";
    const teamCol = teamColor(rows[0]?.team_id) || "#9D4CDD";
    const _tableMinW = 24 + (photoFor ? 42 : 0) + 30 + 124 + 34 + 42 + 34 + (hasStars ? 34 : 0) + cols.reduce((s, c) => s + (c.width || 40), 0);
    const CELL = "bg-[#111219] group-hover:bg-[#191b27] transition-colors border-y border-white/[0.07]";
    return (
        <div className="rounded-2xl bg-[#0E0F16] border border-white/10 shadow-xl shadow-black/40 p-1 sm:p-2.5 overflow-x-auto" data-testid={`${testId}-wrap`}>
            <table className="text-[11px] sm:text-sm border-separate" data-testid={testId} style={{ borderSpacing: "0 8px", tableLayout: "fixed", width: "100%", minWidth: _tableMinW }}>
                <colgroup>
                    {photoFor && <col style={{ width: 42 }} />}
                    <col style={{ width: 30 }} />
                    <col style={{ width: 124 }} />
                    <col style={{ width: 34 }} />
                    <col style={{ width: 42 }} />
                    <col style={{ width: 34 }} />
                    {hasStars && <col style={{ width: 34 }} />}
                    {cols.map((c, ci) => <col key={c.key + ci} style={{ width: c.width || 40 }} />)}
                </colgroup>
                <thead>
                    {showSecs && (
                        <tr className="text-white/45 text-[9px] sm:text-[10px] uppercase tracking-[0.14em]">
                            <th colSpan={(photoFor ? 6 : 5) + (hasStars ? 1 : 0)} className="sticky left-0 z-20 text-center px-1 pt-1 pb-1.5" style={{ background: "#0E0F16" }}>
                                <span className="inline-flex items-center justify-center gap-1 text-[#8B93A7] font-bold whitespace-nowrap"><Users className="h-3 w-3" />Player Info</span>
                            </th>
                            {secGroups.map((grp, gi) => {
                                const meta = PS_SECTIONS[grp.sec];
                                const Icon = meta?.Icon;
                                return (
                                    <th key={grp.sec + gi} colSpan={grp.span} className="text-center px-1 pt-1 pb-1.5">
                                        {meta && <span className="inline-flex items-center gap-1 text-[#8B93A7] font-bold whitespace-nowrap">{Icon && <Icon className="h-3 w-3" />}{meta.title}</span>}
                                    </th>
                                );
                            })}
                        </tr>
                    )}
                    <tr className="text-white/35 text-[9px] sm:text-[10px] uppercase tracking-[0.18em]">
                    {photoFor && <th className="text-center py-1 font-bold"></th>}
                    <th className="text-center py-1 font-bold">#</th>
                    <th className="text-left px-1 font-bold">Player</th>
                    <th className="text-center font-bold">Pos</th>
                    <th className="text-center font-bold" title="Opening Lineup">OL</th><th className="text-center font-bold" title="Captain / Alternate">C/A</th>
                    {hasStars && <th className="text-center font-bold" title="Three Stars of the Game">★</th>}
                    {cols.map((c, ci) => {
                        const sk = c.baseKey || c.key;
                        const sortArrow = sort.key === sk ? (sort.dir === -1 ? " ↓" : " ↑") : "";
                        return (
                        <th key={c.key + ci} className={`text-center px-1 py-1 select-none font-bold ${canDnd ? "cursor-grab" : "cursor-pointer"} ${overIdx === ci ? "ring-1 ring-[#9D4CDD]/70 rounded" : ""} ${sort.key === sk ? "text-[#9D4CDD]" : "hover:text-white"}`} style={periodColor && c.affected ? { color: periodColor } : undefined}
                            draggable={canDnd} data-testid={`pgc-col-${groupId}-${sk}`}
                            onClick={() => toggleSort(c)}
                            onDragStart={canDnd ? () => { dragFrom.current = ci; } : undefined}
                            onDragOver={canDnd ? (e) => { e.preventDefault(); setOverIdx(ci); } : undefined}
                            onDrop={canDnd ? () => dropOn(ci) : undefined}
                            onDragEnd={canDnd ? () => { dragFrom.current = null; setOverIdx(null); } : undefined}>
                            <HoverCard title={titleCase(STAT_TIPS[c.label] || c.label)} className="items-center justify-center">{c.label}{sortArrow}{admin && onColAction && <ColMenu colKey={sk} groupId={groupId} onAction={onColAction} />}</HoverCard>
                        </th>
                        );
                    })}
                </tr></thead>
                <tbody>
                    {sorted.map((r, i) => (
                        <tr key={i} className="group" data-testid={`pgc-insights-row-${r._id}`}>
                            {photoFor && (
                                <td className={`${CELL} rounded-l-2xl border-l text-center py-2`}>
                                    <span className="inline-flex justify-center w-full"><PlayerAvatar photo={photoFor(r.player?.pwhl_id)} name={r.player?.name} playerId={matchMap[r.player?.pwhl_id]} size="xs" showTierRing={false} /></span>
                                </td>
                            )}
                            <td className={`${CELL} ${photoFor ? "" : "rounded-l-2xl border-l"} py-2 font-mono-stat text-[#64748B] text-center`}>{r.player?.jersey}</td>
                            <td className={`${CELL} stat-emph text-white truncate px-1 py-2`}>
                                <span className="inline-flex items-center gap-1.5 max-w-full">
                                    <HoverInfo meta={metaFor ? metaFor(r.player?.pwhl_id) : {}} name={r.player?.name} className="items-center gap-1.5">
                                        <PName person={r.player} matchMap={matchMap} />
                                    </HoverInfo>
                                    {(badges[r._id] || []).map((code) => <GameBadge key={code} code={code} />)}
                                </span>
                            </td>
                            <td className={`${CELL} text-center text-[#94A3B8] text-[11px]`}>{r.player?.position || r.position || "—"}</td>
                            <td className={`${CELL} text-center`}>{r.starter ? <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#9D4CDD]/25 text-[#E9D5FF]" title="Opening Lineup" data-testid={`pgc-starter-${r.player?.pwhl_id}`}>OL</span> : <span className="text-[#3A3F55]">—</span>}</td>
                            <td className={`${CELL} text-center`}>
                                {r.captain === "C" ? <span className="text-[9px] font-black px-1 py-0.5 rounded bg-[#F6C453]/25 text-[#F6C453] border border-[#F6C453]/40" title="Captain" data-testid={`pgc-insights-captain-${r.player?.pwhl_id}`}>C</span>
                                    : r.captain === "A" ? <span className="text-[9px] font-black px-1 py-0.5 rounded bg-white/10 text-[#CBD5E1] border border-white/20" title="Alternate Captain" data-testid={`pgc-insights-alt-${r.player?.pwhl_id}`}>A</span>
                                        : <span className="text-[#3A3F55]">—</span>}
                            </td>
                            {hasStars && (
                                <td className={`${CELL} text-center`}>
                                    {starMap[r.player?.pwhl_id] ? (
                                        <span className="inline-flex items-center gap-0.5 text-[9px] font-black px-1 py-0.5 rounded bg-[#FBBF24]/20 text-[#FBBF24] border border-[#FBBF24]/40" title={`Star ${starMap[r.player.pwhl_id]}`} data-testid={`pgc-insights-star-${r.player?.pwhl_id}`}>
                                            <Star size={9} fill="#FBBF24" style={{ color: "#FBBF24" }} />{starMap[r.player.pwhl_id]}
                                        </span>
                                    ) : <span className="text-[#3A3F55]">—</span>}
                                </td>
                            )}
                            {cols.map((c, ci) => {
                                const val = c.fn ? c.fn(r) : (r[c.key] ?? 0);
                                const dim = val === "—" || val === 0 || val === "0" || val == null;
                                const lit = !!(periodColor && c.affected) && (hl === "all" || !dim);
                                const isLast = ci === cols.length - 1;
                                return (
                                    <td key={c.key + ci} className={`${CELL} text-center px-1 py-2 ${isLast ? "rounded-r-2xl border-r" : ""}`}>
                                        <StatBlock value={val} color={lit ? periodColor : null} />
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ===================== Penalties tab =====================
// Penalties tab — count, total PIM, and up to 3 detailed penalty breakdowns.
function fmtPenalty(p) {
    const mins = p.minutes || 0;
    const when = mins >= 10 ? "Game Misconduct" : `P${p.period || "?"} ${p.time || ""}`.trim();
    return `${p.infraction || "Penalty"} (${mins}) - ${when}`;
}
function PenaltyTable({ rows, matchMap }) {
    const withPens = rows.filter((r) => (r.pens || []).length > 0);
    if (!withPens.length) return <Empty>No penalties.</Empty>;
    const sorted = [...withPens].sort((a, b) => b.pens.length - a.pens.length || (b.pim || 0) - (a.pim || 0));
    return (
        <div className="overflow-x-auto">
            <table className="text-xs w-full" data-testid="pgc-penalties-table" style={{ minWidth: 560 }}>
                <thead><tr className="text-[#64748B] text-[10px] uppercase tracking-wider">
                    <th className="text-left py-2 w-9">#</th><th className="text-left w-36">Skater</th>
                    <th className="text-right px-1 w-9">Pen</th><th className="text-right px-1 w-9">PIM</th>
                    <th className="text-left px-2">Pen 1</th><th className="text-left px-2">Pen 2</th><th className="text-left px-2">Pen 3</th>
                </tr></thead>
                <tbody>
                    {sorted.map((r, i) => (
                        <tr key={i} className="border-t border-[#1E2233]/70 align-top" data-testid={`pgc-penalties-row-${r._id}`}>
                            <td className="py-1.5 font-mono-stat text-[#64748B] text-center">{r.player?.jersey}</td>
                            <td className="stat-emph text-white truncate"><PName person={r.player} matchMap={matchMap} /></td>
                            <td className="text-right px-1 font-mono-stat text-[#CBD5E1]">{r.pens.length}</td>
                            <td className="text-right px-1 font-mono-stat text-[#CBD5E1]">{r.pim || 0}</td>
                            {[0, 1, 2].map((idx) => (
                                <td key={idx} className="px-2 text-[11px] text-[#CBD5E1] whitespace-nowrap">{r.pens[idx] ? fmtPenalty(r.pens[idx]) : <span className="text-[#475569]">—</span>}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}


// ===================== shared bits =====================
function Pill({ children, tone = "purple" }) {
    const cls = tone === "gold" ? "bg-[#FBBF24]/20 text-[#FDE68A]" : "bg-[#9D4CDD]/25 text-[#E9D5FF]";
    return <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold font-mono-stat ${cls}`}>{children}</span>;
}
function Empty({ children }) {
    return <p className="text-xs text-[#64748B] italic">{children}</p>;
}
function PName({ person, matchMap }) {
    if (!person) return <span className="text-[#64748B]">—</span>;
    const rid = matchMap[person.pwhl_id];
    const label = abbrevName(person.name);
    if (rid) return <Link to={`/players/${rid}`} className="hover:text-[#9D4CDD] transition-colors">{label}</Link>;
    return <span>{label}</span>;
}
function fmtTime(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; }
}
